// Aged Care Suggestion Engine Panel – strategy list, Deep Dive, SOA; generates and compares funding strategies (legacy/cashflow/pension).
// Formerly EnhancedSuggestionEngineUI; renamed from EnhancedSuggestionEngineUI for clearer UI naming.

// Add CSS animations for smooth transitions and simple bar charts
const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  /* Simple Bar Chart Styles */
  .bar-chart {
    display: flex;
    align-items: end;
    height: 220px;
    gap: 30px;
    padding: 20px 30px;
    justify-content: space-around;
    background: #f9fafb;
    border-radius: 8px;
    margin: 10px 0;
  }
  
  .bar-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    gap: 8px;
    max-width: 140px;
    min-width: 120px;
  }
  
  .bar-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
    margin-bottom: 8px;
  }
  
  .bar {
    width: 40px;
    border-radius: 4px 4px 0 0;
    transition: height 0.3s ease-in-out;
    position: relative;
    min-height: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .bar-keep {
    background-color: #10b981;
  }
  
  .bar-pay {
    background-color: #3b82f6;
  }
  
  .bar-income-lost {
    background-color: #ef4444;
  }
  
  .bar-benefits-gained {
    background-color: #10b981;
  }
  
  .bar-label {
    font-size: 11px;
    color: #6b7280;
    text-align: center;
    font-weight: 500;
  }
  
  .bar-value {
    font-size: 10px;
    color: #374151;
    font-weight: 600;
    margin-top: 4px;
  }
  
  .mini-bar-chart {
    display: flex;
    align-items: end;
    height: 100px;
    gap: 20px;
    padding: 10px 20px;
    justify-content: center;
    background: #f9fafb;
    border-radius: 6px;
    margin: 10px 0;
  }
  
  .mini-bar {
    width: 35px;
    border-radius: 3px 3px 0 0;
    transition: height 0.3s ease-in-out;
    position: relative;
    min-height: 3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  
  .mini-bar-label {
    font-size: 9px;
    color: #6b7280;
    text-align: center;
    font-weight: 500;
    margin-top: 4px;
  }
  
  .mini-bar-value {
    font-size: 8px;
    color: #374151;
    font-weight: 600;
    margin-top: 2px;
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .bar-chart {
      height: 180px;
      gap: 20px;
      padding: 15px 20px;
    }
    
    .bar-group {
      max-width: 100px;
      min-width: 80px;
    }
    
    .bar {
      width: 35px;
    }
    
    .bar-label {
      font-size: 10px;
    }
    
    .bar-value {
      font-size: 9px;
    }
    
    .mini-bar-chart {
      height: 80px;
      gap: 15px;
      padding: 8px 15px;
    }
    
    .mini-bar {
      width: 30px;
    }
    
    .mini-bar-label {
      font-size: 8px;
    }
    
    .mini-bar-value {
      font-size: 7px;
    }
  }
  
  @media (max-width: 480px) {
    .bar-chart {
      height: 160px;
      gap: 15px;
      padding: 10px 15px;
    }
    
    .bar-group {
      max-width: 80px;
      min-width: 70px;
    }
    
    .bar-label {
      font-size: 9px;
    }
    
    .bar-value {
      font-size: 8px;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = animationStyles;
  document.head.appendChild(styleSheet);
}

// Proper Tooltip Component (based on App.js CalculationTooltip) - Updated to fix tax reference error
// valueLayout: 'inline' (default) = label left / value right for short values; 'stacked' = label then full-width paragraph for long copy
interface CalculationTooltipItem {
    label: string;
    value: string | number;
}

interface CalculationTooltipProps {
    title: string;
    data: CalculationTooltipItem[];
    children: React.ReactNode;
    valueLayout?: 'inline' | 'stacked';
}

const CalculationTooltip = ({ title, data, children, valueLayout = 'inline' }: CalculationTooltipProps) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState('bottom');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - wrapperRect.bottom;
            const spaceAbove = wrapperRect.top;
            const spaceRight = window.innerWidth - wrapperRect.right;
            const spaceLeft = wrapperRect.left;

            // Prefer below, but if not enough space, show above
            if (spaceBelow < tooltipRect.height + 16 && spaceAbove > tooltipRect.height + 16) {
                setPosition('top');
            } else {
                setPosition('bottom');
            }
            // If not enough space to the right, shift left
            if (spaceRight < tooltipRect.width / 2) {
                tooltipRef.current.style.left = `${window.innerWidth - tooltipRect.width - 16}px`;
            }
            // If not enough space to the left, shift right
            if (spaceLeft < tooltipRect.width / 2) {
                tooltipRef.current.style.left = `16px`;
            }
        }
    }, [show]);

    const [coords, setCoords] = useState({ top: 0, left: 0 });
    useEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const tooltipHeight = tooltipRef.current.offsetHeight || 180;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let newPosition = position;
            // If not enough space below, show above
            if (spaceBelow < tooltipHeight + 16 && spaceAbove > tooltipHeight + 16) {
                newPosition = 'top';
            } else {
                newPosition = 'bottom';
            }
            setPosition(newPosition);
            setCoords({
                top: newPosition === 'top' ? rect.top - tooltipHeight - 16 : rect.bottom + 16,
                left: rect.left + rect.width / 2
            });
        }
    }, [show]);

    // Hooks above run every render; only branch on the data shape after they're declared.
    if (!Array.isArray(data)) return <>{children}</>;

    return (
        <div
            className="relative"
            ref={wrapperRef}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && typeof window !== 'undefined' && window.document &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            transform: 'translateX(-50%)',
                            zIndex: 10001,
                            width: valueLayout === 'stacked' ? 'min(28rem, 92vw)' : '340px',
                            maxWidth: '92vw',
                            pointerEvents: 'auto'
                        }}
                        className="p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg"
                    >
                        <h4 className="font-bold text-sm mb-3 text-blue-300">{title}</h4>
                        <div className={valueLayout === 'stacked' ? 'space-y-3' : 'space-y-1'}>
                            {data.map((item, index) => {
                                let displayValue = item.value;
                                if (typeof displayValue === 'number' && !isNaN(displayValue)) {
                                    displayValue = Math.round(displayValue).toLocaleString();
                                }
                                if (item.label.startsWith('---')) {
                                    return <hr key={`hr-${item.label}-${index}`} className="border-gray-600 my-2" />;
                                }
                                if (valueLayout === 'stacked') {
                                    return (
                                        <div key={`row-${item.label}-${index}`} className="text-left border-b border-gray-600/80 pb-3 last:border-0 last:pb-0">
                                            <div className="text-blue-200 font-semibold text-xs uppercase tracking-wide mb-1.5">{item.label}</div>
                                            <div className="text-gray-100 leading-relaxed text-[13px] whitespace-pre-line">{displayValue}</div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={`row-${item.label}-${index}`} className="flex justify-between gap-3">
                                        <span className="text-gray-400 shrink-0">{item.label}:</span>
                                        <span className="font-mono font-semibold text-right break-words">{displayValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

import { engineRowPieces } from './engineRowWords.ts';
import { engineOpening, remainingFloorMs, type EngineOpening } from './engineOpening.ts';
import { moneyOrderParagraph, type RunwayRow } from './moneySequence.tsx';
import { analysisCacheKey, cachedAnalysis, rememberAnalysis } from './engineAnalysisCache.ts';
import { scenarioLivingExpensesOr } from '../../../../shared/src/calculation/support/livingExpenses.ts';
import { radLevelDedupeKey } from './radLevelKey.ts';
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import { householdDailyCost, strategyHouseholdDailyCost, strategyPhaseTwoDailyCost } from './householdCareCosts.ts';
import { phaseFromLabel, recapPhaseLine, roleNames } from './carePhases.ts';
import { describeFundingKeys } from './fundingLabels.ts';
import { homeCanBeLet } from '../../../../shared/src/calculation/householdPosition.ts';
import { runwayFactsOf } from './shortfallOutlook.ts';
import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import AnnuityPreferenceGate from '../../components/strategy/AnnuityPreferenceGate.tsx';
import { annuityPreferenceOf, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import { SameAsBestEstateNote } from '../../components/strategy/SameAsBestEstateNote.tsx';
import { pickCashflowCoveredLeader, CASHFLOW_COVERED_LABEL, CASHFLOW_COVERED_CHIP, BEST_ESTATE_CHIP, friendlyGoalLabel, coveredIsBestEstate } from '../../strategy/cashflowCoveredGoal.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { MoneyFlow } from '../../components/narrative/PlainWords.tsx';
import StrategyPlanRecap from './StrategyPlanRecap.tsx';
import { AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import PlanExplanationModal from './PlanExplanationModal.tsx';
import type { AgedCareBaseline } from './agedCareBaseline.ts';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { agedCareEntrantResidencyOverrides, isIllnessSeparatedCouple } from '../../../../shared/src/calculation/householdStatus.ts';
import { buildStrategyRisks, riskKindsFromAgedCareStrategy } from '../../strategy/strategyRisks.ts';
import { createPortal } from 'react-dom';
import { fundingRunwayCopy, type FundingProjectionYear, planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { payableDebtsOf, type PayableDebt } from './DebtPayoffStrategy.tsx';
import { debtsWorthTesting } from '../../strategy/debtsToTest.ts';
import { parseDebtPayoffKey } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import { liabilityInterestRate } from '../../../../shared/src/calculation/support/creditCards.ts';
import { benchmarkWords } from '../../utils/benchmarkWords.ts';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import AnnualSnapshot, { type SnapshotTileKey, tileWhy, careRowsOf } from './AnnualSnapshot.tsx';
import DoNothingStrip from './DoNothingStrip.tsx';
import CashflowBreakdownModal, { type FigurePage } from './CashflowBreakdownModal.tsx';
import { hasAgedCareProtectedPerson } from '../../../../shared/src/calculation/entityHomeRules.ts';
import ComparisonTable, { type ComparisonRow } from '../../components/strategy/ComparisonTable.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { buildMoneyStory, MoneyStoryBlock, runwayCardLine } from './moneyStory.tsx';
import { summariseFunding } from '../../components/insights/fundingSummary.tsx';
import EngineGoalControl from './EngineGoalControl.tsx';
import EnginePlanCard from './EnginePlanCard.tsx';
import { EngineAnnuityFamilyPanel, engineAnnuityLeverOf } from './annuityPlanFamily.tsx';
import { IncomeOnlyBeforeChoose, incomeOnlyHeld, incomeOnlySetter } from '../../components/strategy/IncomeOnlyTick.tsx';
import EngineQuestions, { answersFromSettings, settingsFromAnswers, annuityGoalsFromAnswers, type EngineAnswers } from './EngineQuestions.tsx';
import type { GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { planWords } from './planWords.ts';
import { fundingPhrase } from './fundingLabels.ts';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { getPersonalLiquidAssetsForRad, computeRadFundingTotals, findAssetInFactFind } from '../../utils/entityFundingUtils.ts';
import { calculateSharePartialDisposalTax } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import { parseRadFundingAssetKey } from '../../../../shared/src/calculation/support/entityFundingUtils.ts';
import { flattenSeRadFundingToManualKeys } from '../../utils/suggestionEngineFunding.ts';
import { AGED_CARE_SCHEDULE } from '../../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { engineStrategyHorizonWarning, engineStrategySetAside, type HorizonSetAside } from './strategyHorizonFlags.ts';
import { Calculator, AlertTriangle, CheckCircle } from 'lucide-react';
import {
    canCommenceAbpFromDownsizer,
    isDownsizerContributionEligible,
    DOWNSIZER_MIN_AGE,
} from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { calculateAge, formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import { AgedCareSuggestionEngineDeepDive } from './AgedCareSuggestionEngineDeepDive.tsx';

// AgedCareSuggestionEngineDeepDive.tsx's own prop interface isn't exported (internal to that module),
// and this panel's Strategy/AnalysisResult types are a slightly different (but structurally
// equivalent) loose shape from that file's DeepDiveScenario/DeepDiveAnalysis — widen the reference
// here rather than duplicating/exporting an interface just for this call site.
const DeepDiveModal = AgedCareSuggestionEngineDeepDive as unknown as React.ComponentType<Record<string, unknown>>;
import { getRadFundingTooltipData } from './getRadFundingTooltipData.tsx';
import type { RadFundingBag } from './getRadFundingTooltipData.tsx';
import { computeFutureEventAdjustedLegacy, estimateRadPaidForStrategy, formatRadStrategyDisplayName, agedCareReadings } from './futureLifeEventUtils.tsx';
import { leadWithinTie, leadExceptionOf, leadChip, leadLine, pickSecondPlan, secondPlanLine, secondPlanChip, allPlansOpenLine, SECOND_PLAN_CHIP, PLAN_CHOICE_RULE_LINES, type LeadException, type PlanReadings } from '../../strategy/planChoiceRule.ts';

/** A change of home's "Stay put" row: the comparison the move is measured against, never a leader (25 Oct 2026). */
export const isStayPutRow = (s: unknown): boolean =>
    !!(s as { facilityMove?: { stayPut?: boolean } | null; stayPut?: boolean } | null)?.facilityMove?.stayPut || !!(s as { stayPut?: boolean } | null)?.stayPut;
import type { EvaluatedStrategy } from './futureLifeEventUtils.tsx';
import {
    SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD,
} from '../../constants/insightsConfig.ts';
import {
    appendEntityIpSaleScenarios,
    applySuggestionEngineScenarioToFactFind,
    calculateSeRadFundingForAmount,
    generateSeFundingVariants,
    isStructureFundingIncludedInSuggestions,
} from '../../utils/suggestionEngineFunding.ts';
import type { RadFundingScenario } from '../../utils/suggestionEngineFunding.ts';
import { appendMeansTestStrategyScenarios, annuityQuoteKindOf, ANNUITY_QUOTE_KIND_WORDS, type ScopedAnnuityQuote } from '../../utils/agedCareMeansTestCandidates.ts';
import { appendEquityReleaseScenarios } from '../../utils/agedCareEquityReleaseCandidates.ts';
import { stripFundingOrderSuffix } from './futureLifeEventUtils.tsx';
import {
    buildAgedCareDigitalSOASnapshot,
    openAgedCareDigitalSOAAttachment
} from './AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import { Button } from '../../components/ui/index.tsx';
import {
    formatServiceBuilderSelectionId,
    notifyHomeCareStrategiesUpdated,
    setActiveHomeCareStrategyId,
} from '../../strategy/careCompanionStrategyEvents.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { AgedCareCosts } from '../../../../shared/src/calculation/agedCareCostTypes.ts';

// DraftFactFind is structurally compatible with FactFind (formDraft.ts); the shared
// calc-engine fns below defensively coerce internally regardless (factFindTypes.ts header).
const asFactFind = (factFind: DraftFactFind): FactFind => factFind as unknown as FactFind;
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import DebtsInPlanNote from '../../components/strategy/DebtsInPlanNote.tsx';
import { RENTED_HOME_CGT_CARD } from '../../strategy/strategyRisks.ts';
import RentedHomeCgtNote from '../../components/strategy/RentedHomeCgtNote.tsx';
import SetAsideReveal from '../../components/strategy/SetAsideReveal.tsx';
import { ChosenAdviceLine } from '../../components/strategy/ChosenAdviceLine.tsx';

type DraftAsset = FactFindAsset;

/**
 * A suggestion-engine strategy/scenario card — the calc engine's `evaluate*`/`generateAllStrategies`
 * family (see calculationApi.ts) returns these as `Promise<any>`, and this panel is the one place that
 * both builds them locally (generateScenarios etc.) and hands them to the Deep Dive / Digital SOA
 * modules, so it stays a single loose "kitchen sink" type (index signature + the fields this file
 * actually reads) rather than duplicating a slightly-different interface per consumer.
 */
export interface Strategy {
    name?: string;
    type?: string;
    /**
     * The means-test lever layered on this strategy, where it has one.
     *
     * Carried out of the engine so the client-side comparisons can weigh what a strategy COMMITS,
     * not merely what it deposits — see estimateLockedCapitalForStrategy.
     */
    meansTestStrategy?: {
        type?: string;
        amount?: number;
        purchaseAmount?: number;
        [key: string]: unknown;
    } | null;
    /** The equity-release loan this strategy takes, with what the projection says it grows to. */
    equityRelease?: Record<string, unknown> | null;
    isLowerRadNearBestCard?: boolean;
    isFutureEventOverlayCard?: boolean;
    groupKey?: string | number;
    /** Present when this object is actually the session-storage "saved strategy" wrapper (see
     *  saveSelectedStrategy) rather than a raw suggestion-engine strategy — the real strategy lives here. */
    strategy?: Strategy;
    snapshot?: {
        legacyValue?: number;
        netCashflow?: number;
        pension?: number;
        totalAgedCareCosts?: number;
        investmentIncome?: number;
        livingExpenses?: number;
        homeStatus?: string;
        agePensionBreakdown?: { label: string; value: string | number }[];
        abpIncome?: number;
        rentalIncome?: number;
        otherInvestmentIncome?: number;
        agedCareBreakdown?: { label: string; value: string | number }[];
        taxBreakdown?: { label: string; value: string | number }[];
        taxPayable?: number;
        [key: string]: unknown;
    };
    projections?: Record<string, unknown>[];
    radFunding?: RadFundingBag;
    radAmount?: number;
    radPaid?: number;
    radPaidNet?: number;
    strategyOptions?: Record<string, unknown>;
    scenarioConfig?: Record<string, unknown>;
    strategySpecificAgedCareCosts?: Record<string, unknown>;
    context?: string;
    description?: string;
    assetAllocation?: {
        assetName?: string;
        status?: 'sold' | 'partiallySold' | 'retained' | string;
        remainingAmount?: number | string;
        liquidatedAmount?: number | string;
        [key: string]: unknown;
    }[];
    originalFactFind?: DraftFactFind;
    optimisationLabel?: string;
    radRoomPrice?: number;
    // Ad hoc top-level fallbacks some strategy sources set instead of (or alongside) `snapshot`.
    agePension?: number;
    estimatedAgePension?: number;
    cashFlow?: number;
    cashflow?: number;
    legacyValue?: number;
    projectedEstateValue?: number;
    // Future-life-event overlay (computeFutureEventAdjustedLegacy) — see futureLifeEventUtils.tsx.
    futureAdjustedLegacy?: number;
    futureEventAdjustmentDelta?: number;
    futureEventAssumptions?: {
        partnerEntryYear?: number;
        partnerRoomPrice?: number;
        radPct?: number;
        originalEntrantAliveAtEvent?: boolean;
        yearsAffected?: number;
        additionalAnnualCareCost?: number;
        additionalLifecycleCost?: number;
        releasedRadFromInitialEntrant?: number;
        [key: string]: unknown;
    };
    futureEventMeta?: {
        bandUsd?: number;
        baselineLegacy?: number;
        maxAdjustedLegacy?: number;
        selectedAdjustedLegacy?: number;
        topAdjustedStrategyName?: string;
        selectedStrategyName?: string;
        radPaidSelected?: number;
        radPaidTopAdjusted?: number;
        usedLowerRadTieBreak?: boolean;
        [key: string]: unknown;
    };
    // Display-only badges/labels stitched on by this panel's card-building logic.
    recommendationLabels?: string[];
    recommendedLabel?: string;
    /** The second card's one line: the trade-off that earns it a place (planChoiceRule.ts). */
    lowerRadNearBestMeta?: { line: string; chip: string };
    [key: string]: unknown;
}

/** Match displayed suggestion-engine cards when the same strategy name appears more than once (e.g. optimal vs lower RAD). */
function strategyCardSelectionKey(s: Strategy | null | undefined): string {
    if (!s?.name) return '';
    return [
        String(s.name),
        s.isLowerRadNearBestCard ? '1' : '0',
        s.isFutureEventOverlayCard ? '1' : '0',
        s.groupKey != null ? String(s.groupKey) : '',
    ].join('\0');
}

export interface SuggestionEngineSettings {
    legacyYears: number;
    emergencyFund: number;
    newHomeValue: number;
    annualRentCost: number;
    sellingCosts: number | undefined;
    stampDuty?: number;
    newHomeState?: string;
    partnerLivingArrangement: 'remain_in_home' | 'rent_separately' | 'downsize_together';
    enablePartnerFutureEvent: boolean;
    partnerEntryYear: number;
    partnerFutureRoomPrice: number | undefined;
    originalEntrantAliveAtPartnerEntry: boolean;
    structureFundingInSuggestions?: 'include' | 'exclude';
}

interface SettingsFormProps {
    currentSettings: SuggestionEngineSettings;
    onSave: (settings: SuggestionEngineSettings & { annuityGoals?: GuaranteedIncomeGoals | null }) => void;
    onCancel: () => void;
    factFind: DraftFactFind;
    scenarioLivingExpenses?: number | string;
    /** The room's full deposit, for the guaranteed income share. */
    roomDeposit?: number;
}

// Settings Form Component — "Things that change the answer": the SAME questions the inline setup asks
// before the plans run (EngineQuestions, UX review 17 Oct 2026), so there is one set of inputs, not a
// setup pop-over plus a settings dialog. The answers map back onto the engine's settings unchanged.
const SettingsForm = ({ currentSettings, onSave, onCancel, factFind, scenarioLivingExpenses = 0, roomDeposit }: SettingsFormProps) => {
    const [answers, setAnswers] = useState<EngineAnswers>(() =>
        answersFromSettings(currentSettings as never, factFind, (Number(currentSettings.newHomeValue) || 0) > 0),
    );
    const hasStructures = (factFind?.structures || []).length > 0;
    const handleSave = () => {
        const next = settingsFromAnswers(currentSettings as never, answers) as unknown as SuggestionEngineSettings & { annuityGoals?: GuaranteedIncomeGoals | null };
        onSave({
            ...next,
            ...(hasStructures ? { structureFundingInSuggestions: answers.structureFunding } : { structureFundingInSuggestions: undefined }),
            annuityGoals: annuityGoalsFromAnswers(answers, factFind),
        });
    };

    return (
        <div className="space-y-6">
            <EngineQuestions
                value={answers}
                onChange={setAnswers}
                factFind={factFind}
                scenarioLivingExpenses={parseFloat(String(scenarioLivingExpenses)) || 0}
                idPrefix="settings"
                roomDeposit={roomDeposit}
            />
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
                >
                    Update the plans
                </button>
            </div>
        </div>
    );
};

export interface CoupleValidation {
    exploreAlternatives?: boolean;
    /** False: the client keeps the home and does not rent it out, so no rent-out plan is tested (25 Oct 2026). */
    testRentingHome?: boolean;
    downsizeNewHomeValue?: number;
    partnerAnnualRent?: number;
    enablePartnerFutureEvent?: boolean;
    partnerEntryYear?: number;
    partnerFutureRoomPrice?: number;
    originalEntrantAliveAtPartnerEntry?: boolean;
    sellingCosts?: number;
    /** The duty on the new home and the state it is in — estimated on the setup modal, overwritable. */
    stampDuty?: number;
    newHomeState?: string;
    emergencyFund?: number;
    [key: string]: unknown;
}

interface RecommendedOptimalStrategyRow {
    label: string;
    strategy: Strategy;
    /** For the cashflow-covered goal: which of its three cases chose this leader (cashflowCoveredGoal.ts). */
    cashflowCovered?: import('../../strategy/cashflowCoveredGoal.ts').CashflowCoveredVerdict;
    /** For the estate leader: set when it is NOT the plan that leaves the most (planChoiceRule.ts rule 2). */
    lead?: LeadException;
}

interface FutureEventV2Config {
    enabled: boolean;
    rankingMetric: string;
    fourthCardMetric?: string;
    assumptions: {
        partnerEntryYear: number;
        partnerRoomPrice: number;
        originalEntrantAliveAtEvent: boolean;
    } | null;
}

/**
 * The suggestion engine's analysis result — assembled locally in this panel from
 * calculationApi.ts's evaluateAgedCareRadFundingScenarios (a `Promise<any>` REST call by design), so
 * kept as a real-but-loose interface rather than a strict model.
 */
export interface AnalysisResult {
    strategies: Strategy[];
    allSimulatedStrategies: Strategy[];
    totalScenarios: number;
    processedScenarios: number;
    recommendedOptimalStrategies: RecommendedOptimalStrategyRow[];
    /**
     * Strategies that would have won a ranking but were set aside because they need more time than
     * the client's age and health support (strategyHorizonFlags.ts). Disclosed under the leader card
     * and in the advice document, and openable — never hidden, never promoted (owner, 23 Sep 2026).
     */
    horizonSetAside?: HorizonSetAside<Strategy>[];
    futureEventV2: FutureEventV2Config;
}

type SuggestionEngineGoals = NonNullable<DraftFactFind['goals']>;

interface AgedCareSuggestionEnginePanelProps {
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    agedCareCosts: AgedCareCosts;
    goals?: SuggestionEngineGoals;
    onStrategySelect: (strategy: Strategy) => void;
    selectedStrategy?: Strategy | null;
    legacyYears?: number;
    coupleValidation?: CoupleValidation;
    onScenarioSaved?: (strategy: Strategy) => void;
    onNavigateToDashboard?: () => void;
    /**
     * "Build a plan yourself" (blank) / "Customise this plan": opens Manual Control, seeded from the row
     * passed (its deposit funding becomes the sliders' opening position — defect 4 made true, 17 Oct
     * 2026) or from nothing. Absent, the links are not offered.
     */
    onBuildYourOwn?: (row: Strategy | null) => void;
    /** The home's name from step 1, kept on each saved plan so the plans can be told apart on Home. */
    homeName?: string;
    /** What the landing asked for on a reopened plan: open its explanation, its risks, the comparison or the document. */
    openOnArrival?: 'explain' | 'risks' | 'compare' | 'document' | 'detail' | 'build' | null;
    onOpenedOnArrival?: () => void;
    /**
     * The engine's own "Do Nothing" row whenever an analysis is on screen (null when there is none),
     * so the analyser's rail and these cards measure against ONE run (owner, 9 Oct 2026: a card
     * read "$82,080 behind" a walk-in figure its own run never produced).
     */
    onDoNothingRow?: (row: Strategy | null) => void;
    /** A run found the chosen plan again: its record as it would be saved now (see the effect that calls it). */
    onChosenPlanRetested?: (record: Record<string, unknown>) => void;
    /** The run, on the chosen plan's own room, does not have it (25 Oct 2026): the analyser marks it out of date. */
    onChosenPlanMissing?: () => void;
    /**
     * The analyser is still working out the figures this run reads (the position and the fees struck on it).
     * The run waits for them, so opening a plan runs it once rather than once per step (26 Oct 2026).
     */
    inputsSettling?: boolean;
    /**
     * The id of a saved plan being re-opened, when that is why this panel mounted (null for a fresh
     * run). Decides the opening — engineOpening.ts: a reopen is quiet, a fresh run thinks.
     */
    reopenedSavedStrategyId?: string | null;
    /**
     * The selected plan's long-run projection rows (the rail's runway), so Explain this on the
     * selected card can say the order the money is used over the years tested rather than only
     * over the plan period. Null when no card is selected.
     */
    selectedPlanRunwayRows?: RunwayRow[] | null;
    /**
     * The shared strategy-options bag the Aged Care Analyser maintains. Only `careStartDate` is read
     * from it here, and only to project every candidate against the same entry date the manual
     * strategy uses — ranking scenarios that assumed different admission dates would compare nothing.
     */
    strategyOptions?: Record<string, unknown>;
    /**
     * The no-plan baseline, computed once by the analyser (agedCareBaseline.ts) and passed down.
     *
     * "Explain this" on a strategy card reconciles that strategy AGAINST this benchmark. Passed
     * rather than rebuilt on purpose: two computations of the same baseline is precisely how the
     * Financial Impact panel and the plan summary came to disagree by $97,649, with neither half's
     * unit test able to see it. Null when the analyser could not compute one, in which case the
     * explanation shows the plan's own figures without a comparison rather than inventing one.
     */
    agedCareBaseline?: AgedCareBaseline | null;
}


/**
 * The one line a client actually reads on a strategy card: when the savings run low, and what pays
 * for the care after that.
 *
 * Built from the strategy's own projections — the same rows the Deep Dive charts — so it cannot
 * drift from the year-by-year figures. Deliberately calm: amber only when the plan outlives what the
 * savings cover, because on most strategies this is reassuring news and should read that way.
 */

/**
 * The line under the goal tabs, which must not describe rows this client can never see.
 *
 * A single client was shown "optional lower-RAD + partner-later rows when enabled" during a meeting
 * on 2026-08-21. He has no partner: the partner-later card cannot appear for him, and offering it
 * invites him to look for something that does not exist. Exported so the rule is pinned by a test
 * rather than by whoever next edits the JSX.
 */
export const legacyFocusHint = (isCouple: boolean): string =>
    isCouple
        ? 'Estate focus; optional lower-RAD + partner-later rows when enabled.'
        : 'Estate focus; an optional lower-RAD row appears when a similar estate can be reached with less paid up front.';
const AgedCareSuggestionEnginePanel = ({
    factFind,
    setFactFind,
    agedCareCosts,
    goals = {},
    onStrategySelect,
    selectedStrategy,
    legacyYears = 5,
    coupleValidation = { downsizeNewHomeValue: 500000, partnerAnnualRent: 40000 },
    onScenarioSaved,
    onNavigateToDashboard,
    onBuildYourOwn,
    homeName = '',
    openOnArrival = null,
    onOpenedOnArrival,
    onDoNothingRow,
    onChosenPlanRetested,
    onChosenPlanMissing,
    inputsSettling = false,
    // Declared on the props but previously unread here. Now the source of the care start date every
    // candidate is projected against — see `careStartDateFromStrategyOptions`.
    strategyOptions,
    /*
     * The no-plan baseline, computed once by the analyser and passed down rather than rebuilt here.
     * "Explain this" reconciles a strategy AGAINST it, and a second computation of the same
     * benchmark is exactly how the panel and the summary came to disagree by $97,649 once before.
     */
    agedCareBaseline = null,
    reopenedSavedStrategyId = null,
    selectedPlanRunwayRows = null,
}: AgedCareSuggestionEnginePanelProps) => {
    // What every card here is compared with, in this module's words (25 Oct 2026): paying daily, or staying put for a change of home.
    const bench = benchmarkWords('agedCare', { facilityMove: !!facilityMoveFromStrategyOptions(strategyOptions) });
    // Decided once, at mount; back to 'thinking' after the first run so re-runs show the work.
    const openingRef = useRef<EngineOpening>(engineOpening({ reopenedSavedStrategyId }));
    /** Which strategy's plain-words explanation is open, if any. */
    const [explainStrategy, setExplainStrategy] = useState<any>(null);
    // "What could go wrong" for one card (StrategyRisksPanel; the list is strategyRisks.ts).
    const [riskStrategy, setRiskStrategy] = useState<Strategy | null>(null);

    /**
     * The walk-in position under every snapshot tile — the SAME no-plan baseline this page's rail
     * measures against, read once, so a card can say "$64,360, up $15,678" without the rail having
     * to repeat the plan beside it (owner, 2026-09-22). Null when the baseline could not be run, in
     * which case the tiles simply show the plan's figures.
     */
    const snapshotBaseline = useMemo(() => {
        const fin = agedCareBaseline?.financials as { investmentIncome?: number; netTax?: number; cashFlowBreakdown?: Array<{ label?: string; value?: unknown }> } | null | undefined;
        if (!fin) return null;
        const livingRow = fin.cashFlowBreakdown?.find((r) => typeof r.label === 'string' && r.label.includes('Living Expenses'));
        const costs = agedCareBaseline?.agedCareCosts as { totalDailyCost?: number } | null | undefined;
        const secondCosts = agedCareBaseline?.secondAgedCareCosts as { totalDailyCost?: number } | null | undefined;
        return {
            pension: Number(agedCareBaseline?.pension) || 0,
            investmentIncome: Number(fin.investmentIncome) || 0,
            livingExpenses: livingRow && typeof livingRow.value === 'number' ? Math.abs(livingRow.value) : Number(factFind?.expenses?.livingExpenses) || 0,
            // The first year: the second room only where both enter together (the baseline's second stage is its own figure).
            careCosts: householdDailyCost(costs, agedCareBaseline?.secondEntersLater ? null : secondCosts) * 365,
            taxPayable: Number(fin.netTax) || 0,
            netCashflow: Number(agedCareBaseline?.cashFlowAfterCare) || 0,
        };
    }, [agedCareBaseline, factFind]);
    // Shared with the digital SOA (see futureLifeEventUtils) so the card headline and the SOA never disagree.
    const getDisplayStrategyName = (name: string = '', radPaidNet: number | null = null, roomPrice: number | null = null, secondRoom: { radPaid: number; roomPrice: number } | null = null, move: { stayPut: boolean; fromRefund: number } | null = null, village: { fromVillage: number } | null = null) =>
        formatRadStrategyDisplayName(name, radPaidNet, roomPrice, secondRoom, move, village);
    /** Leaving a village, as the row records it, for the name. */
    const villageOf = (row: unknown): { fromVillage: number } | null => {
        const ve = (row as { villageExit?: { fromVillage?: number } | null } | null | undefined)?.villageExit;
        return ve ? { fromVillage: Number(ve.fromVillage) || 0 } : null;
    };
    /** A change of home, as the row records it, for the name. */
    const moveOf = (row: unknown): { stayPut: boolean; fromRefund: number } | null => {
        const mv = (row as { facilityMove?: { stayPut?: boolean; fromRefund?: number } | null } | null | undefined)?.facilityMove;
        return mv ? { stayPut: !!mv.stayPut, fromRefund: Number(mv.fromRefund) || 0 } : null;
    };
    /** The second room a row carries, where both of a couple are in care, for the name and the words. */
    const secondRoomOf = (row: unknown): { radPaid: number; roomPrice: number; name: string; leadName: string } | null => {
        const sr = (row as { secondRoom?: { radPaid?: number; roomPrice?: number; person?: 'client' | 'partner' } | null } | null | undefined)?.secondRoom;
        if (!sr || !((Number(sr.roomPrice) || 0) > 0)) return null;
        const names = roleNames(factFind, sr.person);
        return { radPaid: Number(sr.radPaid) || 0, roomPrice: Number(sr.roomPrice) || 0, name: names.secondName, leadName: names.firstName };
    };
    /** The second room a row carries, in full, for the allocation grid and the card's strip. */
    const secondRoomRowOf = (row: unknown) =>
        (row as { secondRoom?: { person?: 'client' | 'partner'; roomPrice?: number; radPaid?: number; radFunding?: Record<string, number>; entersLater?: boolean; careStartDate?: string; agedCareCosts?: { dailyAccommodationPayment?: number } } | null } | null | undefined)?.secondRoom ?? null;
    const partnerDisplayName = useMemo(
        () => (factFind?.partner?.firstName || '').trim() || 'your partner',
        [factFind?.partner?.firstName]
    );
    const clientDisplayName = useMemo(
        () => (factFind?.client?.firstName || '').trim() || 'the person entering care first',
        [factFind?.client?.firstName]
    );
    const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
    // The run's own no-plan row, up to the analyser (see the prop). Null again when the analysis clears.
    useEffect(() => {
        const rows = (analysis?.allSimulatedStrategies?.length ? analysis.allSimulatedStrategies : analysis?.strategies) || [];
        const row = rows.find((s) => /^(do nothing|stay put)$/i.test(String(s?.name || '').trim())) || null;
        onDoNothingRow?.(row);
        return () => { onDoNothingRow?.(null); };
    }, [analysis, onDoNothingRow]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deepDiveModal, setDeepDiveModal] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState<Strategy | null>(null);
    const [activeTab, setActiveTab] = useState('yearlyProjections');
    /** The advice document is being written (its snapshot builds asynchronously): one press, one document. */
    const [documentBusy, setDocumentBusy] = useState(false);
    /** Side-by-side of recommendation leaders only (legacy / cashflow / pension + optional lower RAD + future event) — not the full simulation list. */
    const [showGoalLeadersModal, setShowGoalLeadersModal] = useState(false);
    /** "Show everything tested" inside the comparison — opened directly from the ways-on row too (owner, 18 Oct 2026). */
    const [everythingTestedOpen, setEverythingTestedOpen] = useState(false);
    /* Escape closes whichever of this panel's own dialogs is open. */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setShowGoalLeadersModal(false);
            setShowSettingsModal(false);
            setShowAssumptionsModal(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);
    /* Open what the landing asked for, once the chosen plan's row is in the run. Above the early
       returns further down, like every hook here. */
    useEffect(() => {
        if (!openOnArrival || !analysis?.strategies?.length) return;
        const inner = (selectedStrategy as { strategy?: Strategy } | null)?.strategy ?? (selectedStrategy as Strategy | null);
        const key = inner?.name != null ? strategyCardSelectionKey(inner) : null;
        const rows = [...(analysis.recommendedOptimalStrategies || []).map((r) => r.strategy).filter(Boolean), ...analysis.strategies];
        const card = key ? rows.find((s) => s && strategyCardSelectionKey(s) === key) ?? null : null;
        if (openOnArrival === 'compare') setShowGoalLeadersModal(true);
        else if (!card) return;
        else if (openOnArrival === 'explain') setExplainStrategy(card);
        else if (openOnArrival === 'risks') setRiskStrategy(card);
        // A new tab can only open from a press, so the document arrival lands on the chosen plan's card,
        // which carries the one "Get the advice document" (owner, 24 and 26 Oct 2026).
        else if (openOnArrival === 'document') window.setTimeout(() => document.querySelector('[data-testid="chosen-plan-advice"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
        else if (openOnArrival === 'detail') setDetailOpenKey(strategyCardSelectionKey(card));
        else if (openOnArrival === 'build') onBuildYourOwn?.(card);
        onOpenedOnArrival?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openOnArrival, analysis]);
    const [allStrategiesSortBy, setAllStrategiesSortBy] = useState('legacy');
    /*
     * A REAL QUOTE, and the draft being typed, kept apart.
     *
     * Only the committed value re-runs the engine - typing a digit must not fire an analysis per
     * keystroke. Same split the village module uses.
     */
    const [acAnnuityQuote, setAcAnnuityQuote] = useState<ScopedAnnuityQuote | null>(null);
    const [acAnnuityQuoteDraft, setAcAnnuityQuoteDraft] = useState<number | string>('');
    const [showAssumptionsModal, setShowAssumptionsModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    /** User-facing filter: which single objective to surface first. 'all' uses the same card set as legacy (v1). */
    const [optimisationFocus, setOptimisationFocus] = useState(() => {
        const g = goals?.agedCareSuggestionFocus;
        if (g === 'cashflow' || g === 'pension' || g === 'legacy' || g === 'all' || g === 'covered') return g;
        // Best estate leads (owner, 24 Sep 2026: "Best estate, then Best estate cashflow covered…").
        return 'legacy';
    });
    /** Which card has "Show the detail" open, and which of its snapshot tiles a "why?" asked for. */
    const [detailOpenKey, setDetailOpenKey] = useState<string | null>(null);
    const [tileRequest, setTileRequest] = useState<{ cardKey: string; tile: SnapshotTileKey; nonce: number } | null>(null);
    const openTile = (cardKey: string, tile: SnapshotTileKey) => {
        setDetailOpenKey(cardKey);
        setTileRequest({ cardKey, tile, nonce: Date.now() });
    };
    /** "Where the $32,006 comes from" — the cashflow pop-over for one card (UX review, 17 Oct 2026). */
    const [cashflowFor, setCashflowFor] = useState<{ row: Strategy; page: FigurePage } | null>(null);
    const [currentSettings, setCurrentSettings] = useState<SuggestionEngineSettings>({
        legacyYears: legacyYears || 5,
        emergencyFund: getRecommendedEmergencyFund(factFind), // Default: greater of 6 months' expenses or the floor
        // Alternative Scenario Parameters (from original setup)
        newHomeValue: Math.round((parseFloat(String(factFind?.home?.value)) || 0) * 0.7) || 250000, // ~70% of current home (if downsizing)
        annualRentCost: 40000, // If partner rents
        sellingCosts: 0, // Selling costs in $ for downsize (consistent with Manual Control)
        // Partner living arrangement (for couples)
        partnerLivingArrangement: 'remain_in_home', // 'remain_in_home', 'rent_separately', 'downsize_together'
        // Future life event (optional): partner enters residential care later.
        enablePartnerFutureEvent: factFind?.adviceFor === 'couple',
        partnerEntryYear: 3,
        partnerFutureRoomPrice: parseFloat(String(agedCareCosts?.radAmount)) || 500000,
        originalEntrantAliveAtPartnerEntry: true,
    });
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    // Keep Suggestion Engine settings aligned with Strategy Analysis Setup (coupleValidation)
    useEffect(() => {
        if (!coupleValidation) return;
        setCurrentSettings((prev) => ({
            ...prev,
            enablePartnerFutureEvent:
                coupleValidation.enablePartnerFutureEvent !== undefined
                    ? coupleValidation.enablePartnerFutureEvent
                    : prev.enablePartnerFutureEvent,
            partnerEntryYear: coupleValidation.partnerEntryYear ?? prev.partnerEntryYear,
            partnerFutureRoomPrice:
                (coupleValidation.partnerFutureRoomPrice || 0) > 0
                    ? coupleValidation.partnerFutureRoomPrice!
                    : prev.partnerFutureRoomPrice,
            originalEntrantAliveAtPartnerEntry:
                coupleValidation.originalEntrantAliveAtPartnerEntry !== undefined
                    ? coupleValidation.originalEntrantAliveAtPartnerEntry
                    : prev.originalEntrantAliveAtPartnerEntry,
            newHomeValue: coupleValidation.downsizeNewHomeValue ?? prev.newHomeValue,
            annualRentCost: coupleValidation.partnerAnnualRent ?? prev.annualRentCost,
            sellingCosts: coupleValidation.sellingCosts ?? prev.sellingCosts,
            emergencyFund: coupleValidation.emergencyFund ?? prev.emergencyFund,
        }));
    }, [coupleValidation]);

    // Keep the "analysing" UI on screen for at least the opening's floor, even when the simulation
    // finishes quickly. We really are running hundreds of scenarios, and showing that work is part
    // of the experience — without a floor it can flash past in a fraction of a second. A re-opened
    // saved plan has no floor (engineOpening.ts), and once any run completes the panel is back on
    // the thinking opening.
    const stopLoadingAfterMinDelay = (startedAt: number) => {
        const remaining = remainingFloorMs(openingRef.current, startedAt, Date.now());
        setTimeout(() => {
            setLoading(false);
            openingRef.current = 'thinking';
        }, remaining);
    };

    /**
     * The just-in-time "how would new money be invested?" gate. The aged care engine explores
     * downsize scenarios (booking downsizer contributions into super / a pension) whenever a
     * target home value is in play — which the default settings provide — so the question is
     * asked BEFORE the engine prices anything, same rules as the village module: answering saves
     * to the profile (asked once, ever, across all modules); skipping runs at Conservative
     * without recording an answer.
     *
     * It is only reached by flows that never open the Strategy Analysis Setup modal (a single
     * client with no structures), because that modal now carries the same question among the
     * run's other inputs. Completing the setup counts as having been asked: its default option,
     * "Not sure — assume Conservative", is a legitimate answer that deliberately records nothing,
     * so testing `hasAnsweredNewMoneyProfile` alone put the interim screen back in front of every
     * adviser who left the dropdown alone — the exact screen moving the question was meant to
     * remove.
     */
    const [newMoneySkipped, setNewMoneySkipped] = useState(false);
    /*
     * THE ANNUITY WILLINGNESS QUESTION (owner, 25 Sep 2026). The Strategy Analysis Setup asks it,
     * but that pop-over only opens for some flows — a single client with no structures never sees
     * it — so the gate stands in, exactly as the new-money question does, and suppresses itself
     * once an answer is recorded anywhere.
     */
    const [annuityPrefSkipped, setAnnuityPrefSkipped] = useState(false);
    const acNeedsAnnuityAnswer = !annuityPreferenceOf(factFind as never).answered && !annuityPrefSkipped;
    // The gate's three answers (back / all / none), dated, merged into the client's goals.
    const answerAnnuityPreference = (appetite: AnnuityAppetite, residual: AnnuityResidual, answeredOn?: string) => {
        if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), annuityAppetite: appetite, annuityResidual: residual, ...(answeredOn ? { annuityAnsweredOn: answeredOn } : {}) } }));
        else setAnnuityPrefSkipped(true);
    };
    const acRunWouldInvest = (Number(coupleValidation?.downsizeNewHomeValue) || 0) > 0;
    const acNewMoneyAskedInSetup = !!coupleValidation?.suggestionEngineSetupComplete;
    const acNeedsNewMoneyAnswer =
        acRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped && !acNewMoneyAskedInSetup;
    const answerNewMoneyProfile = (id: InvestmentProfileId) => {
        if (setFactFind) {
            setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
        } else {
            setNewMoneySkipped(true);
        }
    };

    // The Transfer Balance Cap question, chained after the new-money one: the downsize scenarios
    // can commence a pension from the downsizer contribution, so when a person's balances trip the
    // $1.6m screen the ATO figure is asked for before pricing (transferBalanceCap.ts). Skipping
    // estimates from current pension balances; the engine splits at the cap either way.
    const [tbcSkipped, setTbcSkipped] = useState(false);
    const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && acRunWouldInvest
        ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
            : (factFind?.adviceFor === 'couple' && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
        : null;
    const acNeedsTbcAnswer = !acNeedsNewMoneyAnswer && tbcPersonNeedingAnswer !== null;
    const answerTbc = (amount: number) => {
        if (setFactFind && tbcPersonNeedingAnswer) {
            const key = tbcPersonNeedingAnswer;
            setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
        } else {
            setTbcSkipped(true);
        }
    };

    /*
     * The profile as the run sees it: a change to the income-only confirmation alone (ticked beside a card's
     * Choose button) is a record, not an input, so it must not re-run hundreds of scenarios and reshuffle the
     * cards under the reader's cursor (guaranteed income review E5, 21 Oct 2026).
     */
    const runFactFindKey = useMemo(() => {
        const g = { ...((factFind?.goals || {}) as Record<string, unknown>) };
        delete g.incomeOnlyConfirmation;
        return JSON.stringify({ ...factFind, goals: g });
    }, [factFind]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const runFactFind = useMemo(() => factFind, [runFactFindKey]);

    /*
     * ONE RUN PER CHANGE THAT MATTERS (26 Oct 2026: opening a saved plan sent the whole analysis three times,
     * ~2 minutes and 1.3 GB). The fees handed in are recomputed in steps as the analyser restores its state,
     * each a new object, and each re-ran several hundred scenarios. The run now keys on what it actually
     * reads — the profile, the room and the fee figures the candidates are built from, the living costs,
     * the settings — waits while the analyser is still working those out, and skips a key it has run.
     */
    const runInputsKey = useMemo(() => {
        const g = { ...((goals || {}) as Record<string, unknown>) };
        delete g.incomeOnlyConfirmation;
        const c = (agedCareCosts || {}) as Record<string, unknown>;
        const move = facilityMoveFromStrategyOptions(strategyOptions);
        return JSON.stringify([
            runFactFindKey,
            move ? c : [c.radAmount, c.basicDailyFee, c.dailyHSC, c.dailyMTCF, c.dailyExtraServiceFee, c.scenarioLivingExpenses],
            g, legacyYears, coupleValidation, acNeedsNewMoneyAnswer, acNeedsTbcAnswer, acAnnuityQuote,
            careStartDateFromStrategyOptions(strategyOptions), radDrawdownFromStrategyOptions(strategyOptions),
            feeArrangementsFromStrategyOptions(strategyOptions), secondRoomFromStrategyOptions(strategyOptions), move,
            villageExitFromStrategyOptions(strategyOptions),
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runFactFindKey, agedCareCosts, goals, legacyYears, coupleValidation, acNeedsNewMoneyAnswer, acNeedsTbcAnswer, acAnnuityQuote, strategyOptions]);
    const lastRunKeyRef = useRef<string | null>(null);
    const runSeqRef = useRef(0);

    // Run analysis when data changes
    useEffect(() => {
        if (acNeedsNewMoneyAnswer || acNeedsTbcAnswer) { setLoading(false); return; }
        if (inputsSettling) { if (lastRunKeyRef.current !== runInputsKey) setLoading(true); return; }
        if (lastRunKeyRef.current === runInputsKey) return;
        if (factFind && agedCareCosts) {
            lastRunKeyRef.current = runInputsKey;
            const runSeq = (runSeqRef.current += 1);
            setLoading(true);
            const analysisStartedAt = Date.now();
            const runAnalysis = async () => {
                try {
                    // Yield one tick so React can paint the loader BEFORE the analysis runs.
                    // The simulation is synchronous and blocks the main thread (~1s+), so without
                    // this the previous screen freezes and the loader only flashes at the very end.
                    await new Promise((resolve) => setTimeout(resolve, 50));
                    // Debug: Log what agedCareCosts contains
                    
                    // Extract adjusted living expenses from agedCareCosts.
                    // Future-life-event inputs must match Strategy Analysis Setup (coupleValidation): the gear modal
                    // merges these in runAnalysisWithSettings, but the initial/re-run effect previously did not,
                    // so future-aware scoring and disclosure never activated until a settings save.
                    const defaultPartnerRoom = parseFloat(String(agedCareCosts?.radAmount)) || 500000;
                    const adjustedGoals = {
                        ...goals,
                        adjustedLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
                        enablePartnerFutureEvent:
                            coupleValidation?.enablePartnerFutureEvent !== undefined
                                ? coupleValidation.enablePartnerFutureEvent
                                : (goals.enablePartnerFutureEvent ?? (factFind?.adviceFor === 'couple')),
                        partnerEntryYear: coupleValidation?.partnerEntryYear ?? goals.partnerEntryYear ?? 3,
                        partnerFutureRoomPrice:
                            (coupleValidation?.partnerFutureRoomPrice || 0) > 0
                                ? coupleValidation!.partnerFutureRoomPrice!
                                : ((Number(goals.partnerFutureRoomPrice) || 0) > 0 ? goals.partnerFutureRoomPrice : defaultPartnerRoom),
                        originalEntrantAliveAtPartnerEntry:
                            coupleValidation?.originalEntrantAliveAtPartnerEntry !== undefined
                                ? coupleValidation.originalEntrantAliveAtPartnerEntry
                                : (goals.originalEntrantAliveAtPartnerEntry ?? true),
                    };
                    
                    // INTEGRATED APPROACH: Generate scenarios and call manual control directly with progress tracking
                    // Downsize is included when parent passes exploreAlternatives/downsizeNewHomeValue, or after user sets it in settings and clicks Save & Re-optimise
                    const results = await generateScenariosAndCallManualControl(
                        factFind,
                        agedCareCosts,
                        adjustedGoals,
                        legacyYears,
                        coupleValidation,
                        setProgress, // Pass setProgress for progress tracking
                        // The entry date the adviser set on the Analyser, so the ranked candidates are
                        // projected against the same case the manual strategy is.
                        careStartDateFromStrategyOptions(strategyOptions),
                        acAnnuityQuote,
                        radDrawdownFromStrategyOptions(strategyOptions),
                        feeArrangementsFromStrategyOptions(strategyOptions),
                        secondRoomFromStrategyOptions(strategyOptions),
                        facilityMoveFromStrategyOptions(strategyOptions),
                        villageExitFromStrategyOptions(strategyOptions),
                    );
                    
                    // A later run has started: this one's answer is for inputs no longer on screen.
                    if (runSeq !== runSeqRef.current) return;
                    setAnalysis(results);
                    setError(null);
                } catch (err) {
                    if (runSeq !== runSeqRef.current) return;
                    console.error('Error in analyzeFundingStrategies:', err);
                    // A failed run may be tried again for the same inputs.
                    lastRunKeyRef.current = null;
                    setError(err instanceof Error ? err.message : String(err));
                } finally {
                    if (runSeq === runSeqRef.current) stopLoadingAfterMinDelay(analysisStartedAt);
                }
            };

            runAnalysis();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runInputsKey, inputsSettling]);

    /** The record a chosen plan is saved as: its row, its figures, the doing-nothing figures it was measured against. */
    const savedRecordFor = (strategy: Strategy) => {

        // The row's own costs (after the deposit), both rooms where it carries a second one; the
        // base costs where the row has none.
        const annualAgedCareCost = strategyHouseholdDailyCost(strategy as never, agedCareCosts as never) * 365;
        
        // Audit trail for SOA: what was recommended vs what the client selected
        const recommendedList = analysis?.recommendedOptimalStrategies || [];
        const recommendedOptimalStrategies = recommendedList.map(ro => ({ label: ro.label, name: ro.strategy?.name }));
        const clientSelectedIsAlternative = recommendedList.length > 0 && !recommendedList.some(ro => ro.strategy?.name === strategy.name);
        
        const savedStrategy = {
            id: `strategy-${Date.now()}`,
            name: strategy.name,
            type: 'aged_care',
            source: 'suggestion_engine',
            strategy: strategy,
            createdAt: new Date().toISOString(),
            recommendedOptimalStrategies,
            clientSelectedIsAlternative,
            /* The plan in the client's words, the home it was modelled for, and the doing-nothing
               figures it was measured against — so Home and the Aged care landing can show "+$61,507
               vs doing nothing" without re-running anything (UX review, 17 Oct 2026). */
            displayName: planWords(strategy as never, factFind, getDisplayStrategyName(strategy.name, strategy.radPaidNet, strategy.radRoomPrice, secondRoomOf(strategy), moveOf(strategy), villageOf(strategy))).title,
            homeName: homeName || null,
            // The one plan on the file that is chosen (the analyser unmarks the others). Survives a reload,
            // which the in-memory selection does not, so Home and the landing know which plan is real.
            chosen: true,
            baseline: agedCareBaseline && agedCareBaseline.pension != null ? {
                legacyValue: agedCareBaseline.legacyValue ?? null,
                pension: Number(agedCareBaseline.pension) || 0,
                cashFlowAfterCare: Number(agedCareBaseline.cashFlowAfterCare) || 0,
                careCosts: snapshotBaseline?.careCosts ?? null,
            } : null,
            outputs: {
                agedCareCost: annualAgedCareCost, // Use strategy-specific cost after RAD payment
                agePension: strategy.snapshot?.pension || strategy.agePension || strategy.estimatedAgePension || 0,
                cashFlow: strategy.snapshot?.netCashflow ?? strategy.snapshot?.cashFlow ?? strategy.cashFlow ?? strategy.cashflow ?? 0,
                legacyEstimate: strategy.isFutureEventOverlayCard && strategy.futureAdjustedLegacy != null
                    ? strategy.futureAdjustedLegacy
                    : (strategy.snapshot?.legacyValue || strategy.legacyValue || strategy.projectedEstateValue || 0),
                roomCost: agedCareCosts.radAmount || 0, // Add room cost for identification
                livingExpenses: strategy.snapshot?.livingExpenses ?? scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) // Save adjusted living expenses
            },
            inputs: {
                scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
                legacyYears: currentSettings.legacyYears,
                radInput: agedCareCosts.radAmount || 0,
            }
        };
        return savedStrategy;
    };

    // The saved record is what the advice document is written from, so it carries the plan in full.
    const saveSelectedStrategy = async (lightOrFull: Strategy) => {
        const strategy = await fullStrategyRow(lightOrFull);
        const savedStrategy = savedRecordFor(strategy);

        // Save to session storage
        const existingStrategies = JSON.parse(sessionStorage.getItem('careCompanionStrategies') || '[]');
        const updatedStrategies = [...existingStrategies, savedStrategy];
        sessionStorage.setItem('careCompanionStrategies', JSON.stringify(updatedStrategies));
        setActiveHomeCareStrategyId(formatServiceBuilderSelectionId('aged_care', savedStrategy.id));
        notifyHomeCareStrategiesUpdated();

        // Notify parent component
        if (onStrategySelect) {
            onStrategySelect(savedStrategy);
        } else {
        }
    };

    /*
     * THE CHOSEN PLAN'S SAVED FIGURES FOLLOW ITS RE-TEST (25 Oct 2026). Home and the plan page show the
     * figures saved when the plan was chosen; the card shows this run's. When the rules or the day moved
     * between the two, the same plan read $3,286,281 on Home and $3,285,977 on its card. Each run that
     * finds the chosen plan again (same plan, same room, same deposit) hands its fresh record up, and the
     * analyser rewrites the chosen record where a figure changed — so every screen reads one source.
     */
    useEffect(() => {
        if (!analysis || !onChosenPlanRetested) return;
        const chosen = (selectedStrategy?.strategy ?? selectedStrategy) as Strategy | null | undefined;
        if (!chosen?.name) return;
        const key = strategyCardSelectionKey(chosen);
        const room = Number(chosen.radRoomPrice) || 0;
        const pool: Strategy[] = [...(analysis.strategies || []), ...((analysis.recommendedOptimalStrategies || []).map((r) => r.strategy).filter(Boolean) as Strategy[])];
        const row = pool.find((s) => strategyCardSelectionKey(s) === key
            && Math.abs((Number(s.radRoomPrice) || 0) - room) < 1
            && Math.abs((Number(s.radPaidNet) || 0) - (Number(chosen.radPaidNet) || 0)) < 1);
        if (row) { void fullStrategyRow(row).then((full) => onChosenPlanRetested(savedRecordFor(full))); return; }
        // Not found, on the room it was chosen for: not a run for another home, which has its own plans.
        const pricedRoom = Number(agedCareCosts?.radAmount) || 0;
        const everyRow = [...pool, ...((analysis.allSimulatedStrategies || []) as Strategy[])];
        if (room > 0 && Math.abs(pricedRoom - room) < 1 && !everyRow.some((s) => strategyCardSelectionKey(s) === key)) onChosenPlanMissing?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis]);

    const handleDeepDive = (strategy: Strategy) => {
        setSelectedScenario(strategy);
        setDeepDiveModal(true);
        // The year-by-year window shows each year's working; a light row is fetched in full and swapped in.
        if ((strategy as { lite?: unknown }).lite) {
            void fullStrategyRow({ ...strategy }).then((full) => setSelectedScenario((current) => (current && current.name === full.name ? full : current)));
        }
    };

    // Straight to the questions: re-testing keeps a chosen plan chosen when its row still exists, so the
    // "Save Current Strategy?" interstitial asked about a loss that does not happen (UX review, 17 Oct 2026).
    const handleSettingsClick = () => {
        setShowSettingsModal(true);
    };

    const handleSettingsSave = (newSettings: SuggestionEngineSettings & { annuityGoals?: GuaranteedIncomeGoals | null }) => {
        const { structureFundingInSuggestions: nextStructureFunding, annuityGoals, ...engineSettings } = newSettings;
        // The annuity answer lives on the client's goals (every module reads it there); the run below
        // sees it through the same override the structures answer uses.
        const goalsOverride = annuityGoals ? { goals: { ...(factFind?.goals || {}), ...annuityGoals } } : {};
        if (setFactFind && (nextStructureFunding != null || annuityGoals)) {
            setFactFind((prev) => ({
                ...prev,
                ...(nextStructureFunding != null ? { structureFundingInSuggestions: nextStructureFunding } : {}),
                ...(annuityGoals ? { goals: { ...(prev.goals || {}), ...annuityGoals } } : {}),
            }));
        }
        setCurrentSettings(engineSettings);
        setShowSettingsModal(false);

        setAnalysis(null);
        setLoading(true);

        setTimeout(() => {
            runAnalysisWithSettings(
                engineSettings,
                { ...(nextStructureFunding != null ? { structureFundingInSuggestions: nextStructureFunding } : {}), ...goalsOverride },
            );
        }, 100);
    };

    const runAnalysisWithSettings = async (settings: SuggestionEngineSettings, formDataOverrides: Record<string, unknown> = {}) => {
        const settingsAnalysisStartedAt = Date.now();
        try {
            // Yield so the loader paints before the synchronous, main-thread-blocking analysis.
            await new Promise((resolve) => setTimeout(resolve, 50));
            const updatedFactFind: DraftFactFind = {
                ...factFind,
                ...formDataOverrides,
                emergencyFund: settings.emergencyFund,
            };

            // Create updated goals that include the simplified settings
            const adjustedGoals = {
                ...goals,
                adjustedLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, updatedFactFind.expenses?.livingExpenses),
                emergencyFund: settings.emergencyFund,
                // Alternative scenario parameters
                newHomeValue: settings.newHomeValue,
                annualRentCost: settings.annualRentCost,
                // Partner living arrangement
                partnerLivingArrangement: settings.partnerLivingArrangement,
                // Future event assumptions
                enablePartnerFutureEvent: settings.enablePartnerFutureEvent,
                partnerEntryYear: settings.partnerEntryYear,
                partnerFutureRoomPrice: settings.partnerFutureRoomPrice,
                originalEntrantAliveAtPartnerEntry: settings.originalEntrantAliveAtPartnerEntry,
            };
            
            // Include downsize when "New home value (if downsizing)" is set; pass selling costs $ for downsize.
            // Downsize is now driven purely off downsizeNewHomeValue (see downsizeEnabled), not a magic string.
            const effectiveCoupleValidation: CoupleValidation = (Number(settings.newHomeValue) > 0)
                ? { ...coupleValidation, downsizeNewHomeValue: Number(settings.newHomeValue), sellingCosts: parseFloat(String(settings.sellingCosts)) || 0, stampDuty: Number(settings.stampDuty) || 0, newHomeState: settings.newHomeState }
                : { ...coupleValidation, sellingCosts: parseFloat(String(settings.sellingCosts)) || 0 };
            const result = await generateScenariosAndCallManualControl(updatedFactFind, agedCareCosts, adjustedGoals, settings.legacyYears, effectiveCoupleValidation, null, careStartDateFromStrategyOptions(strategyOptions), null, radDrawdownFromStrategyOptions(strategyOptions), feeArrangementsFromStrategyOptions(strategyOptions), secondRoomFromStrategyOptions(strategyOptions), facilityMoveFromStrategyOptions(strategyOptions), villageExitFromStrategyOptions(strategyOptions));
            setAnalysis(result);
        } catch (error) {
            console.error('Error re-running analysis:', error);
            setError('Failed to re-optimise with new settings');
        } finally {
            stopLoadingAfterMinDelay(settingsAnalysisStartedAt);
        }
    };

    // Keep this memo before early-return branches below to preserve hook order.
    // Primary recommendations use baseline legacy; optional fourth card shows the best strategy
    // under future-event-adjusted legacy when partner-later assumptions are enabled.
    const futureEventOptimalStrategy = useMemo<Strategy | null>(() => {
        if (!currentSettings.enablePartnerFutureEvent) return null;
        if (!analysis?.strategies?.length) return null;

        const partnerEntryYear = parseInt(String(currentSettings.partnerEntryYear), 10) || 3;
        const partnerFutureRoomPrice = parseFloat(String(currentSettings.partnerFutureRoomPrice)) || 0;
        const originalEntrantAliveAtPartnerEntry = !!currentSettings.originalEntrantAliveAtPartnerEntry;
        if (partnerFutureRoomPrice <= 0) return null;

        const horizonYears = currentSettings.legacyYears || legacyYears || 5;
        const scored = analysis.strategies
            .map((strategy) => {
                const result = computeFutureEventAdjustedLegacy({
                    strategy: strategy as unknown as EvaluatedStrategy,
                    legacyYears: horizonYears,
                    partnerEntryYear,
                    partnerRoomPrice: partnerFutureRoomPrice,
                    originalEntrantAliveAtEvent: originalEntrantAliveAtPartnerEntry,
                    baseAgedCareCosts: (strategy.strategySpecificAgedCareCosts as AgedCareCosts) || agedCareCosts,
                });
                if (!result) return null;
                return {
                    strategy,
                    result,
                    adjustedLegacy: result.adjustedLegacy,
                    radPaid: estimateRadPaidForStrategy(strategy as unknown as EvaluatedStrategy),
                };
            })
            .filter((row): row is NonNullable<typeof row> => row !== null);

        if (scored.length === 0) return null;

        // Primary: highest future-adjusted legacy. Tie-break (within band): lowest estimated RAD paid.
        const maxAdjustedLegacy = scored.reduce((m, row) => Math.max(m, row.adjustedLegacy), -Infinity);
        if (!Number.isFinite(maxAdjustedLegacy)) return null;

        const bandUsd = SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD;
        const threshold = maxAdjustedLegacy - bandUsd;
        const nearBest = scored.filter((row) => row.adjustedLegacy >= threshold);

        const topAdjustedRow = scored.reduce((best, row) => (row.adjustedLegacy > best.adjustedLegacy ? row : best), scored[0]!);
        const pickedRow = nearBest.reduce((best, row) => {
            if (row.radPaid < best.radPaid) return row;
            if (row.radPaid === best.radPaid && row.adjustedLegacy > best.adjustedLegacy) return row;
            return best;
        }, nearBest[0]!);

        const usedLowerRadTieBreak = pickedRow.strategy?.name !== topAdjustedRow.strategy?.name;

        return {
            ...pickedRow.strategy,
            isFutureEventOverlayCard: true,
            name: `${pickedRow.strategy.name} (Partner enters later)`,
            recommendationLabels: [
                usedLowerRadTieBreak
                    ? 'Optimal Legacy (Partner Later) — lower RAD option'
                    : 'Optimal Legacy (Partner Later Scenario)',
            ],
            futureEventAssumptions: pickedRow.result.assumptions,
            futureAdjustedLegacy: pickedRow.adjustedLegacy,
            futureEventAdjustmentDelta: pickedRow.result.adjustmentDelta,
            futureEventMeta: {
                bandUsd,
                baselineLegacy: pickedRow.result.baselineLegacy,
                maxAdjustedLegacy,
                selectedAdjustedLegacy: pickedRow.adjustedLegacy,
                topAdjustedStrategyName: topAdjustedRow.strategy?.name,
                selectedStrategyName: pickedRow.strategy?.name,
                radPaidSelected: pickedRow.radPaid,
                radPaidTopAdjusted: topAdjustedRow.radPaid,
                usedLowerRadTieBreak,
            },
            // Keep baseline snapshot legacyValue intact; the future-aware value is carried separately.
        };
    }, [analysis, agedCareCosts, currentSettings, legacyYears]);

    const showFutureEventFourthCard = !!futureEventOptimalStrategy;

    const lowerRadNearBestStrategy = useMemo<Strategy | null>(() => {
        if (!analysis?.strategies?.length) return null;
        /*
         * THE SECOND CARD, ONLY FOR A REAL TRADE-OFF (owner, 20 Oct 2026: "$5k difference for $771 LESS"
         * is not a choice). The rule is planChoiceRule.ts, the same in every module: close enough to the
         * estate leader to weigh, and clearly better on something else — much less money moved, more to
         * spare each year, or no irreversible step where the leader takes one.
         *
         * THE SAME FIELD AS THE LEADERS (owner-caught, 9 Oct 2026): a strategy the horizon rule set aside
         * can never fill this slot.
         */
        const eligible = (analysis.strategies as Strategy[]).filter((s) => !engineStrategySetAside(s, factFind) && !isStayPutRow(s));
        const leadName = (analysis.recommendedOptimalStrategies || []).find((r) => r.label === 'Optimal Legacy')?.strategy?.name;
        const lead = eligible.find((s) => s.name === leadName) ?? null;
        const second = pickSecondPlan(eligible, lead, agedCareReadings as unknown as PlanReadings<Strategy>, (a, b) => a.name === b.name);
        if (!second) return null;
        return {
            ...second.strategy,
            isLowerRadNearBestCard: true,
            recommendationLabels: [SECOND_PLAN_CHIP],
            lowerRadNearBestMeta: { line: secondPlanLine(second, formatCurrency), chip: secondPlanChip(second) },
        };
    }, [analysis, factFind]);

    const showLowerRadNearBestCard = !!lowerRadNearBestStrategy;
    if (acNeedsNewMoneyAnswer) {
        return (
            <div className="max-w-3xl mx-auto mt-6">
                <NewMoneyProfileGate
                    onAnswer={answerNewMoneyProfile}
                    onSkip={() => setNewMoneySkipped(true)}
                    contextLine="Some of the strategies I'll compare invest money that gets freed up — for example if downsizing the home is modelled alongside the aged care move."
                />
            </div>
        );
    }
    if (acNeedsAnnuityAnswer) {
        return (
            <div className="max-w-3xl mx-auto mt-6">
                <AnnuityPreferenceGate
                    offerGuaranteed={guaranteedShapeAvailable(factFind as never)}
                    guaranteeWindow={guaranteeWindowWords({
                        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
                        sex: factFind?.client?.sex,
                        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
                        partnerSex: factFind?.partner?.sex,
                        ownerName: factFind?.client?.firstName,
                        partnerName: factFind?.partner?.firstName,
                    })}
                    onAnswer={answerAnnuityPreference}
                    onSkip={() => setAnnuityPrefSkipped(true)}
                    contextLine="Some of the strategies I'll compare move savings into a guaranteed income to lower the care fees."
                />
            </div>
        );
    }
    if (acNeedsTbcAnswer) {
        return (
            <div className="max-w-3xl mx-auto mt-6">
                <TransferCapGate
                    name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                    onAnswer={answerTbc}
                    onSkip={() => setTbcSkipped(true)}
                />
            </div>
        );
    }
    const waitingForAnalysis = factFind && agedCareCosts && !analysis && !error;
    if ((loading || waitingForAnalysis) && openingRef.current === 'quiet') {
        // A saved plan coming back: no illustration, no floor — just a moment while its figures are rebuilt.
        // In place, not a fixed overlay: inside the Card a fixed overlay was trapped by its backdrop filter
        // and filled only the card, so a reader saw a grey box rather than a page (17 Oct 2026).
        return (
            <div className="flex min-h-[280px] items-center justify-center" data-testid="engine-quiet-opening">
                <div className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 shadow-card px-5 py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600" />
                    <p className="text-sm text-slate-700">Opening your saved plan…</p>
                </div>
            </div>
        );
    }
    if (loading || waitingForAnalysis) {
        return (
            <div className="flex min-h-[420px] items-center justify-center" data-testid="engine-thinking">
                <div className="text-center max-w-lg px-6">
                    {/* Analysis illustration: chart bars + trend */}
                    <div className="mb-8 flex justify-center">
                        <svg width="240" height="140" viewBox="0 0 240 140" fill="none" className="text-blue-600/90">
                            <defs>
                                <linearGradient id="barGrad" x1="0" y1="1" x2="0" y2="0">
                                    <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="currentColor" stopOpacity="0.9" />
                                </linearGradient>
                            </defs>
                            <rect x="40" y="70" width="28" height="50" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s' }} />
                            <rect x="80" y="50" width="28" height="70" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.2s' }} />
                            <rect x="120" y="35" width="28" height="85" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.4s' }} />
                            <rect x="160" y="55" width="28" height="65" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.6s' }} />
                            <rect x="200" y="25" width="28" height="95" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.8s' }} />
                            <path d="M 48 95 Q 88 75 128 60 T 208 45" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.7" strokeDasharray="6 4" className="animate-pulse" style={{ animationDuration: '2s' }} />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">
                        {analysis ? 'Re-optimising with new settings...' : 'Optimising your funding strategies'}
                    </h2>
                    <p className="text-gray-600 mb-6">
                        We're working through a large number of simulated options - this may take a few minutes.
                    </p>
                    {progress.total > 0 && (
                        <p className="text-sm text-blue-700 font-medium">
                            Running scenario {progress.current} of {progress.total}
                        </p>
                    )}
                    <div className="mt-6 flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-200 border-t-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white border border-slate-200/70 border-l-4 border-red-400 rounded-lg p-6">
                <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
                        <div>
                        <h3 className="text-lg font-medium text-red-800">Analysis Error</h3>
                        <p className="text-red-600 mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!analysis || !analysis.strategies || analysis.strategies.length === 0) {
        return (
            <div className="text-center p-8">
                <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                {/* Said in plain words, with what to do (live review, 22 Oct 2026: "No Strategies Available. Please check
                    your input data"). The engine tests nothing without a room price, by design: it never guesses one. */}
                <h3 className="text-lg font-medium text-gray-900 mb-2">{(Number(agedCareCosts?.radAmount) || 0) > 0 ? 'No plans could be tested' : 'The room price is needed first'}</h3>
                <p className="text-gray-600">{(Number(agedCareCosts?.radAmount) || 0) > 0 ? 'Something on the file stopped the plans being tested. Check the figures on steps 1 and 2, then try again.' : 'Every plan is a way of paying for the room, so we test them once its price is in. Enter it on step 1, "What the room costs".'}</p>
            </div>
        );
    }

    // Top 3 recommended strategies (Optimal Legacy, Optimal Cashflow, Optimal Age Pension); fallback to first 3 if no recommended list
    const recommended = analysis.recommendedOptimalStrategies || [];
    const topStrategies = (() => {
        if (recommended.length === 0) return analysis.strategies.slice(0, 3);

        // Collapse duplicate strategies and keep all recommendation labels as badges.
        const byStrategyName = new Map<string, Strategy>();
        recommended.forEach((row) => {
            const name = row?.strategy?.name;
            if (!name || !row?.strategy) return;

            if (!byStrategyName.has(name)) {
                byStrategyName.set(name, { ...row.strategy, recommendationLabels: [row.label] });
                return;
            }

            const existing = byStrategyName.get(name)!;
            const labels = existing.recommendationLabels || [];
            if (row.label && !labels.includes(row.label)) labels.push(row.label);
            byStrategyName.set(name, { ...existing, recommendationLabels: labels });
        });

        const uniqueRecommended = Array.from(byStrategyName.values());
        if (uniqueRecommended.length >= 3) return uniqueRecommended.slice(0, 3);

        const used = new Set(uniqueRecommended.map((s) => s.name));
        const fillers = analysis.strategies.filter((s) => !used.has(s.name)).slice(0, 3 - uniqueRecommended.length);
        return [...uniqueRecommended, ...fillers];
    })();
    // Goal-first display (v1): legacy + "all goals" surface = Optimal Legacy + optional Lower RAD + optional future-event card;
    // cashflow / pension = single primary card only (no Lower RAD / future-event extras — keeps non-legacy views light).
    const effectiveOptimisationSurface = optimisationFocus === 'all' ? 'legacy' : optimisationFocus;
    const displayedGoalStrategies = (() => {
        const rec = analysis.recommendedOptimalStrategies || [];
        const enrich = (label: string): Strategy | null => {
            const row = rec.find((r) => r.label === label);
            if (!row?.strategy?.name) return null;
            const merged = topStrategies.find((s) => s.name === row.strategy.name);
            return merged || row.strategy;
        };
        if (effectiveOptimisationSurface === 'covered') {
            // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
            if (coveredIsBestEstate(rec)) return [];
            const s = enrich(CASHFLOW_COVERED_LABEL);
            return s ? [s] : (topStrategies[0] ? [topStrategies[0]] : []);
        }
        if (effectiveOptimisationSurface === 'cashflow') {
            const s = enrich('Optimal Cashflow');
            return s ? [s] : (topStrategies[0] ? [topStrategies[0]] : []);
        }
        if (effectiveOptimisationSurface === 'pension') {
            // No plan pays an Age Pension: no card, and the line below says why (owner, 20 Oct 2026).
            const s = enrich('Optimal Age Pension');
            return s ? [s] : [];
        }
        const out = [];
        const leg = enrich('Optimal Legacy');
        if (leg) out.push(leg);
        if (showLowerRadNearBestCard && lowerRadNearBestStrategy) {
            out.push(lowerRadNearBestStrategy);
        }
        if (showFutureEventFourthCard && futureEventOptimalStrategy) {
            out.push(futureEventOptimalStrategy);
        }
        if (out.length) return out;
        return [
            ...topStrategies,
            ...(showFutureEventFourthCard ? [futureEventOptimalStrategy] : []),
            ...(showLowerRadNearBestCard ? [lowerRadNearBestStrategy] : []),
        ].filter(Boolean);
    })();
    /* The words the step-3 screen speaks in (UX review, 17 Oct 2026): who is entering care, who the
       plans are for, how many ways were tested, and the scale the two bars on every card share. */
    const enteringName = (goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'partner' ? partnerDisplayName : clientDisplayName;
    // Who the plans are for, in the reader's voice: "you" for the client, "John and Kerry" for an adviser (21 Oct 2026).
    const voice = householdVoice(factFind as DraftFactFind);
    /** Whether choosing this row must wait for the client's income-only confirmation for its amount (E5). */
    const incomeOnlyHeldFor = (row: Strategy): boolean => {
        const lever = engineAnnuityLeverOf(row as never);
        return !!lever && incomeOnlyHeld(factFind, lever.incomeOnly, lever.amount);
    };
    const waysTested =analysis.totalScenarios || analysis.allSimulatedStrategies?.length || analysis.strategies.length;
    const cardLegacyOf = (s: Strategy): number =>
        s.isFutureEventOverlayCard && s.futureAdjustedLegacy != null ? Number(s.futureAdjustedLegacy) : Number(s.snapshot?.legacyValue) || 0;
    const cardGainMax = Math.max(1, ...displayedGoalStrategies.map((s) => Math.abs(cardLegacyOf(s) - (agedCareBaseline?.legacyValue ?? cardLegacyOf(s)))));
    const cardDepositMax = Math.max(1, ...displayedGoalStrategies.map((s) => Math.max(0, Number(s.radPaidNet) || 0)));
    const innerSelectedStrategyForHighlight = selectedStrategy?.strategy ?? selectedStrategy;
    let suggestionEngineCardHighlightKey =
        innerSelectedStrategyForHighlight?.name != null
            ? strategyCardSelectionKey(innerSelectedStrategyForHighlight)
            : null;
    /*
     * A plan chosen for ANOTHER room never reads as chosen here (owner's journey 2, 17 Oct 2026): the
     * same engine name at a different room price is a different plan. Its room is remembered for the
     * caution above the cards.
     */
    const chosenRoomPrice = Number((innerSelectedStrategyForHighlight as { radRoomPrice?: unknown } | null | undefined)?.radRoomPrice) || 0;
    const thisRoomPrice = Number(agedCareCosts?.radAmount) || 0;
    const chosenForOtherRoom = chosenRoomPrice > 0 && thisRoomPrice > 0 && Math.abs(chosenRoomPrice - thisRoomPrice) > 1 ? chosenRoomPrice : null;
    if (chosenForOtherRoom != null) suggestionEngineCardHighlightKey = null;
    if (
        suggestionEngineCardHighlightKey &&
        !displayedGoalStrategies.some((s) => strategyCardSelectionKey(s) === suggestionEngineCardHighlightKey)
    ) {
        suggestionEngineCardHighlightKey = null;
    }
    // Rows for "Compare all strategies": goal-type leaders + optional cards only (not all simulated scenarios).
    const goalComparisonRows = (() => {
        const rec = analysis.recommendedOptimalStrategies || [];
        const enrich = (label: string): Strategy | null => {
            const row = rec.find((r) => r.label === label);
            if (!row?.strategy?.name) return null;
            const merged = topStrategies.find((s) => s.name === row.strategy.name);
            return merged || row.strategy;
        };
        const rows = [];
        const cov = enrich(CASHFLOW_COVERED_LABEL);
        if (cov) rows.push({ goalLabel: CASHFLOW_COVERED_CHIP, strategy: cov });
        const leg = enrich('Optimal Legacy');
        if (leg) rows.push({ goalLabel: BEST_ESTATE_CHIP, strategy: leg });
        const cf = enrich('Optimal Cashflow');
        if (cf) rows.push({ goalLabel: 'Best cashflow', strategy: cf });
        const pen = enrich('Optimal Age Pension');
        if (pen) rows.push({ goalLabel: 'Best pension', strategy: pen });
        if (showLowerRadNearBestCard && lowerRadNearBestStrategy) {
            rows.push({ goalLabel: SECOND_PLAN_CHIP, strategy: lowerRadNearBestStrategy });
        }
        if (showFutureEventFourthCard && futureEventOptimalStrategy) {
            rows.push({ goalLabel: 'Partner enters later (scenario)', strategy: futureEventOptimalStrategy });
        }
        return rows;
    })();
    const tableStrategies = (() => {
        const base = analysis?.allSimulatedStrategies || analysis?.strategies || [];
        const sorted = [...base];
        if (allStrategiesSortBy === 'cashflow') {
            sorted.sort((a, b) => (b.snapshot?.netCashflow ?? -Infinity) - (a.snapshot?.netCashflow ?? -Infinity));
        } else if (allStrategiesSortBy === 'pension') {
            sorted.sort((a, b) => (b.snapshot?.pension ?? -Infinity) - (a.snapshot?.pension ?? -Infinity));
        } else {
            sorted.sort((a, b) => (b.snapshot?.legacyValue ?? -Infinity) - (a.snapshot?.legacyValue ?? -Infinity));
        }
        return sorted;
    })();

    /*
     * THE ADVICE DOCUMENT OPENS STRAIGHT AWAY (owner, 24 Oct 2026). A "Generate Statement of Advice" page
     * used to sit between the button and the document, left over from a PDF-or-digital choice long gone;
     * every other module opens it at once. It opens in a new tab, a copy downloads, and the draft is saved
     * with the file.
     */
    const openAdviceDocument = async (lightOrFull: Strategy) => {
        if (documentBusy) return;
        setDocumentBusy(true);
        try {
            const strategy = await fullStrategyRow(lightOrFull);
            // buildAgedCareDigitalSOASnapshot's own param interface isn't exported (it's an
            // internal detail of that module) — cast via its own inferred parameter type
            // rather than duplicating/exporting an interface just for this call site.
            const snapshot = await buildAgedCareDigitalSOASnapshot({
                selectedStrategy: strategy,
                factFind,
                agedCareCosts,
                analysis,
                coupleValidation,
                reportAudience: 'direct_client'
            } as unknown as Parameters<typeof buildAgedCareDigitalSOASnapshot>[0]);
            openAgedCareDigitalSOAAttachment(snapshot);
            const savedSoaScenario = {
                id: `soa-${Date.now()}`,
                // The plan in plain words (25 Oct 2026); "SE" is kept only as data (soaSource), never shown.
                name: planWords(strategy as never, factFind, getDisplayStrategyName(strategy.name, strategy.radPaidNet ?? null, (Number(strategy.radRoomPrice) || null), secondRoomOf(strategy), moveOf(strategy), villageOf(strategy))).title,
                createdAt: new Date().toISOString(),
                soaSource: 'SE',
                soaDeliveryStatus: 'draft',
                isCurrentForCase: true,
                outputs: {
                    agedCareCost: strategyHouseholdDailyCost(strategy as never, agedCareCosts as never) * 365,
                    agePension: strategy.snapshot?.pension || 0,
                    cashFlow: strategy.snapshot?.netCashflow || 0,
                    legacyEstimate: strategy.isFutureEventOverlayCard && strategy.futureAdjustedLegacy != null
                        ? strategy.futureAdjustedLegacy
                        : (strategy.snapshot?.legacyValue ?? 0),
                },
                soaSnapshot: snapshot,
            };
            onScenarioSaved?.(savedSoaScenario);
        } finally {
            setDocumentBusy(false);
        }
    };

    /*
     * ONE CARD, DRAWN ONE WAY: the leading plans, and a set-aside plan opened with "See it anyway" (owner,
     * 23 Oct 2026: that link opened the year-by-year projections instead of the plan).
     */
    const renderEngineCard = (strategy: Strategy, index: number | string) => {
        const cardKey = strategyCardSelectionKey(strategy);
        const isChosen = suggestionEngineCardHighlightKey != null && cardKey === suggestionEngineCardHighlightKey;
        const legacyHere = cardLegacyOf(strategy);
        const baseLegacy = agedCareBaseline?.legacyValue ?? null;
        const legacyDelta = baseLegacy != null ? legacyHere - baseLegacy : null;
        const paidHere = Math.max(0, Number(strategy.radPaidNet) || 0);
        const cardCosts = (strategy.strategySpecificAgedCareCosts || {}) as { dailyAccommodationPayment?: number; totalDailyCost?: number };
        const dapHere = Number(cardCosts.dailyAccommodationPayment) || 0;
        const baseCosts = (agedCareBaseline?.agedCareCosts || {}) as { dailyAccommodationPayment?: number };
        const baseDap = Number(baseCosts.dailyAccommodationPayment) || 0;
        const careHere = Number(strategy.snapshot?.totalAgedCareCosts) || 0;
        const pensionHere = Number(strategy.snapshot?.pension) || 0;
        const pensionDelta = snapshotBaseline ? pensionHere - snapshotBaseline.pension : null;
        const cfHere = Number(strategy.snapshot?.netCashflow) || 0;
        const runway = fundingRunwayCopy((strategy.projections ?? []) as FundingProjectionYear[]);
        const words = planWords(strategy as never, factFind, getDisplayStrategyName(strategy.name, strategy.radPaidNet, strategy.radRoomPrice, secondRoomOf(strategy), moveOf(strategy), villageOf(strategy)));
        const labels = (strategy.recommendationLabels || [strategy.recommendedLabel || strategy.optimisationLabel]).filter(Boolean) as string[];
        // The chip says what the card is (planChoiceRule.ts): "Leaves the most" only on the plan that does.
        const estateLead = (analysis.recommendedOptimalStrategies || []).find((r) => r.label === 'Optimal Legacy');
        const leadException = estateLead?.lead && estateLead.strategy?.name === strategy.name && !strategy.isLowerRadNearBestCard && !strategy.isFutureEventOverlayCard ? estateLead.lead : null;
        const chip = strategy.isLowerRadNearBestCard
            ? (strategy.lowerRadNearBestMeta?.chip ?? SECOND_PLAN_CHIP)
            : strategy.isFutureEventOverlayCard
                ? `If ${partnerDisplayName} enters care later`
                : labels.includes('Optimal Legacy')
                    ? leadChip(leadException)
                    : labels.includes(CASHFLOW_COVERED_LABEL)
                        ? 'Income covers the costs'
                        : labels.includes('Optimal Cashflow')
                            ? 'Most left over each year'
                            : labels.includes('Optimal Age Pension')
                                ? 'Highest Age Pension'
                                : labels[0] ? friendlyGoalLabel(labels[0]) : null;
        const fundingLines = describeFundingKeys(factFind, flattenSeRadFundingToManualKeys((strategy.radFunding || {}) as never) as Record<string, number>);
        const soldPhrase = fundingPhrase(fundingLines, formatCurrency);
        const lines: React.ReactNode[] = [];
        if (paidHere > 0.5) {
            lines.push(
                <>
                    {soldPhrase ? <><strong className="text-slate-900">{soldPhrase.charAt(0).toUpperCase() + soldPhrase.slice(1)}</strong> {fundingLines.length > 1 ? 'pay' : 'pays'} the deposit.</> : <>The deposit of <strong className="text-slate-900">{formatCurrency(Math.round(paidHere))}</strong> is paid up front.</>}
                    {' '}It comes back when care ends, less what the home keeps — 2% a year for up to five years, so never more than 10%.
                </>,
            );
            lines.push(
                <>
                    Paying {formatCurrency(Math.round(paidHere))} up front{baseDap > 0 ? <> <strong className="text-slate-900">cuts the daily room fee from {formatCurrency(Math.round(baseDap))} to {formatCurrency(Math.round(dapHere))} a day</strong></> : null}
                    {pensionDelta != null && Math.round(pensionDelta) !== 0 ? <>{baseDap > 0 ? ' and ' : ' '}<strong className="text-slate-900">{pensionDelta > 0 ? 'lifts' : 'lowers'} the Age Pension by {formatCurrency(Math.abs(Math.round(pensionDelta)))} a year</strong>{pensionDelta > 0 ? ', because deposit money isn’t counted in the assets test' : ''}</> : null}.
                </>,
            );
        } else {
            lines.push(<>Nothing is paid up front; the whole room is charged daily, like rent{dapHere > 0 ? <> — <strong className="text-slate-900">{formatCurrency(Math.round(dapHere))} a day</strong></> : null}.</>);
        }
        lines.push(
            cfHere < 0
                ? <>Income still falls <strong className="text-slate-900">{formatCurrency(Math.abs(Math.round(cfHere)))} a year short</strong>; savings cover it. Care usually ends well before the savings do.</>
                : <>Income covers the costs, with <strong className="text-slate-900">{formatCurrency(Math.round(cfHere))} a year</strong> to spare.</>,
        );
        // A plan that leaves the family WORSE off than doing nothing says so in its own line — any
        // plan, not only a reverse mortgage (owner, 18 Oct 2026). A better cashflow or pension must
        // never read as the whole story when the estate pays for it.
        if (legacyDelta != null && Math.round(legacyDelta) < 0) {
            lines.push(
                <span data-testid="card-estate-worse">
                    <strong className="text-rose-700">Important:</strong> what you leave your family would be <strong className="text-rose-700">{formatCurrency(Math.abs(Math.round(legacyDelta)))} less</strong> than {bench.word} after about {currentSettings.legacyYears} {currentSettings.legacyYears === 1 ? 'year' : 'years'}. That is the price of what this plan buys elsewhere.
                </span>,
            );
        }
        // A home kept and let: the six-year capital gains tax rule, as every module says it (22 Oct 2026).
        if ((strategy.strategyOptions as { rentHome?: boolean } | undefined)?.rentHome) {
            lines.push(<span data-testid="card-rented-home-cgt"><strong className="text-slate-900">Worth knowing:</strong> {RENTED_HOME_CGT_CARD}</span>);
        }
        if (leadException) {
            lines.push(<span data-testid="lead-exception-note"><strong className="text-slate-900">Why it comes first:</strong> {leadLine(leadException, formatCurrency)}</span>);
        }
        // Why a second card is shown sits beside its figures (EnginePlanCard whyShown), as in every module (23 Oct 2026).
        const whyShown = strategy.isLowerRadNearBestCard && strategy.lowerRadNearBestMeta ? strategy.lowerRadNearBestMeta.line : null;
        const horizonNote = engineStrategyHorizonWarning(strategy, factFind);
        const futureNotice = strategy.isFutureEventOverlayCard && strategy.futureEventAssumptions ? (
            <div className="border-b border-slate-200 bg-white px-5 py-3 text-[13px] text-slate-700">
                <p className="font-semibold text-slate-900">If {partnerDisplayName} enters care in year {strategy.futureEventAssumptions.partnerEntryYear} (room {formatCurrency(strategy.futureEventAssumptions.partnerRoomPrice)})</p>
                <p className="mt-0.5 text-slate-600">{clientDisplayName} is modelled as {strategy.futureEventAssumptions.originalEntrantAliveAtEvent ? 'still living' : 'having passed away'} at that time; about {formatCurrency(strategy.futureEventAssumptions.additionalAnnualCareCost)} a year of extra care cost from then on. An illustration beside the main plan, not a recommendation.</p>
            </div>
        ) : null;
        const detailNode = (
            <div className="space-y-6">

            {/* Asset Allocation */}
            <div className="mb-6">
                {/* Every block on this card states the PERIOD it describes, because they do
                    not share one and the card previously left that to be inferred. These
                    balances are a point in time (today); the snapshot below is a full
                    normalised year. Putting an undated payoff between them made three
                    different bases sit in one tile with nothing distinguishing them. */}
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                    <h4 className="text-lg font-semibold text-gray-800">{secondRoomOf(strategy) ? `Allocation to ${secondRoomOf(strategy)!.leadName}'s room` : 'Allocation to RAD'}</h4>
                    <span className="text-xs text-gray-500">Your balances today</span>
                </div>
                {/* Above the grid, because the grid is what would otherwise be misread: every
                    row shows an opening balance, and a reader with no warning takes
                    "Kept: $150,000" to mean the account is untouched. */}
                {(() => {
                    const mv = (strategy as { facilityMove?: { stayPut?: boolean; fromRefund?: number; refundToday?: number } | null }).facilityMove;
                    if (!mv) return null;
                    return mv.stayPut ? (
                        <p className="text-sm text-slate-700 mb-3" data-testid="card-stay-put">Nothing changes: the current deposit stays where it is and nothing is sold.</p>
                    ) : (
                        <p className="text-sm text-slate-700 mb-3" data-testid="card-refund-first"><strong>{formatCurrency(Math.round(mv.fromRefund || 0))}</strong> comes from the refund of the current deposit first; the balances below show only what is sold on top of it.</p>
                    );
                })()}
                {/* Leaving a village: the exit payment arrives months after entry, so what it
                    pays is not in the balances below and the wait has a cost of its own. */}
                {(() => {
                    const ve = (strategy as { villageExit?: { fromVillage?: number; toBank?: number; refundDate?: string; paidAtMove?: boolean; interim?: { perDay?: number; total?: number; days?: number } | null } | null }).villageExit;
                    if (!ve) return null;
                    const fromVillage = Number(ve.fromVillage) || 0;
                    const by = ve.paidAtMove ? ' (at the move)' : ve.refundDate ? ` (by ${phaseFromLabel(ve.refundDate)})` : '';
                    if (fromVillage <= 0.5) {
                        return <p className="text-sm text-slate-700 mb-3" data-testid="card-village-bank">The village's {formatCurrency(Math.round(Number(ve.toBank) || 0))} exit payment goes to the bank when it arrives{by}; nothing of it goes to the room.</p>;
                    }
                    const perDayVillage = Number(ve.interim?.perDay) || 0;
                    const waitTotal = Number(ve.interim?.total) || 0;
                    return (
                        <p className="text-sm text-slate-700 mb-3" data-testid="card-village-refund"><strong>{formatCurrency(Math.round(fromVillage))}</strong> comes from the village's exit payment when it arrives{by}{waitTotal > 0.5 ? <>; until then ${(Math.round(perDayVillage * 100) / 100).toFixed(2)} a day is charged on that part of the room (about {formatCurrency(Math.round(waitTotal))} in all)</> : null}. The balances below show only what is sold at entry.</p>
                    );
                })()}
                {debtPayoffFundingLine(strategy, factFind) && (
                    <p className="text-sm text-teal-700 mb-3">
                        Separately, {debtPayoffFundingLine(strategy, factFind)} is used to clear debt
                        {careStartDateFromStrategyOptions(strategyOptions) ? ' on the entry date' : ''} — after
                        the balances shown here.
                    </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {strategy.assetAllocation?.filter((asset) => {
                        /*
                         * A ROW FOR AN ACCOUNT THAT DOES NOT EXIST IS PURE NOISE.
                         *
                         * The generator walks every asset type the fact-find can hold, so
                         * an empty placeholder rendered as "Shares / Managed Funds - $0
                         * kept - No balance - Untouched": four pieces of furniture around
                         * nothing, sitting beside the rows that matter (owner, 2026-09-01,
                         * on this screen looking "very bla").
                         */
                        const kept = Math.max(0, parseFloat(String(asset.remainingAmount)) || 0);
                        const used = Math.max(0, parseFloat(String(asset.liquidatedAmount)) || 0);
                        return kept + used > 0;
                    }).map((asset, assetIndex) => {
                        const keptAmount = Math.max(0, parseFloat(String(asset.remainingAmount)) || 0);
                        const soldAmount = Math.max(0, parseFloat(String(asset.liquidatedAmount)) || 0);
                        const opening = keptAmount + soldAmount;
                        const soldShare = opening > 0 ? Math.round((soldAmount / opening) * 100) : 0;
                        return (
                        <div key={assetIndex} className="rounded-xl border border-slate-200 bg-white p-3">
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
                                    {asset.assetName}
                                </span>
                                <span className="shrink-0 text-sm font-bold tabular-nums text-slate-900">
                                    {formatCurrency(keptAmount)}{' '}
                                    <span className="text-[11px] font-medium text-slate-500">kept</span>
                                </span>
                            </div>
                            {/*
                              * HOW MUCH OF THIS ACCOUNT THE PLAN SPENDS, drawn rather than
                              * left to be worked out from two figures (owner, 2026-09-01:
                              * the screen "looks very bla"). Four grey rows of numbers made
                              * the reader do the subtraction; the bar answers "how much of
                              * this is gone" before any of it is read. Percentage and both
                              * dollar figures stay, so the bar adds a reading rather than
                              * replacing one.
                              */}
                            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
                                <div
                                    className={`h-full rounded-full ${soldShare >= 100 ? 'bg-rose-500' : soldShare > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                    style={{ width: `${soldShare > 0 ? Math.max(4, soldShare) : 100}%` }}
                                />
                            </div>
                            <div className="mt-1.5 flex items-baseline justify-between gap-2 text-[11.5px]">
                                <span className="text-slate-500">
                                    {opening > 0 ? `${formatCurrency(opening)} today` : 'No balance'}
                                </span>
                                {soldAmount > 0 ? (
                                    <span className="font-semibold text-rose-700 tabular-nums">
                                        -{formatCurrency(soldAmount)} used ({soldShare}%)
                                    </span>
                                ) : (
                                    <span className="font-semibold text-emerald-700">Untouched</span>
                                )}
                            </div>
                        </div>
                        );
                    })}
    </div>
                {/* THE SECOND ROOM'S DEPOSIT, from the same accounts (owner, 7 Oct 2026: "where is
                    the other $310K coming from"). Taken from what the first deposit leaves, on
                    the second person's own entry date. */}
                {(() => {
                    const sr = secondRoomRowOf(strategy);
                    if (!sr || !((Number(sr.roomPrice) || 0) > 0)) return null;
                    const names = roleNames(factFind, sr.person);
                    const lines = describeFundingKeys(factFind, sr.radFunding);
                    const funded = lines.reduce((t, l) => t + l.amount, 0);
                    const when = sr.entersLater && sr.careStartDate ? `taken when ${names.secondName} moves in, ${phaseFromLabel(sr.careStartDate)}` : 'taken with the first deposit';
                    return (
                        <div className="mt-4" data-testid="second-room-allocation">
                            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                                <h4 className="text-lg font-semibold text-gray-800">Allocation to {names.secondName}&apos;s room</h4>
                                <span className="text-xs text-gray-500">From what is left after {names.firstName}&apos;s deposit — {when}</span>
                            </div>
                            {lines.length === 0 ? (
                                <p className="text-sm text-slate-600">Nothing is paid up front for {names.secondName}&apos;s room; the whole {formatCurrency(Number(sr.roomPrice) || 0)} is paid daily.</p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {lines.map((l) => (
                                        <div key={l.key} className="rounded-xl border border-slate-200 bg-white p-3">
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="text-sm font-semibold text-gray-800">{l.label}</span>
                                                <span className="text-sm font-bold tabular-nums text-rose-700">-{formatCurrency(l.amount)} used</span>
                                            </div>
                                            <p className="mt-1 text-xs text-slate-500">After {names.firstName}&apos;s deposit has come out of it.</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="mt-2 text-sm text-slate-700">
                                {formatCurrency(funded)} paid up front for {names.secondName}&apos;s {formatCurrency(Number(sr.roomPrice) || 0)} room
                                {funded + 0.5 < (Number(sr.roomPrice) || 0) ? <> — the unpaid {formatCurrency((Number(sr.roomPrice) || 0) - funded)} is charged daily as a DAP.</> : '.'}
                            </p>
                        </div>
                    );
                })()}
</div>

{/* The card's STORY — the same "in plain words" principle as Manual Control's plan
    recap: what this strategy actually does, told as a money FLOW the eye can follow
    (Sell -> RAD -> DAP) with one caption for the nuance chips can't carry. Built from
    this card's own rows and its own engine costs; the tiles below then hold each
    figure's why + calculation. Chips over prose: owner feedback 2026-08-24 ("too much
    visual load ... explain what's going on"). */}
{(() => {
    const sold = (strategy.assetAllocation || []).filter(
        (a) => (parseFloat(String(a.liquidatedAmount)) || 0) > 0,
    );
    const roomPrice = Number(strategy.radRoomPrice) || 0;
    const radPaidHere = Number(strategy.radPaidNet) || 0;
    const unpaidRoom = Math.max(0, roomPrice - radPaidHere);
    const cardCosts = (strategy.strategySpecificAgedCareCosts || {}) as { dailyAccommodationPayment?: number };
    const cardDap = Number(cardCosts.dailyAccommodationPayment) || 0;
    if (!(roomPrice > 0)) return null;
    /*
     * The CGT the engine already deducted (radPaidNet is NET of it) but never showed
     * (owner, 2026-08-24). Rebuilt from the card's own funding map with the SAME shared
     * functions the engine ran - flatten to manual keys, total with
     * computeRadFundingTotals, detail with calculateSharePartialDisposalTax - so the
     * figure here equals the deduction to the cent.
     */
    const manualKeys = flattenSeRadFundingToManualKeys((strategy.radFunding || {}) as never);
    const fundingTotals = computeRadFundingTotals(factFind, manualKeys, {});
    const cgtRows = Object.entries(fundingTotals.byKey)
        .filter(([, e]) => e.cgt > 0)
        .map(([key, e]) => {
            const parsed = parseRadFundingAssetKey(key);
            const located = parsed ? findAssetInFactFind(factFind, parsed.assetType, parsed.assetId) : null;
            const asset = located?.asset;
            const summary = asset
                ? calculateSharePartialDisposalTax(factFind as never, asset as never, e.gross, { trustDisposal: 'distribute' })
                : null;
            return { label: asset?.description || 'shares', gross: e.gross, cgt: e.cgt, net: e.net, summary };
        });
    const totalCgt = cgtRows.reduce((t, r) => t + r.cgt, 0);
    const cgtExplain =
        cgtRows
            .map((r) => {
                const gain = (r.summary as { capitalGain?: number } | null)?.capitalGain;
                const assessable = (r.summary as { assessableCapitalGain?: number } | null)?.assessableCapitalGain;
                return (
                    `${r.label}: selling ${formatCurrency(r.gross)} realises an estimated capital gain of ` +
                    `${gain != null ? formatCurrency(gain) : 'n/a'}; after the 50% discount, ` +
                    `${assessable != null ? formatCurrency(assessable) : 'half'} is taxable, giving estimated tax of ` +
                    `${formatCurrency(r.cgt)} — so ${formatCurrency(r.net)} reaches the deposit.`
                );
            })
            .join(' ') +
        ` Worked out from the recorded cost base (or the standard assumption where none is recorded) at each owner's marginal rate — the same estimate Manual Control's funding panel shows. An accountant can confirm the exact amount.`;
    /*
     * TRIMMED (owner, 18 Oct 2026: "is it a bit duplicated?"). The card's three layers and
     * "Explain this" already say what the plan does, so only what is said NOWHERE else
     * stays here: the second stage where a partner enters later, and the capital gains tax
     * the engine deducted on the way to the deposit.
     */
    const costs = strategy.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number; totalDailyCost?: number } | undefined;
    const secondRoomRow = (strategy as { secondRoom?: { person?: 'client' | 'partner'; entersLater?: boolean } }).secondRoom;
    const phaseTwo = (strategy as { phaseTwo?: { fromDate?: string; householdDailyCost?: number } | null }).phaseTwo;
    const phaseTwoDaily = strategyPhaseTwoDailyCost({ phaseTwo });
    const phaseNames = { ...roleNames(factFind, secondRoomRow?.person), fromLabel: phaseFromLabel(phaseTwo?.fromDate || '') };
    /* Both of a couple in care: each room's deposit and unpaid part, as chips, because the
       card's one line cannot carry two rooms (owner, 7 Oct 2026). One room needs no chips —
       the card's sub-line already says "65% of the room as a deposit; $280,000 paid daily". */
    const flow = (() => {
        const sr = secondRoomRowOf(strategy);
        if (!sr || !((Number(sr.roomPrice) || 0) > 0)) return [];
        const names = roleNames(factFind, sr.person);
        const price = Number(sr.roomPrice) || 0;
        const paid = Math.min(price, Number(sr.radPaid) || 0);
        const unpaid = Math.max(0, price - paid);
        const dap = Number(sr.agedCareCosts?.dailyAccommodationPayment) || 0;
        const paidWhen = sr.entersLater && sr.careStartDate ? `, paid ${phaseFromLabel(sr.careStartDate)}` : '';
        return [
            { amount: radPaidHere, label: 'RAD', sub: unpaidRoom > 0.5 ? `refundable ${String.fromCharCode(0xb7)} ${Math.round((radPaidHere / roomPrice) * 100)}% of room` : 'refundable ' + String.fromCharCode(0xb7) + ' room paid in full', tone: 'good' as const },
            ...(unpaidRoom > 0.5 ? [{ amount: unpaidRoom, label: 'unpaid', sub: cardDap > 0 ? `DAP ${formatCurrency(cardDap)}/day ${String.fromCharCode(0xb7)} like rent` : 'charged daily, like rent', tone: 'warn' as const }] : []),
            ...(paid > 0.5 ? [{ amount: paid, label: `RAD ${String.fromCharCode(0xb7)} ${names.secondName}'s room`, sub: `refundable ${String.fromCharCode(0xb7)} ${Math.round((paid / price) * 100)}% of room${paidWhen}`, tone: 'good' as const }] : []),
            ...(unpaid > 0.5 ? [{ amount: unpaid, label: `unpaid ${String.fromCharCode(0xb7)} ${names.secondName}'s room`, sub: dap > 0 ? `DAP ${formatCurrency(dap)}/day ${String.fromCharCode(0xb7)} like rent` : 'charged daily, like rent', tone: 'warn' as const }] : []),
        ];
    })();
    if (flow.length === 0 && phaseTwoDaily == null && !(sold.length > 0 && totalCgt > 0)) return null;
    return (
        <div className="mb-6 space-y-2 text-[13px] leading-relaxed text-slate-600" data-testid="card-detail-notes">
            {flow.length > 0 && <MoneyFlow steps={flow} />}
            {phaseTwoDaily != null && costs && (
                <p><span data-testid="card-phase-two">{recapPhaseLine(phaseNames, Number(costs.totalDailyCost) || 0, phaseTwoDaily)}</span></p>
            )}
            {sold.length > 0 && totalCgt > 0 && (
                <p>
                    Funded by the sales shown above — an estimated{' '}
                    <span className="font-semibold text-slate-700">{formatCurrency(totalCgt)}</span> in{' '}
                    <JargonTerm term="capital gains tax" /> is deducted before the money reaches the
                    deposit (<JargonTerm term="how is it worked out?">{cgtExplain}</JargonTerm>).
                </p>
            )}
        </div>
    );
})()}
{/* Annual Snapshot — extracted so each tile can carry its own why + calculation.
    See AnnualSnapshot.tsx: the six figures, hover tooltips kept, tap layer added. */}
<AnnualSnapshot strategy={strategy} agedCareCosts={agedCareCosts} factFind={factFind} baseline={snapshotBaseline} openRequest={tileRequest && tileRequest.cardKey === cardKey ? { tile: tileRequest.tile, nonce: tileRequest.nonce } : null} />
            </div>
        );
        return (
            <EnginePlanCard
                key={`${strategy.name}-${index}`}
                testId={`engine-plan-card-${index}`}
                chip={chip}
                whyShown={whyShown}
                chipTone={strategy.isLowerRadNearBestCard ? 'neutral' : 'blue'}
                title={words.title}
                sub={words.sub || undefined}
                legacy={legacyHere}
                legacyDelta={legacyDelta}
                horizonYears={currentSettings.legacyYears}
                baselineWord={bench.word}
                bars={{ gainPct: legacyDelta != null ? (Math.abs(legacyDelta) / cardGainMax) * 100 : 0, tiedUp: paidHere, tiedUpPct: (paidHere / cardDepositMax) * 100 }}
                numbers={[
                    { label: 'Care costs', value: careHere, unit: 'a year', vs: <>{dapHere > 0 ? `${formatCurrency(Math.round(dapHere * 365))} of it is the daily room fee · ` : ''}{bench.word} {formatCurrency(Math.round(snapshotBaseline?.careCosts ?? 0))}</>, onWhy: () => setCashflowFor({ row: strategy, page: 'care' }) },
                    { label: `${enteringName}'s Age Pension`, value: pensionHere, unit: 'a year', tone: pensionDelta != null && pensionDelta > 0 ? 'good' : 'neutral', vs: pensionDelta != null && Math.round(pensionDelta) !== 0 ? <span className={pensionDelta > 0 ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>{pensionDelta > 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(pensionDelta)))} a year{pensionDelta > 0 ? ' — deposit money isn’t counted' : ''}</span> : `the same as ${bench.word}`, onWhy: () => setCashflowFor({ row: strategy, page: 'pension' }) },
                    { label: cfHere < 0 ? 'Each year, short by' : 'Left over each year', value: Math.abs(cfHere), tone: cfHere < 0 ? 'neutral' : 'good', vs: <>{snapshotBaseline ? `was ${formatCurrency(Math.abs(Math.round(snapshotBaseline.netCashflow)))} ${bench.after}` : ''}{cfHere < 0 ? ' · met from savings' : ''}</>, onWhy: () => setCashflowFor({ row: strategy, page: 'cashflow' }) },
                ]}
                runline={(() => {
                    // Every account in the order it is drawn, then the deposit or borrowing — the line every
                    // module's card now gives (owner, 22 Oct 2026). The older headline stays as a fallback.
                    const ff = (strategy.originalFactFind || factFind) as DraftFactFind;
                    const forWhom = (ff.goals as { aged_care_for?: string } | undefined)?.aged_care_for;
                    const refundTo = ff.adviceFor === 'couple' && forWhom !== 'both' ? ((forWhom === 'partner' ? ff.client?.firstName : ff.partner?.firstName) || 'your partner') : 'the estate';
                    const line = runwayCardLine({ rows: (strategy.projections ?? []) as never, factFind: ff, horizonYears: Number(currentSettings.legacyYears) || 5, enteringName, deposit: { paid: Number(strategy.radPaidNet) || 0, refundTo } });
                    if (line) return <><strong className="text-slate-900">When the money runs out:</strong> {line}</>;
                    return runway ? <><strong className="text-slate-900">When the money runs out:</strong> {runway.headline}{runway.flag ? ` ${runway.flag}` : ''}</> : null;
                })()}
                worthKnowing={(() => {
                    /* ON THE CARD, not only inside "Explain this" (owner, 2026-09-01: "it wasn't until I
                       clicked on 'Explain this' that I realised that this is a reverse mortgage"). A loan
                       with no repayments puts nothing in the cashflow number above, which is exactly
                       what makes it invisible; the one line that has to reach a reader who never opens
                       the explanation is that there IS a cost and where it went. */
                    const er = (strategy as { equityRelease?: Record<string, unknown> }).equityRelease;
                    const loan = er && Number(er.loanAmount) > 0 ? er : null;
                    const grown = loan ? Number(loan.projectedBalance) || 0 : 0;
                    const drawn = loan ? Number(loan.loanAmount) || 0 : 0;
                    const yrs = loan ? Number(loan.years) || 0 : 0;
                    // A debt this plan clears, said on the card (the rebuild had dropped the old card's
                    // "Clears …" line; owner, 18 Oct 2026). Where it comes from is in "Show the detail".
                    const cleared = debtsClearedBy(strategy);
                    const clearedFrom = cleared ? debtPayoffFundingLine(strategy, factFind) : null;
                    if (!horizonNote && !loan && !cleared) return null;
                    return (
                        <>
                            {cleared && (
                                <p data-testid="card-clears-debt">
                                    <span className="font-semibold">Clears the {cleared}</span>{clearedFrom ? <> with {clearedFrom}</> : null}{clearedDebtsCarryInterest(strategy, factFind) ? ', so the interest on it stops.' : ', so it is no longer owed.'}
                                </p>
                            )}
                            {horizonNote && <p><span className="font-semibold">Worth knowing:</span> {horizonNote}</p>}
                            {loan && (
                                <p>
                                    <span className="font-semibold">This is a {loan.kind === 'heas' ? 'Home Equity Access Scheme loan' : 'reverse mortgage'}.</span>{' '}
                                    There are no repayments, so none of its cost appears in the cashflow below.
                                    {grown > drawn && yrs > 0 && <> The debt compounds from {formatCurrency(drawn)} to <span className="font-semibold">{formatCurrency(grown)}</span> over {yrs} {yrs === 1 ? 'year' : 'years'} and is settled out of the house.</>}{' '}
                                    <span className="font-semibold">Explain this</span> sets out how it works and what to watch.
                                </p>
                            )}
                        </>
                    );
                })()}
                lines={lines}
                // What the family receives from the guaranteed income at the start, middle and end
                // (guaranteed income review E4; mock-up screens 3 and 4).
                familyPanel={<EngineAnnuityFamilyPanel row={strategy} evaluated={analysis.allSimulatedStrategies || analysis.strategies} factFind={factFind} personName={enteringName} horizonYears={currentSettings.legacyYears} testId={`engine-plan-card-${index}-family`} />}
                // Income only is held until the client confirms it, for this amount (E5; mock-up screen 4).
                beforeChoose={(() => {
                    const lever = engineAnnuityLeverOf(strategy);
                    if (!lever?.incomeOnly) return null;
                    const confirm = incomeOnlySetter(setFactFind);
                    return confirm ? <IncomeOnlyBeforeChoose factFind={factFind} amount={lever.amount} onChange={confirm} testId={`engine-plan-card-${index}-income-only`} /> : null;
                })()}
                chooseHeld={incomeOnlyHeldFor(strategy)}
                chosen={isChosen}
                onExplain={() => setExplainStrategy(strategy)}
                onRisks={() => setRiskStrategy(strategy)}
                onChoose={() => { if (!incomeOnlyHeldFor(strategy)) saveSelectedStrategy(strategy); }}
                chooseLabel={suggestionEngineCardHighlightKey != null ? 'Choose this instead' : 'Choose this plan'}
                detail={detailNode}
                detailOpen={detailOpenKey === cardKey}
                onToggleDetail={() => setDetailOpenKey((k) => (k === cardKey ? null : cardKey))}
                notices={futureNotice}
                // The ONE "Get the advice document" on this screen sits here, where the plan was just
                // chosen (owner, 26 Oct 2026); the banner above keeps its sentence but not a button.
                chosenStrip={
                    <ChosenAdviceLine
                        possessiveFile={voice.possessive}
                        onAdvice={() => { void openAdviceDocument(strategy); }}
                        busy={documentBusy}
                        onCustomise={onBuildYourOwn ? () => onBuildYourOwn(strategy) : null}
                    />
                }
            />
        );
    };

    return (
        <div className="space-y-6">
            {/*
              * "Understanding your plan" for a SUGGESTION ENGINE strategy — the same component, the
              * same modal and the same baseline Manual Control uses, driven by this strategy's own
              * figures. The tile expanders on each card explain what a FEE is; this explains what
              * this PLAN does, and why each figure moved.
              *
              * Every value below is taken from the strategy row as the engine built it. The one that
              * needs care is `simulatedFinancials`: the recap nets care off itself, so this must be
              * the GROSS financials (strategyFinancials), never `snapshot`, whose cashflow already
              * has care removed. Passing the snapshot would charge the fees twice.
              */}
            {explainStrategy && (
                <PlanExplanationModal
                    eyebrow="Explain this"
                    title={planWords(explainStrategy, factFind, getDisplayStrategyName(
                        explainStrategy.name,
                        explainStrategy.radPaidNet,
                        explainStrategy.radRoomPrice,
                        secondRoomOf(explainStrategy),
                        moveOf(explainStrategy),
                        villageOf(explainStrategy),
                    )).title}
                    intro={<>Each line says what you do, and what that does to the numbers. {bench.sentence}</>}
                    onClose={() => setExplainStrategy(null)}
                    footer={
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                const s = explainStrategy;
                                setExplainStrategy(null);
                                handleDeepDive(s);
                            }}
                        >
                            See the year-by-year figures
                        </Button>
                    }
                >
                    <StrategyPlanRecap
                        eyebrow={null}
                        told="causeEffect"
                        causeEffectFacts={{
                            baselineCareAnnual: snapshotBaseline?.careCosts ?? null,
                            baselineDapDaily: Number((agedCareBaseline?.agedCareCosts as { dailyAccommodationPayment?: number } | null | undefined)?.dailyAccommodationPayment) || null,
                            baselineInvestmentIncome: snapshotBaseline?.investmentIncome ?? null,
                            planInvestmentIncome: explainStrategy.snapshot?.investmentIncome != null
                                ? Number(explainStrategy.snapshot.investmentIncome) - (Number(explainStrategy.snapshot.rentalExpenses) || 0)
                                : null,
                            enteringName,
                            illnessSeparated: factFind?.adviceFor === 'couple' && isIllnessSeparatedCouple(factFind as unknown as FactFind, agedCareEntrantResidencyOverrides(factFind as unknown as FactFind) as never),
                        }}
                        factFind={explainStrategy.originalFactFind || factFind}
                        overallStrategy={explainStrategy.strategyOptions || {}}
                        strategyInputs={explainStrategy.strategyOptions || {}}
                        downsizeDetails={explainStrategy.strategyOptions?.downsizeDetails || {}}
                        investmentPropertyDetails={explainStrategy.strategyOptions?.investmentPropertyDetails || {}}
                        reverseMortgageDetails={explainStrategy.strategyOptions?.reverseMortgageDetails || null}
                        heasDetails={explainStrategy.strategyOptions?.heasDetails || null}
                        otherStrategies={explainStrategy.strategyOptions?.otherStrategies || null}
                        // Money the plan already has coming — a village's exit payment, a current room's
                        // refund — opens the story, as it does in the advice document (9 Oct 2026).
                        {...engineRowPieces(explainStrategy, (explainStrategy.originalFactFind || factFind) as DraftFactFind)}
                        annuityRecord={explainStrategy.strategyOptions?.annuityRecord || null}
                        debtPayoffPlan={explainStrategy.strategyOptions?.debtPayoff || null}
                        // The flattened `type-id` keys, not the engine's nested shape — the recap
                        // reads the Manual Control form. Converted server-side by the engine's own
                        // helper so both surfaces name the same funding assets.
                        radFunding={explainStrategy.radFundingManualKeys || {}}
                        cgtOverrides={explainStrategy.strategyOptions?.cgtOverrides || {}}
                        radInput={Number(explainStrategy.radRoomPrice) || 0}
                        radPaid={Number(explainStrategy.radPaidNet) || 0}
                        secondRoom={(() => { const sr = (explainStrategy as { secondRoom?: { person: string; roomPrice: number; radPaid: number; agedCareCosts?: { totalDailyCost?: number } } }).secondRoom; return sr ? { name: roleNames(factFind, sr.person as 'client' | 'partner').secondName, leadName: roleNames(factFind, sr.person as 'client' | 'partner').firstName, roomPrice: sr.roomPrice, radPaid: sr.radPaid, dailyTotal: sr.agedCareCosts?.totalDailyCost ?? null, funding: describeFundingKeys(factFind, (sr as { radFunding?: Record<string, number> }).radFunding), paidFromLabel: (sr as { entersLater?: boolean; careStartDate?: string }).entersLater && (sr as { careStartDate?: string }).careStartDate ? phaseFromLabel((sr as { careStartDate?: string }).careStartDate!) : undefined } : null; })()}
                        agedCareCosts={explainStrategy.strategySpecificAgedCareCosts || {}}
                        simulatedFinancials={explainStrategy.strategyFinancials || {}}
                        extraServiceFeeInput={0}
                        // ONE baseline for the cards and the explanation — passed down, never rebuilt.
                        sharedBaseline={
                            agedCareBaseline?.financials
                                ? { pension: agedCareBaseline.pension, cashFlowAfterCare: agedCareBaseline.cashFlowAfterCare }
                                : null
                        }
                        planRunway={runwayFactsOf(explainStrategy.projections)}
                        {...(() => {
                            // The selected card reads the rail's long-run rows; any other card, its own plan-period rows.
                            const isSelected = !!selectedStrategy && (selectedStrategy as { id?: string }).id != null && (selectedStrategy as { id?: string }).id === (explainStrategy as { id?: string }).id;
                            const rows = (isSelected && selectedPlanRunwayRows?.length ? selectedPlanRunwayRows : (explainStrategy.projections ?? [])) as RunwayRow[];
                            const runway = planRunwayCopy(rows as never, rows.length);
                            const ff = (explainStrategy.originalFactFind || factFind) as DraftFactFind;
                            const forWhom = (ff.goals as { aged_care_for?: string } | undefined)?.aged_care_for;
                            const refundTo = ff.adviceFor === 'couple' && forWhom !== 'both'
                                ? ((forWhom === 'partner' ? ff.client?.firstName : ff.partner?.firstName) || 'your partner')
                                : 'the estate';
                            const paid = Number(explainStrategy.radPaidNet) || 0;
                            // The story, account by account, with the engine's order and why (owner, 18 Oct 2026).
                            // Only where a year falls short, as every other module: on a plan with money to spare there is no
                            // gap to draw, so no order to tell (consistency audit, 22 Oct 2026).
                            const story = summariseFunding(rows as never).hasDeficit ? buildMoneyStory({ rows: rows as never, factFind: ff, runway, deposit: { paid, refundTo }, horizonYears: Number(legacyYears) || 5, enteringName }) : null;
                            return {
                                moneyOrder: moneyOrderParagraph(rows, ff, runway, { refund: paid, refundTo }),
                                moneyStory: story ? <MoneyStoryBlock story={story} /> : null,
                            };
                        })()}
                        baselineLegacy={agedCareBaseline?.legacyValue ?? null}
                        planLegacy={
                            Array.isArray(explainStrategy.projections) && explainStrategy.projections.length > 0
                                ? explainStrategy.projections[explainStrategy.projections.length - 1]?.netWorth ?? null
                                : null
                        }
                        legacyYears={legacyYears}
                        /*
                         * The engine's annuity terms, told INSIDE the story rather than bolted on
                         * after it. Rendered alongside the recap, the modal showed one small box and
                         * nothing else whenever the recap had nothing to say (owner, 2026-09-01).
                         */
                        engineAnnuityTerms={(explainStrategy as { annuityTerms?: Record<string, unknown> }).annuityTerms}
                        equityRelease={(explainStrategy as { equityRelease?: Record<string, unknown> }).equityRelease}
                        /*
                         * ENTER THE REAL QUOTE (owner, 2026-09-01: the Suggestion Engine "doesn't
                         * provide the ability to enter the actual quote"). Committing one re-runs
                         * the whole analysis, because a real rate can change which strategy wins -
                         * relabelling this one card would leave the ranking built on our rate.
                         */
                        annuityQuoteControl={(() => {
                            /* A quote belongs to THIS plan's type and amount (guaranteed income review M5): other
                               amounts of the same type are scaled from it; other types stay on our estimate. */
                            const lever = (explainStrategy as Strategy).meansTestStrategy as { type?: string; purchaseAmount?: number; annuityType?: string; residualCapital?: number; indexation?: boolean } | null | undefined;
                            const terms = (explainStrategy as { annuityTerms?: { purchaseAmount?: number } }).annuityTerms;
                            const amount = Number(terms?.purchaseAmount) || Number(lever?.purchaseAmount) || 0;
                            const kind = annuityQuoteKindOf(lever);
                            const scope = (
                                <>
                                    Have a quote from a provider? Enter the yearly income they offered for {ANNUITY_QUOTE_KIND_WORDS[kind]} of {formatCurrency(amount)}.
                                    The plans with this type are re-compared on your number, scaled to their amount; the other types stay on our estimate.
                                    {acAnnuityQuote && <> The quote in use: {formatCurrency(acAnnuityQuote.income)} a year for {ANNUITY_QUOTE_KIND_WORDS[acAnnuityQuote.kind]} of {formatCurrency(acAnnuityQuote.amount)}.</>}
                                </>
                            );
                            return (
                                <AnnuityQuoteControl
                                    draft={acAnnuityQuoteDraft}
                                    onDraft={setAcAnnuityQuoteDraft}
                                    committed={acAnnuityQuote ? acAnnuityQuote.income : null}
                                    scope={scope}
                                    onCommit={(next) => { setAcAnnuityQuote(next != null && next > 0 && amount > 0 ? { income: next, kind, amount } : null); setExplainStrategy(null); }}
                                />
                            );
                        })()}
                    />
                    {(() => {
                        // The debts this plan keeps, and what the same plan with them paid off did (C.AC.4's twins:
                        // "<plan> + Clear Debt", or "+ Pay Down Debt (keep repayments)" where it cannot all be cleared).
                        const all = (analysis?.allSimulatedStrategies || analysis?.strategies || []) as Strategy[];
                        const name = String(explainStrategy.name || '');
                        const twin = all.find((s) => s.name === `${name} + Clear Debt`) ?? all.find((s) => s.name === `${name} + Pay Down Debt (keep repayments)`) ?? null;
                        const payments = (explainStrategy.strategyOptions as { debtPayoff?: { payments?: Record<string, number> } } | undefined)?.debtPayoff?.payments || {};
                        const fig = (s: Strategy) => ({ estate: Number(s.snapshot?.legacyValue) || 0, pension: Number(s.snapshot?.pension) || 0, cashflow: Number(s.snapshot?.netCashflow) || 0 });
                        return <DebtsInPlanNote factFind={factFind} clearedAmount={Object.values(payments).reduce((t, x) => t + (Number(x) || 0), 0)} plan={fig(explainStrategy)} twin={twin ? fig(twin) : null} years={legacyYears} context="tested" />;
                    })()}
                    {/* The home kept and let: the six-year capital gains tax rule, whatever the years planned (22 Oct 2026). */}
                    {(explainStrategy.strategyOptions as { rentHome?: boolean } | undefined)?.rentHome && <RentedHomeCgtNote horizonYears={Number(legacyYears) || 5} they={householdVoice(factFind as DraftFactFind).they} />}
                </PlanExplanationModal>
            )}

            {(factFind?.structures || []).length > 0 && (
                <div className="p-4 rounded-lg border border-slate-200/70 border-l-4 border-amber-400 bg-white text-sm text-amber-950 space-y-1">
                    <p className="font-semibold">Trusts and companies in strategy suggestions</p>
                    <p>
                        {isStructureFundingIncludedInSuggestions(factFind)
                            ? 'Automatic suggestions may use trust or company liquid assets and whole property sales. Pension and care fee estimates always include your structures.'
                            : 'Automatic suggestions use personal assets only. Trust and company assets still count toward pension and care fee estimates.'}
                    </p>
                    <p className="text-xs">
                        Change this in Strategy Analysis Setup (when entering the Suggestion Engine) or via the settings
                        gear above.
                    </p>
                </div>
            )}

            {/* Assumptions modal */}
            {showAssumptionsModal && (
                <DialogShell
                    titleId="engine-assumptions-heading"
                    testId="engine-assumptions-dialog"
                    eyebrow="How the plans are tested"
                    title="What the engine assumes"
                    onClose={() => setShowAssumptionsModal(false)}
                    footer={<Button type="button" variant="primary" onClick={() => setShowAssumptionsModal(false)}>Got it</Button>}
                >
                    <div className="space-y-4 text-sm text-slate-700">
                            <p className="text-slate-600">The Suggestion Engine uses the following assumptions. For scenarios that fall outside these, use Manual Control.</p>
                            <ul className="list-disc list-inside space-y-2">
                                <li><strong>Funding optimisation:</strong> For each scenario (e.g. "Pay 50% RAD, Keep Home"), we test every possible order of drawing down from your liquid assets (bank, account-based pensions, shares). We then select the outcome that gives the best projected estate value (legacy) for that scenario. So the strategy shown is the optimal funding mix we found, not a single fixed path.</li>
                                <li><strong>Downsize residual:</strong> When modelling a downsize, surplus from the sale (after the new home purchase) is allocated to the RAD and/or the bank by default. Eligible clients (55+) may also model max downsizer contributions to super, or commencing an account-based pension at minimum drawdown where age-eligible (60+). Other growth assets (e.g. shares, investment properties) are not used for the residual because in aged care we face longevity risk and volatility. If you want to model allocating the residual to shares or property, use the Manual Control section.</li>
                                <li><strong>RAD levels:</strong> Strategies are tested across a range of RAD levels (e.g. 10% to 100% of the room price). Each level shows the outcome if that amount is paid as a Refundable Accommodation Deposit.</li>
                                <li><strong>Selling costs & new home value:</strong> Downsize scenarios use the selling costs and new home value you entered in settings (or in Manual Control). Net proceeds = sale price - selling costs; surplus = net proceeds - new home cost.</li>
                                <li><strong>Emergency fund & legacy years:</strong> Settings such as emergency fund protection and the projection period (legacy years) apply to all strategies. Change these under "Things that change the answer".</li>
                                <li><strong>Income, growth & cost base:</strong> Projected results factor in the income rates, growth rates and any applicable cost base (e.g. for shares or investment property) entered in the fact find. If these assumptions differ in practice, the optimised result may be impacted.</li>
                                {/*
                                  * THIS SAID THE OPPOSITE OF WHAT THE ENGINE DOES.
                                  *
                                  * It read "Annuity not in Suggestion Engine: The Suggestion Engine
                                  * does not evaluate purchasing a lifetime or fixed-term annuity."
                                  * True when written; false since the annuity levers went in. The
                                  * engine now builds up to eight annuity candidates per funding
                                  * scenario (four sizes x two residual-capital variants) and ranks
                                  * them alongside everything else - so a client could read that
                                  * annuities were ignored while looking at a plan containing one
                                  * (owner-caught, 2026-09-01).
                                  *
                                  * The caution that text carried was sound and is kept, because it
                                  * is the honest thing to say about this cohort: a deposit is the
                                  * stronger lever here, and it is refundable.
                                  */}
                                <li><strong>Annuities are modelled, with a caution:</strong> The Suggestion Engine does test buying a guaranteed income for life (a lifetime annuity) — layered on top of a deposit, never instead of one, and never more than the share of the savings the client allowed. It tests the types that pay the money back to the family: money back in the early years and, for someone in aged care, the type that pays the full amount back at any time. Income only (level, or rising with prices) is tested only when the client asked for the most income even if nothing goes back, and a plan using it can&apos;t be chosen until the client confirms they understand that. Weigh it carefully in residential care: length of stay is typically 2&ndash;5 years, an annuity is priced on standard life expectancy, and the capital is committed for life. A <strong>RAD is usually the stronger lever</strong> — it is fully exempt from the Age Pension assets test and it is refunded — so an annuity is generally only worth considering for money left over after the deposit you are comfortable paying. Where one is proposed, the card states who owns it, what comes back and whether the income continues to a surviving partner.</li>
                                {/* How the plans are chosen (planChoiceRule.ts, owner 20 Oct 2026) — the same words in every module. */}
                                {PLAN_CHOICE_RULE_LINES.map((r) => (
                                    <li key={r.label}><strong>{r.label}:</strong> {r.text}</li>
                                ))}
                            </ul>
                    </div>
                </DialogShell>
            )}

            {/* THE CHOSEN PLAN, SAID ONCE, ABOVE EVERYTHING (the mock-up's "You've chosen…" banner, 17 Oct 2026):
                what was chosen, that it is saved, and the one next step — the advice document. The Statement
                of Advice is generated from HERE, for the chosen strategy only (owner, 2 Oct 2026). */}
            {(() => {
                if (!suggestionEngineCardHighlightKey) return null;
                const chosenRow = displayedGoalStrategies.find((s) => strategyCardSelectionKey(s) === suggestionEngineCardHighlightKey);
                if (!chosenRow) return null;
                const title = planWords(chosenRow, factFind, getDisplayStrategyName(chosenRow.name, chosenRow.radPaidNet, chosenRow.radRoomPrice, secondRoomOf(chosenRow), moveOf(chosenRow), villageOf(chosenRow))).title;
                return (
                    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card" data-testid="chosen-plan-banner">
                        <span className="shrink-0 text-emerald-600" aria-hidden><CheckCircle size={26} strokeWidth={1.75} /></span>
                        <p className="min-w-[240px] flex-1 text-[14px] leading-relaxed text-slate-700">
                            <strong className="text-slate-900">You&apos;ve chosen &quot;{title}&quot;.</strong> It&apos;s saved with {voice.possessive} file. The next step is the advice document — the written record of this plan: what it assumes, what it costs each year, and what could go wrong. It&apos;s the document to keep.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                            {onBuildYourOwn && (
                                <button type="button" onClick={() => onBuildYourOwn(chosenRow)} className="text-[13px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                                    Customise this plan
                                </button>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* THE FOUR NUMBERS FROM STEP 2, AS A STRIP (owner decision, 17 Oct 2026: strip, not rail). The
                engine's own do-nothing row where the run produced one, else the analyser's baseline. */}
            {(() => {
                const costs = (agedCareBaseline?.agedCareCosts || {}) as { totalDailyCost?: number };
                const secondCosts = agedCareBaseline?.secondEntersLater ? null : (agedCareBaseline?.secondAgedCareCosts as { totalDailyCost?: number } | null | undefined);
                const daily = householdDailyCost(costs, secondCosts);
                if (!agedCareBaseline || agedCareBaseline.pension == null) return null;
                return (
                    <DoNothingStrip
                        careDaily={daily}
                        careAnnual={daily * 365}
                        enteringName={enteringName}
                        pension={Number(agedCareBaseline.pension) || 0}
                        cashflowAfterCare={Number(agedCareBaseline.cashFlowAfterCare) || 0}
                        legacy={agedCareBaseline.legacyValue ?? null}
                        horizonYears={currentSettings.legacyYears}
                        onWhyYears={() => setShowSettingsModal(true)}
                    />
                );
            })()}

            {/* Goal-first: what to optimise for (presentation only; does not re-run the engine) */}
            <EngineGoalControl
                value={(effectiveOptimisationSurface === 'covered' || effectiveOptimisationSurface === 'cashflow' || effectiveOptimisationSurface === 'pension') ? effectiveOptimisationSurface : 'legacy'}
                onChange={(id) => setOptimisationFocus(id)}
                household={voice.names}
                enteringName={enteringName}
                horizonYears={currentSettings.legacyYears}
                coveredSameAsBest={coveredIsBestEstate(analysis?.recommendedOptimalStrategies)}
                waysTested={waysTested}
                shown={Math.max(1, displayedGoalStrategies.length)}
            />

            {/* Strategy Cards — the plans that fit the chosen goal, side by side (UX review, 17 Oct 2026). */}
            {effectiveOptimisationSurface === 'covered' && coveredIsBestEstate(analysis?.recommendedOptimalStrategies) && (
                <SameAsBestEstateNote onShowBestEstate={() => setOptimisationFocus('legacy')} />
            )}
            {/* Owner, 23 Sep 2026: call a set-aside plan out, let them open it, never promote it. */}
            {(analysis.horizonSetAside ?? []).filter((sa) => sa.metric === effectiveOptimisationSurface).map((sa) => (
                <SetAsideReveal
                    key={`set-aside-${sa.label}`}
                    testId="engine-set-aside"
                    className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-amber-400 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5"
                    linkClassName="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900"
                    note={(link) => (
                        <>
                            <p className="font-semibold text-slate-900 leading-snug">One strategy was set aside.</p>
                            <p className="text-slate-600 leading-relaxed">
                                <span className="font-semibold text-slate-800">{getDisplayStrategyName(sa.strategy.name)}</span> would give a
                                {sa.metric === 'legacy' ? ' larger estate' : sa.metric === 'cashflow' ? ' higher cashflow' : ' higher Age Pension'}, but it needs more time than{' '}
                                <JargonTerm term="age and health" expandAs="dialog">{sa.warning}</JargonTerm> support, so it is not recommended.{' '}
                                {link}
                            </p>
                        </>
                    )}
                    renderCard={() => renderEngineCard(sa.strategy, 'set-aside')}
                />
            ))}
            {/* ANOTHER HOME (owner's design, 17 Oct 2026): a different room price changes which plan is best,
                so nothing is chosen here until it is chosen; the other home's plan stays chosen meanwhile. */}
            {chosenForOtherRoom != null && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card" data-testid="another-home-caution">
                    <strong className="text-slate-900">A different room price changes which plan is best,</strong> so the plans are re-tested from scratch for this home. Nothing here is chosen until you choose it; {voice.possessive} plan for the {formatCurrency(Math.round(chosenForOtherRoom))} room stays chosen unless you choose one of these instead. Once a plan for this home is chosen, the two homes can be compared side by side from Home.
                </div>
            )}
            {/* The partner-later card was asked for but has no year to apply to (owner-caught, 18 Oct 2026). */}
            {currentSettings.enablePartnerFutureEvent && !futureEventOptimalStrategy && effectiveOptimisationSurface === 'legacy' && (parseInt(String(currentSettings.partnerEntryYear), 10) || 3) > (currentSettings.legacyYears || legacyYears || 5) && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card" data-testid="partner-later-beyond-horizon">
                    <strong className="text-slate-900">{partnerDisplayName} entering care in {new Date().getFullYear() + (parseInt(String(currentSettings.partnerEntryYear), 10) || 3) - 1} is after the {currentSettings.legacyYears || legacyYears || 5} years these plans look ahead,</strong> so no plan here is affected by it and no &quot;{partnerDisplayName} enters later&quot; card is shown. Pick an earlier year or plan for more years in &quot;Things that change the answer&quot;.
                </div>
            )}
            {effectiveOptimisationSurface === 'pension' && displayedGoalStrategies.length === 0 && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card" data-testid="no-pension-leader">
                    <strong className="text-slate-900">No plan tested pays an Age Pension.</strong> The assets counted stay above the cut-off in every one, so there is no pension to improve. That is a finding, not a gap; choose another goal above to see the plans.
                </div>
            )}
            <div className={displayedGoalStrategies.length > 1 ? `grid grid-cols-1 gap-5 xl:grid-cols-2 ${detailOpenKey ? 'items-start' : 'items-stretch'}` : 'space-y-4'} data-testid="engine-plan-cards">
                {displayedGoalStrategies.map((strategy, index) => renderEngineCard(strategy, index))}
            </div>

            {/* Three ways on, in one row (UX review, 17 Oct 2026): every plan side by side, the things that
                change the answer, and building a plan yourself. "Analyse more" lives inside Compare. */}
            {analysis.strategies && analysis.strategies.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center justify-center gap-3" data-testid="engine-ways-on">
                    <Button type="button" onClick={() => { setEverythingTestedOpen(false); setShowGoalLeadersModal(true); }} variant="secondary">
                        Compare all {goalComparisonRows.length} plans side by side
                    </Button>
                    {tableStrategies.length > goalComparisonRows.length && (
                        <Button type="button" onClick={() => { setEverythingTestedOpen(true); setShowGoalLeadersModal(true); }} variant="secondary" data-testid="engine-all-ways-tested">
                            All {tableStrategies.length} ways tested
                        </Button>
                    )}
                    <Button type="button" onClick={handleSettingsClick} variant="secondary">
                        Things that change the answer
                    </Button>
                    {onBuildYourOwn && (
                        <button type="button" onClick={() => onBuildYourOwn(null)} className="text-[13px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                            Prefer to set the numbers yourself? Build a plan →
                        </button>
                    )}
                </div>
            )}
            {analysis.strategies && analysis.strategies.length > 0 && (
                <p className="text-center text-[13px] text-slate-500" data-testid="all-plans-open-line">{allPlansOpenLine(tableStrategies.length)}</p>
            )}
            <p className="text-center text-[13px] text-slate-500">
                Figures use the rates in force from {RATES_PROVENANCE.agePension} and are projections, not guarantees.{' '}
                <button type="button" onClick={() => setShowAssumptionsModal(true)} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">What the engine assumes</button>.
            </p>

            {/* All the plans, side by side: doing nothing first so every figure has its comparison, one row per
                distinct plan, columns in words, the chosen row marked, and the full field the engine ran behind
                "Show everything tested". Portalled to the body: inside the Card its backdrop-filter trapped the
                fixed overlay (defect 1, 17 Oct 2026). */}
            {showGoalLeadersModal && analysis.strategies && (
                <DialogShell
                    titleId="compare-plans-heading"
                    testId="compare-plans-dialog"
                    size="xl"
                    eyebrow={`The best plan for each goal, and ${bench.word}`}
                    title="All the plans, side by side"
                    intro={<>Figures are for about {currentSettings.legacyYears} {currentSettings.legacyYears === 1 ? 'year' : 'years'} in care. Tap a plan to see it year by year.</>}
                    onClose={() => setShowGoalLeadersModal(false)}
                    footer={<Button type="button" onClick={() => setShowGoalLeadersModal(false)} variant="primary">Close</Button>}
                >
                    <div>
                            {goalComparisonRows.length === 0 ? (
                                <p className="py-10 text-center text-sm text-slate-600">No goal comparison rows are available for this analysis run.</p>
                            ) : (
                                <ComparisonTable
                                    testId="compare-plans-table"
                                    columns={[
                                        { key: 'bestFor', label: 'Best for', text: true },
                                        { key: 'legacy', label: 'Left to the family', best: 'max', headline: true },
                                        { key: 'cf', label: 'Each year, short by', best: 'max' },
                                        { key: 'pension', label: 'Age Pension a year', best: 'max' },
                                        { key: 'deposit', label: 'Tied up in the deposit', bar: 'amber' },
                                    ]}
                                    rows={(() => {
                                        const out: ComparisonRow[] = [];
                                        if (agedCareBaseline && agedCareBaseline.pension != null) {
                                            const bcf = agedCareBaseline.cashFlowAfterCare ?? 0;
                                            out.push({
                                                key: 'doing-nothing', baseline: true, title: bench.word === 'paying daily' ? 'Paying daily — nothing up front, nothing sold' : 'Staying put — the current room',
                                                cells: {
                                                    bestFor: { value: null, display: '' },
                                                    legacy: { value: agedCareBaseline.legacyValue ?? null },
                                                    cf: { value: bcf, display: bcf < 0 ? formatCurrency(Math.abs(Math.round(bcf))) : `+${formatCurrency(Math.round(bcf))}`, tone: bcf < 0 ? 'bad' : 'good' },
                                                    pension: { value: Number(agedCareBaseline.pension) || 0 },
                                                    deposit: { value: 0, display: '$0' },
                                                },
                                            });
                                        }
                                        // One row per distinct plan, every goal it wins named on it.
                                        const byName = new Map<string, { strategy: Strategy; goals: string[] }>();
                                        goalComparisonRows.forEach(({ goalLabel, strategy }) => {
                                            const key = strategy.name + (strategy.isFutureEventOverlayCard ? '|future' : '');
                                            const label = goalLabel === BEST_ESTATE_CHIP ? leadChip((analysis.recommendedOptimalStrategies || []).find((r) => r.label === 'Optimal Legacy')?.lead) : goalLabel === CASHFLOW_COVERED_CHIP ? 'Income covers the costs' : goalLabel === 'Best cashflow' ? 'Most left over each year' : goalLabel === 'Best pension' ? 'Highest Age Pension' : goalLabel;
                                            const have = byName.get(key);
                                            if (have) have.goals.push(label); else byName.set(key, { strategy, goals: [label] });
                                        });
                                        const baseLegacy = agedCareBaseline?.legacyValue ?? null;
                                        Array.from(byName.values()).forEach(({ strategy, goals: g }, idx) => {
                                            const chosenRow = suggestionEngineCardHighlightKey != null && strategyCardSelectionKey(strategy) === suggestionEngineCardHighlightKey;
                                            const legacyHere = cardLegacyOf(strategy);
                                            const delta = baseLegacy != null ? legacyHere - baseLegacy : null;
                                            const cf = Number(strategy.snapshot?.netCashflow ?? 0);
                                            const w = planWords(strategy as never, factFind, getDisplayStrategyName(strategy.name, strategy.radPaidNet, strategy.radRoomPrice, secondRoomOf(strategy), moveOf(strategy), villageOf(strategy)));
                                            const paid = Math.round(Math.max(0, Number(strategy.radPaidNet) || 0));
                                            const borrowed = !!(strategy as { equityRelease?: { loanAmount?: number } }).equityRelease?.loanAmount;
                                            out.push({
                                                key: `${strategy.name}-${idx}`, chosen: chosenRow, title: w.title,
                                                onOpen: () => { setShowGoalLeadersModal(false); handleDeepDive(strategy); },
                                                cells: {
                                                    bestFor: { value: null, display: g.join(' · ') },
                                                    legacy: { value: legacyHere, sub: delta != null ? <span className={delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{delta >= 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(delta)))} {bench.vs}</span> : undefined },
                                                    cf: { value: cf, display: cf < 0 ? formatCurrency(Math.abs(Math.round(cf))) : `+${formatCurrency(Math.round(cf))}`, tone: cf < 0 ? 'neutral' : 'good' },
                                                    pension: { value: Math.round(Number(strategy.snapshot?.pension ?? 0)) },
                                                    deposit: { value: paid, display: `${formatCurrency(paid)}${borrowed ? ' (borrowed)' : ''}` },
                                                },
                                            });
                                        });
                                        return out;
                                    })()}
                                />
                            )}
                            {coveredIsBestEstate(analysis?.recommendedOptimalStrategies) && (
                                <p className="mt-3 text-[13px] text-slate-500">&quot;Leaves the most&quot; and &quot;income covers the costs&quot; point to the same plan here, so it is listed once.</p>
                            )}
                            {/* Everything the engine ran, inside this dialog rather than behind a second button (owner's question, 17 Oct 2026). */}
                            {tableStrategies.length > 0 && (
                                <details className="mt-5 border-t border-slate-200" data-testid="show-everything-tested" open={everythingTestedOpen} onToggle={(e) => setEverythingTestedOpen((e.currentTarget as HTMLDetailsElement).open)}>
                                    <summary className="cursor-pointer list-none py-3 text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy">
                                        Show everything tested — all {tableStrategies.length} ways, ranked by{' '}
                                        {allStrategiesSortBy === 'cashflow' ? 'what is left over each year' : allStrategiesSortBy === 'pension' ? 'the Age Pension' : 'what is left to the family'} ›
                                    </summary>
                                    <div className="border-t border-slate-200 px-4 py-3">
                                        <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
                                            <label htmlFor="all-plans-sort">Rank by</label>
                                            <select id="all-plans-sort" value={allStrategiesSortBy} onChange={(e) => setAllStrategiesSortBy(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[13px]">
                                                <option value="legacy">Left to the family</option>
                                                <option value="cashflow">Left over each year</option>
                                                <option value="pension">Age Pension</option>
                                            </select>
                                            <span className="text-slate-500">Tap a row to see it year by year. Under each plan, in small type, is the engine&apos;s own name for it, so an adviser can match it to the advice document.</span>
                                        </div>
                                        <table className="w-full border-collapse text-[13px]">
                                            <thead>
                                                <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                    <th className="border-b border-slate-200 py-2 pr-3">Plan</th>
                                                    <th className="border-b border-slate-200 py-2 pr-3 text-right">Left to the family</th>
                                                    <th className="border-b border-slate-200 py-2 pr-3 text-right">Left over each year</th>
                                                    <th className="border-b border-slate-200 py-2 text-right">Age Pension</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableStrategies.map((s, idx) => {
                                                    const isDownsize = s.strategyOptions?.downsizeHome || (s.name && s.name.startsWith('Downsize'));
                                                    const downsizeTooltipData = isDownsize ? getDownsizeStrategyTooltipData(factFind, s) : null;
                                                    const radTooltipData = !downsizeTooltipData ? getRadFundingTooltipData(s.radFunding as unknown as RadFundingBag) : null;
                                                    const tooltipData = downsizeTooltipData || radTooltipData;
                                                    return (
                                                        <tr key={`${s.name}-${idx}`} onClick={() => { setShowGoalLeadersModal(false); handleDeepDive(s); }} className="cursor-pointer border-b border-slate-100 hover:bg-blue-50/40">
                                                            <td className="py-2 pr-3 text-slate-800">
                                                                {/* The plan as its card says it, with the engine's name beneath for matching to the advice document
                                                                    (live review, 22 Oct 2026: "Pay 100% RAD, Keep Home (Bank -> Shares -> ABP …)" as the only words). */}
                                                                <span className="block font-medium text-slate-900">{planWords(s as never, factFind, getDisplayStrategyName(s.name, s.radPaidNet, s.radRoomPrice, secondRoomOf(s), moveOf(s), villageOf(s))).title}</span>
                                                                <span className="block text-[11.5px] text-slate-500">{tooltipData ? <CalculationTooltip title={downsizeTooltipData ? 'How this strategy is funded' : 'RAD funded from'} data={tooltipData}><span className="cursor-help">{s.name}</span></CalculationTooltip> : s.name}</span>
                                                            </td>
                                                            <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(s.snapshot?.legacyValue ?? 0)}</td>
                                                            <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(s.snapshot?.netCashflow ?? 0)}</td>
                                                            <td className="py-2 text-right tabular-nums">{formatCurrency(s.snapshot?.pension ?? 0)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </details>
                            )}
                        </div>
                </DialogShell>
            )}



            {cashflowFor && (() => {
                const row = cashflowFor.row;
                const paid = Math.max(0, Number(row.radPaidNet) || 0);
                const room = Number(row.radRoomPrice) || 0;
                const dapDaily = Number((row.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number } | undefined)?.dailyAccommodationPayment) || 0;
                const partnerEntering = (goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'partner';
                const stayingName = factFind?.adviceFor === 'couple' ? (partnerEntering ? clientDisplayName : partnerDisplayName) : null;
                return (
                    <CashflowBreakdownModal
                        planTitle={planWords(row, factFind, getDisplayStrategyName(row.name, row.radPaidNet, row.radRoomPrice, secondRoomOf(row), moveOf(row), villageOf(row))).title}
                        snapshot={(row.snapshot || {}) as never}
                        baseline={snapshotBaseline ? { pension: snapshotBaseline.pension, netCashflow: snapshotBaseline.netCashflow } : null}
                        deposit={{ paid, unpaid: Math.max(0, room - paid), dapAnnual: dapDaily * 365 }}
                        fundingLines={describeFundingKeys(factFind, (row.radFundingManualKeys || {}) as Record<string, number>)}
                        abpNames={((factFind?.assets?.accountBasedPensions || []) as { description?: string }[]).map((a) => String(a.description || '').trim()).filter(Boolean)}
                        enteringName={enteringName}
                        stayingName={stayingName}
                        people={factFind?.adviceFor === 'couple' ? [{ firstName: clientDisplayName }, { firstName: partnerDisplayName }] : [{ firstName: clientDisplayName }]}
                        initialPage={cashflowFor.page}
                        pensionWhy={tileWhy('pension', { isCouple: factFind?.adviceFor === 'couple', protectedPersonHome: hasAgedCareProtectedPerson(factFind as unknown as FactFind, {}) })}
                        pensionRows={(row.snapshot?.agePensionBreakdown as { label: string; value: string | number }[] | undefined) || [{ label: 'No breakdown available for this plan.', value: '' }]}
                        careWhy={tileWhy('care', { isCouple: factFind?.adviceFor === 'couple', protectedPersonHome: hasAgedCareProtectedPerson(factFind as unknown as FactFind, {}) })}
                        careRows={careRowsOf((row.snapshot || {}) as Record<string, unknown>)}
                        onDeepDive={() => { setCashflowFor(null); handleDeepDive(row); }}
                        onClose={() => setCashflowFor(null)}
                    />
                );
            })()}

            {riskStrategy && (
                <StrategyRisksPanel
                    voice={householdVoice(factFind)}
                    strategyName={planWords(riskStrategy, factFind, getDisplayStrategyName(riskStrategy.name, riskStrategy.radPaidNet, riskStrategy.radRoomPrice, secondRoomOf(riskStrategy), moveOf(riskStrategy), villageOf(riskStrategy))).title}
                    onClose={() => setRiskStrategy(null)}
                    risks={buildStrategyRisks(riskKindsFromAgedCareStrategy(riskStrategy as never), {
                        horizonWarning: engineStrategyHorizonWarning(riskStrategy, factFind),
                    })}
                />
            )}

            {/* Deep Dive Modal */}
            {deepDiveModal && selectedScenario && (
                <DeepDiveModal
                    selectedScenario={selectedScenario}
                    isChosen={suggestionEngineCardHighlightKey != null && strategyCardSelectionKey(selectedScenario) === suggestionEngineCardHighlightKey}
                    factFind={factFind}
                    agedCareCosts={agedCareCosts}
                    legacyYears={legacyYears}
                    analysis={analysis}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onSelectStrategy={() => { if (incomeOnlyHeldFor(selectedScenario)) return; saveSelectedStrategy(selectedScenario); setDeepDiveModal(false); }}
                    selectHeld={incomeOnlyHeldFor(selectedScenario)}
                    beforeContent={(() => {
                        const lever = engineAnnuityLeverOf(selectedScenario as never);
                        if (!lever) return null;
                        const confirm = incomeOnlySetter(setFactFind);
                        return (
                            <>
                                <EngineAnnuityFamilyPanel row={selectedScenario as never} evaluated={analysis?.allSimulatedStrategies || analysis?.strategies} factFind={factFind} personName={enteringName} horizonYears={currentSettings.legacyYears} testId="deep-dive-family" />
                                {lever.incomeOnly && confirm && <IncomeOnlyBeforeChoose factFind={factFind} amount={lever.amount} onChange={confirm} testId="deep-dive-income-only" />}
                            </>
                        );
                    })()}
                    onClose={() => setDeepDiveModal(false)}
                    applyRadFundingToFactFind={applyRadFundingToFactFind}
                    careStartDate={careStartDateFromStrategyOptions(strategyOptions)}
                    radDrawdown={radDrawdownFromStrategyOptions(strategyOptions)}
                    residentialFeeArrangements={feeArrangementsFromStrategyOptions(strategyOptions)}
                />
            )}



            {/* "Things that change the answer" — the same questions the first visit asked, reopened. Changing
                one re-tests every plan; a chosen plan stays chosen when its row still exists (no "save first?"
                interstitial — UX review, 17 Oct 2026). */}
            {showSettingsModal && (
                <DialogShell
                    titleId="engine-settings-heading"
                    testId="engine-settings-dialog"
                    size="lg"
                    eyebrow="Your answers"
                    title="Things that change the answer"
                    intro="Change any of these and we re-test every way to pay. Your chosen plan stays chosen if it still exists."
                    onClose={() => setShowSettingsModal(false)}
                >
                    <div>
                            <SettingsForm
                                currentSettings={currentSettings}
                                onSave={handleSettingsSave}
                                onCancel={() => setShowSettingsModal(false)}
                                factFind={factFind}
                                scenarioLivingExpenses={scenarioLivingExpensesOr(agedCareCosts?.scenarioLivingExpenses, factFind?.expenses?.livingExpenses)}
                                roomDeposit={Number(agedCareCosts?.radAmount) || 0}
                            />
                    </div>
                </DialogShell>
            )}
        </div>
    );
};

/**
 * The nominated care start date off the shared strategy options, or `undefined` if none was set.
 *
 * Read defensively because `strategyOptions` is a loose bag on this panel's props — a date that failed
 * to be a `YYYY-MM-DD` string is treated as no date at all, which is the same "care is already under
 * way" the engine assumes for an absent one.
 */
/** The second room the Analyser set, where both of a couple are entering care — priced in every candidate. */
function secondRoomFromStrategyOptions(strategyOptions?: Record<string, unknown>): SecondRoomForEngine | null {
    const v = strategyOptions?.secondRoom as SecondRoomForEngine | undefined;
    if (!v || !(Number(v.roomPrice) > 0) || (v.person !== 'client' && v.person !== 'partner')) return null;
    return { person: v.person, roomPrice: Number(v.roomPrice), ...(v.careStartDate ? { careStartDate: v.careStartDate } : {}), ...(v.extraServiceAnnual ? { extraServiceAnnual: Number(v.extraServiceAnnual) } : {}), entersLater: !!v.entersLater };
}
type SecondRoomForEngine = { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number; entersLater?: boolean };
/** A change of home, as the Analyser set it (see the engine's FacilityMoveInput). */
type FacilityMoveForEngine = { person: 'client' | 'partner'; moveDate: string; newRoomPrice: number; extraServiceAnnual?: number; refundToday: number; currentRoomPrice: number; currentDapAnnual: number; grandfathered: boolean; currentSchedule?: Record<string, unknown> };
function facilityMoveFromStrategyOptions(strategyOptions?: Record<string, unknown>): FacilityMoveForEngine | null {
    const v = strategyOptions?.facilityMove as FacilityMoveForEngine | undefined;
    if (!v || !(Number(v.newRoomPrice) > 0) || !v.moveDate) return null;
    return v;
}
/** Leaving a retirement village, as the Analyser set it (see the engine's VillageExitInput). */
type VillageExitForEngine = { person: 'client' | 'partner'; leaveDate: string; refundDate: string; entitlement: number };
function villageExitFromStrategyOptions(strategyOptions?: Record<string, unknown>): VillageExitForEngine | null {
    const v = strategyOptions?.villageExit as VillageExitForEngine | undefined;
    if (!v || !(Number(v.entitlement) > 0) || !v.leaveDate || !v.refundDate) return null;
    return v;
}

function careStartDateFromStrategyOptions(strategyOptions?: Record<string, unknown>): string | undefined {
    const value = strategyOptions?.careStartDate;
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

/**
 * Whether the provider lets fees be drawn from the deposit, as answered on the Analyser's costs form.
 * Read the same defensive way as the entry date; anything unrecognised is "not answered", which the
 * engine treats as the usual written agreement ('fees').
 */
/** The fee arrangements the Analyser chose for a no-worse-off resident, if any — read the same way as the deposit arrangement. */
function feeArrangementsFromStrategyOptions(strategyOptions?: Record<string, unknown>): '2014' | '2025' | undefined {
    const value = strategyOptions?.residentialFeeArrangements;
    return value === '2014' || value === '2025' ? value : undefined;
}

function radDrawdownFromStrategyOptions(strategyOptions?: Record<string, unknown>): 'fees' | 'dap' | 'none' | undefined {
    const value = strategyOptions?.radDrawdown;
    return value === 'fees' || value === 'dap' || value === 'none' ? value : undefined;
}

// INTEGRATED METHOD: Generate scenarios and call manual control directly
/**
 * ONE PLAN IN FULL (26 Oct 2026). The analysis comes back light — every figure, but not the year's HTML
 * working or the full-year blend, which together were three quarters of each of several hundred rows and
 * took a tab past a gigabyte. Before a plan's year-by-year window, its advice document, or choosing it
 * (whose saved record the document is written from), the same scenario is evaluated again on the same
 * inputs — each scenario is evaluated on its own, so its row is identical — and the two fields are put
 * back on the row itself, so it is fetched once.
 */
type AcRunContext = {
    args: [DraftFactFind, AgedCareCosts, number, string | undefined, unknown, unknown, unknown, unknown, unknown];
    scenarios: Map<string, unknown>;
};
/** Each light run's inputs, by the id its rows carry (`liteRun`); as many as the analysis cache keeps. */
const acRuns = new Map<string, AcRunContext>();
let acRunSeq = 0;
function rememberAcRun(context: AcRunContext): string {
    const id = `ac-run-${Date.now().toString(36)}-${(acRunSeq += 1)}`;
    acRuns.set(id, context);
    while (acRuns.size > 8) acRuns.delete(acRuns.keys().next().value as string);
    return id;
}

export async function fullStrategyRow<T>(row: T): Promise<T> {
    const r = row as unknown as Record<string, unknown> | null;
    if (!r || r.lite !== true) return row;
    const run = acRuns.get(String(r.liteRun ?? ''));
    if (!run) return row;
    const name = String(r.name ?? '');
    const scenario = run.scenarios.get(name) ?? {
        name,
        radFunding: r.radFunding ?? {},
        strategyOptions: r.strategyOptions ?? {},
        ...(r.groupKey ? { groupKey: r.groupKey } : {}),
        ...(r.fundingOrderLabel ? { fundingOrderLabel: r.fundingOrderLabel } : {}),
        ...(r.meansTestStrategy ? { meansTestStrategy: r.meansTestStrategy } : {}),
    };
    const [factFind, agedCareCosts, legacyYears, careStartDate, radDrawdown, feeArrangements, secondRoom, facilityMove, villageExit] = run.args;
    const rows = await calculationApi.evaluateAgedCareRadFundingScenarios(
        factFind, agedCareCosts, [scenario] as never, legacyYears, careStartDate,
        radDrawdown as never, feeArrangements as never, undefined, secondRoom as never, facilityMove as never, villageExit as never,
    );
    const full = Array.isArray(rows) ? (rows[0] as Record<string, unknown> | undefined) : undefined;
    if (!full) return row;
    Object.assign(r, {
        projections: full.projections,
        atDefaultRatesProjections: full.atDefaultRatesProjections ?? null,
        optInProjections: full.optInProjections ?? null,
        lite: false,
    });
    return row;
}

async function generateScenariosAndCallManualControl(
    factFind: DraftFactFind,
    agedCareCosts: AgedCareCosts,
    goals: SuggestionEngineGoals,
    legacyYears: number,
    coupleValidation: CoupleValidation,
    setProgressCallback: ((progress: { current: number; total: number }) => void) | null = null,
    /**
     * When the client moves into care (`YYYY-MM-DD`), or absent for care already under way — the same
     * date the Aged Care Analyser's "when does care start?" control sets.
     *
     * Applied to every scenario alike, because it describes the case rather than any one funding
     * strategy: candidates that assumed different entry dates would be ranked against each other on
     * different sets of years.
     */
    careStartDate?: string,
    /** A provider's quoted annual annuity income, for one type and amount (scaled to other amounts of that type). */
    annuityIncomeOverride: ScopedAnnuityQuote | null = null,
    /** Whether fees may be drawn from the deposit — a fact about the case, applied to every candidate. */
    radDrawdown?: 'fees' | 'dap' | 'none',
    /** `'2025'` = a no-worse-off resident opting in to the 1 November 2025 fee arrangements; likewise a fact about the case. */
    residentialFeeArrangements?: '2014' | '2025',
    /** The second room, where both of a couple are entering care; every candidate prices it. */
    secondRoom: SecondRoomForEngine | null = null,
    /** A change of home: every candidate but "stay put" moves on its date. */
    facilityMove: FacilityMoveForEngine | null = null,
    /** Leaving a retirement village: the exit payment pays the room first when it arrives. */
    villageExit: VillageExitForEngine | null = null,
): Promise<AnalysisResult> {
    // The same inputs give the same answer: a run this session already did is handed straight back,
    // which is what makes re-opening a saved plan immediate (engineAnalysisCache.ts).
    const cacheKey = analysisCacheKey([factFind, agedCareCosts, goals, legacyYears, coupleValidation, careStartDate, annuityIncomeOverride, radDrawdown, residentialFeeArrangements, secondRoom, facilityMove, villageExit]);
    const remembered = cachedAnalysis<AnalysisResult>(cacheKey);
    if (remembered) {
        if (setProgressCallback) setProgressCallback({ current: remembered.strategies.length, total: remembered.strategies.length });
        return remembered;
    }

    // Generate scenarios from the original factFind (evaluateAgedCareRadFundingScenarios below runs
    // server-side over the network, so factFind is only ever read here, never mutated in place).
    const scenarios = generateScenarios(factFind, agedCareCosts, coupleValidation, annuityIncomeOverride, secondRoom, facilityMove, villageExit);

    // Update total scenarios for progress tracking
    if (setProgressCallback) {
        setProgressCallback({ current: 0, total: scenarios.length });
    }

    const futureEventConfig = {
        // The "partner later" overlay is the old approximation of a second room; with the second
        // room modelled in the engine itself (7 Oct 2026) it is switched off.
        enabled: !!goals?.enablePartnerFutureEvent && !secondRoom,
        partnerEntryYear: parseInt(String(goals?.partnerEntryYear), 10) || 3,
        partnerRoomPrice: parseFloat(String(goals?.partnerFutureRoomPrice)) || 0,
        originalEntrantAliveAtEvent: !!goals?.originalEntrantAliveAtPartnerEntry,
    };

    // One request evaluates every scenario against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/agedCare.ts) instead of one request per scenario
    // (previously up to ~150-300 scenarios).
    // Light rows (26 Oct 2026): every figure but the year's HTML working and full-year blend, which
    // `fullStrategyRow` fetches for the one plan that is opened, chosen or written up.
    const liteRows: Strategy[] = await calculationApi.evaluateAgedCareRadFundingScenarios(factFind, agedCareCosts, scenarios, legacyYears, facilityMove ? undefined : careStartDate, radDrawdown, residentialFeeArrangements, undefined, secondRoom, facilityMove, villageExit, true);
    const liteRun = rememberAcRun({
        args: [factFind, agedCareCosts, legacyYears, facilityMove ? undefined : careStartDate, radDrawdown, residentialFeeArrangements, secondRoom, facilityMove, villageExit],
        scenarios: new Map(scenarios.map((s) => [String((s as { name?: unknown }).name ?? ''), s])),
    });
    const evaluatedStrategies: Strategy[] = (Array.isArray(liteRows) ? liteRows : []).map((row) => ((row as { lite?: unknown }).lite ? { ...row, liteRun } : row));
    if (setProgressCallback) {
        setProgressCallback({ current: scenarios.length, total: scenarios.length });
    }

    // The future-life-event overlay is a pure heuristic on top of the already-evaluated
    // strategy.snapshot/.name/.radFunding (no re-projection needed), so it stays a client-side
    // post-processing step, unchanged from the original per-scenario loop.
    const strategies: Strategy[] = evaluatedStrategies.map((strategyRow) => {
        if (futureEventConfig.enabled && futureEventConfig.partnerRoomPrice > 0) {
            const futureEventResult = computeFutureEventAdjustedLegacy({
                strategy: strategyRow as unknown as EvaluatedStrategy,
                legacyYears,
                partnerEntryYear: futureEventConfig.partnerEntryYear,
                partnerRoomPrice: futureEventConfig.partnerRoomPrice,
                originalEntrantAliveAtEvent: futureEventConfig.originalEntrantAliveAtEvent,
                baseAgedCareCosts: (strategyRow.strategySpecificAgedCareCosts as AgedCareCosts) || agedCareCosts,
            });
            if (futureEventResult) {
                return {
                    ...strategyRow,
                    futureAdjustedLegacy: futureEventResult.adjustedLegacy,
                    futureEventAdjustmentDelta: futureEventResult.adjustmentDelta,
                    futureEventAssumptions: futureEventResult.assumptions,
                };
            }
        }
        return strategyRow;
    });

    // Primary ranking and "Analyse more" legacy sort use baseline projected estate.
    // Future-event-adjusted legacy is computed per strategy for the optional fourth card only.
    const getBaselineLegacyRankingValue = (strategy: Strategy | null | undefined) => strategy?.snapshot?.legacyValue || 0;

    // Keep full list for "Analyse more" (all simulated strategies), sorted by baseline legacy
    const allSimulatedStrategies = [...strategies].sort((a, b) => getBaselineLegacyRankingValue(b) - getBaselineLegacyRankingValue(a));

    // Phase 2: Keep only the best funding order per scenario group (by legacy value) for top cards & recommendations
    const byGroup: Record<string, Strategy[]> = {};
    const ungrouped: Strategy[] = [];
    strategies.forEach(s => {
        if (s.groupKey) {
            const key = String(s.groupKey);
            if (!byGroup[key]) byGroup[key] = [];
            byGroup[key].push(s);
        } else {
            ungrouped.push(s);
        }
    });
    const bestPerGroup = Object.values(byGroup).map(group =>
        group.reduce((best, s) => getBaselineLegacyRankingValue(s) > getBaselineLegacyRankingValue(best) ? s : best)
    );
    /*
     * Strip the FUNDING ORDER and nothing else. This was "any trailing parenthesised group", which
     * also removed the annuity's residual-capital variant and the debt-repayment basis — leaving two
     * genuinely different strategies under one name, one showing a $200,000 larger estate than the
     * other (seen in the compare-all table, 2026-08-27). It mutates `s.name`, so the collision
     * followed the strategy everywhere, including into the SOA.
     */
    bestPerGroup.forEach(s => {
        s.name = stripFundingOrderSuffix(s.name || '');
    });
    strategies.length = 0;
    strategies.push(...ungrouped, ...bestPerGroup);

    // Rank strategies by baseline legacy (today's projection); future-aware winner is the optional fourth card.
    strategies.sort((a, b) => getBaselineLegacyRankingValue(b) - getBaselineLegacyRankingValue(a));

    // === MARK OPTIMAL STRATEGY (no duplicate entry) ===
    // Label the top strategy so the UI can show "Age Pension Optimised" / "Cashflow Maximised" as a badge
    // while keeping the strategy approach (e.g. "Pay 40% RAD, Keep Home") as the main title.
    try {
        if (strategies.length > 0) {
            let bestPensionStrategy: Strategy | null = null;
            let bestCashflowStrategy: Strategy | null = null;

            strategies.forEach(s => {
                if (!bestPensionStrategy || (s.snapshot?.pension || 0) > (bestPensionStrategy.snapshot?.pension || 0)) {
                    bestPensionStrategy = s;
                }
                if (!bestCashflowStrategy || (s.snapshot?.netCashflow || -Infinity) > (bestCashflowStrategy.snapshot?.netCashflow || -Infinity)) {
                    bestCashflowStrategy = s;
                }
            });

            const hasPensionEligibility = ((bestPensionStrategy as Strategy | null)?.snapshot?.pension || 0) > 0;
            const base: Strategy | null = hasPensionEligibility ? bestPensionStrategy : bestCashflowStrategy;
            const optimisationLabel = hasPensionEligibility ? 'Age Pension Optimised' : 'Cashflow Maximised';

            if (base) {
                (base as Strategy).optimisationLabel = optimisationLabel;
            }
        }
    } catch (e) {
        console.warn('Optimisation label assignment skipped due to error:', e);
    }

    // Deduplicate by name (keep first = highest legacy) so we never show duplicate strategy tiles
    const seenNames = new Set<string>();
    const deduped = strategies.filter(s => {
        if (seenNames.has(s.name || '')) return false;
        seenNames.add(s.name || '');
        return true;
    });
    strategies.length = 0;
    strategies.push(...deduped);


    // === RECOMMENDED OPTIMAL STRATEGIES (top 3: legacy, cashflow, age pension; no duplicates; pension fallback) ===
    const recommendedOptimalStrategies: RecommendedOptimalStrategyRow[] = [];
    const horizonSetAside: HorizonSetAside<Strategy>[] = [];
    if (strategies.length > 0) {
        // Use the full simulated set for cashflow/pension optimisation so recommendations
        // align with the "All modelled strategies" table.
        const fullUniverse = (allSimulatedStrategies && allSimulatedStrategies.length > 0)
            ? allSimulatedStrategies
            : strategies;
        /*
         * A STRATEGY THAT NEEDS MORE TIME THAN THE CLIENT HAS IS NEVER CROWNED (owner, 23 Sep 2026:
         * "I definitely don't think we should promote it as the best result"). It stays in the field,
         * costed and openable, with its warning on the card; it just cannot be a leader. Where one
         * would have won, that is recorded below and disclosed, so nothing is hidden either.
         *
         * Only the LEADERS are judged here. The full field is untouched, so no figure moves.
         */
        const flagged = (s: Strategy) => engineStrategySetAside(s, factFind);
        /*
         * STAYING PUT IS THE COMPARISON, NEVER A LEADER (25 Oct 2026). When the client is moving to another home,
         * "Stay put" is what every plan is measured against — as staying put is in the granny flat, village and
         * downsizing modules — so it can never be crowned the best way to make the move they have decided on.
         */
        const movers = strategies.filter((s) => !isStayPutRow(s));
        const moversUniverse = fullUniverse.filter((s) => !isStayPutRow(s));
        const eligibleStrategies = movers.filter((s) => !flagged(s));
        const recommendationUniverse = moversUniverse.filter((s) => !flagged(s));
        // A near-tie on the estate is a tie, led by the plan that asks less (planChoiceRule.ts, owner 20 Oct 2026).
        const acReadings = agedCareReadings as unknown as PlanReadings<Strategy>;
        const bestLegacy = leadWithinTie(eligibleStrategies.length ? eligibleStrategies : (movers.length ? movers : strategies), acReadings) ?? (movers[0] ?? strategies[0])!;
        const bestCashflow = (recommendationUniverse.length ? recommendationUniverse : (moversUniverse.length ? moversUniverse : fullUniverse)).reduce((best, s) =>
            (s.snapshot?.netCashflow ?? -Infinity) > (best?.snapshot?.netCashflow ?? -Infinity) ? s : best
        );
        const bestPension = (recommendationUniverse.length ? recommendationUniverse : (moversUniverse.length ? moversUniverse : fullUniverse)).reduce((best, s) =>
            (s.snapshot?.pension ?? 0) > (best?.snapshot?.pension ?? 0) ? s : best
        );
        // The set-asides: a flagged strategy that beats the chosen leader on that ranking's metric.
        const metricOf = { legacy: (s: Strategy) => Number(s.snapshot?.legacyValue ?? -Infinity), cashflow: (s: Strategy) => Number(s.snapshot?.netCashflow ?? -Infinity), pension: (s: Strategy) => Number(s.snapshot?.pension ?? 0) };
        const setAsideFor = (label: string, metric: keyof typeof metricOf, leader: Strategy | null | undefined) => {
            const bestFlagged = moversUniverse.filter(flagged).reduce<Strategy | null>((b, s) => (b == null || metricOf[metric](s) > metricOf[metric](b) ? s : b), null);
            if (!bestFlagged || !leader || metricOf[metric](bestFlagged) <= metricOf[metric](leader)) return;
            const warning = engineStrategyHorizonWarning(bestFlagged, factFind);
            if (warning) horizonSetAside.push({ label, strategy: bestFlagged, warning, metric });
        };
        setAsideFor('Optimal Legacy', 'legacy', bestLegacy);
        setAsideFor('Optimal Cashflow', 'cashflow', bestCashflow);
        setAsideFor('Optimal Age Pension', 'pension', bestPension);
        const hasPensionEligibility = (bestPension?.snapshot?.pension ?? 0) > 0;
        const used = new Set<string | undefined>();
        const add = (strategy: Strategy | null | undefined, label: string) => {
            if (!strategy || used.has(strategy.name)) return false;
            used.add(strategy.name);
            recommendedOptimalStrategies.push({ label, strategy });
            return true;
        };
        if (add(bestLegacy, 'Optimal Legacy')) {
            const ex = leadExceptionOf(eligibleStrategies.length ? eligibleStrategies : movers, bestLegacy, acReadings);
            if (ex) recommendedOptimalStrategies[recommendedOptimalStrategies.length - 1]!.lead = ex;
        }
        if (!add(bestCashflow, 'Optimal Cashflow')) {
            const nextCashflow = recommendationUniverse
                .filter(s => !used.has(s.name))
                .reduce<Strategy | null>((best, s) => (s.snapshot?.netCashflow ?? -Infinity) > (best?.snapshot?.netCashflow ?? -Infinity) ? s : best, null);

            // If the best cashflow strategy is already used (same as legacy), keep it as
            // "Optimal Cashflow" unless an alternative is at least as good.
            if (nextCashflow && (nextCashflow.snapshot?.netCashflow ?? -Infinity) >= (bestCashflow?.snapshot?.netCashflow ?? -Infinity)) {
                add(nextCashflow, 'Optimal Cashflow');
            } else if (bestCashflow) {
                recommendedOptimalStrategies.push({ label: 'Optimal Cashflow', strategy: bestCashflow });
            }
        }
        if (hasPensionEligibility) {
            if (!add(bestPension, 'Optimal Age Pension')) {
                const nextPension = recommendationUniverse
                    .filter(s => !used.has(s.name))
                    .reduce<Strategy | null>((best, s) => (s.snapshot?.pension ?? 0) > (best?.snapshot?.pension ?? 0) ? s : best, null);
                if (nextPension) add(nextPension, 'Optimal Age Pension');
            }
        }
        // No plan pays an Age Pension: the goal has no leader and says so, rather than badging the
        // next-best estate as the pension's best (owner, 20 Oct 2026).
        /*
         * THE FOURTH GOAL, FIRST (owner, 24 Sep 2026): the best estate among the plans that pay the
         * bills; failing that, whose shortfall the savings carry for the whole period; failing that,
         * the one that lasts longest, badged as such. In care a daily payment on the room nearly
         * always leaves a shortfall, so the second case is the common one here — and it is the
         * question the family is actually asking. A flagged strategy cannot be crowned here either.
         */
        const covered = pickCashflowCoveredLeader(recommendationUniverse.length ? recommendationUniverse : moversUniverse, { cashflowTarget: 0, readings: acReadings });
        if (covered) recommendedOptimalStrategies.unshift({ label: CASHFLOW_COVERED_LABEL, strategy: covered.strategy, cashflowCovered: covered.verdict });
    }

    // Check for 70% RAD strategies specifically
    const seventyPercentStrategies = strategies.filter(s => (s.name || '').includes('70% RAD'));
    if (seventyPercentStrategies.length > 0) {
    } else {
        console.warn('Ã¢Å¡Â Ã¯Â¸Â NO 70% RAD STRATEGIES FOUND IN RESULTS! Checking all strategy names...');
    }
    
    const result = {
        strategies: strategies,
        allSimulatedStrategies: allSimulatedStrategies,
        totalScenarios: scenarios.length,
        processedScenarios: strategies.length,
        recommendedOptimalStrategies: recommendedOptimalStrategies,
        horizonSetAside,
        futureEventV2: futureEventConfig.enabled
            ? {
                enabled: true,
                rankingMetric: 'baseline_legacy',
                fourthCardMetric: 'future_adjusted_legacy',
                assumptions: {
                    partnerEntryYear: futureEventConfig.partnerEntryYear,
                    partnerRoomPrice: futureEventConfig.partnerRoomPrice,
                    originalEntrantAliveAtEvent: futureEventConfig.originalEntrantAliveAtEvent,
                },
            }
            : {
                enabled: false,
                rankingMetric: 'legacy',
                assumptions: null,
            },
    };
    
    return rememberAnalysis(cacheKey, result);
}

// Generate scenarios directly in UI
/**
 * The RAD levels a debt-payoff twin is generated for ON THE LEVER VARIANTS (an annuity, funeral bonds,
 * a gift, a property sale). The PLAIN candidates are paired at every level — see `appendDebtPayoffScenarios`.
 *
 * THE CAP THAT HID THE ANSWER (owner-caught, 18 Oct 2026: "a credit card debt of $15K at 15% — how come
 * the system did not suggest this get paid off first?"). Until then every twin was confined to these five
 * levels, on the reasoning that the payoff decision barely interacts with the RAD percentage. The
 * decision does not; the RANKING does. On that client the twin beat its base at every paired level (50%:
 * +$3,804 estate, +$6,052 a year cashflow) — and lost the ranking to the unpaired 70% base, which won on
 * the deposit level alone, so no card ever said "clear the debt". The modelling was right and the
 * candidate set could not show it. The plain candidates now carry a twin at every level; the lever
 * variants keep this spread, because pairing every lever at every level would double the set again.
 */
const DEBT_PAYOFF_PAIRED_RAD_LEVELS = [0, 25, 50, 75, 100];

/**
 * Debts worth generating a payoff candidate for (strategy/debtsToTest.ts, 26 Oct 2026): never a card paid off
 * in full each month; any debt that costs interest, whatever its size; interest-free debts from $5,000.
 */
function materialDebtsForSuggestionEngine(factFind: DraftFactFind) {
    return debtsWorthTesting(payableDebtsOf(factFind) as PayableDebt[]);
}

/**
 * Pairs a spread of the generated scenarios with a twin that also clears the client's debts.
 *
 * The engine is given both worlds and its existing ranking picks — there is no debt-specific rule, and
 * deliberately so. "Should this be paid off?" is answered by the same legacy-and-cashflow comparison
 * that answers every other question here, which means there is no second rulebook to keep in step with
 * the first. It also means the answer can differ per client, which it genuinely does: an unsecured debt
 * counts against the assets test in full, a mortgage over an investment property is already netted off
 * that property, and one over an exempt home does nothing for the means test at all.
 *
 * Funded from the client's own cash first and only then from other liquid assets, so a payoff candidate
 * does not gratuitously trigger CGT that would make it lose for the wrong reason.
 */
function appendDebtPayoffScenarios(scenarios: RadFundingScenario[], factFind: DraftFactFind): void {
    const debts = materialDebtsForSuggestionEngine(factFind);
    if (debts.length === 0) return;

    const payments = Object.fromEntries(debts.map((debt) => [debt.key, debt.balance]));
    const totalToClear = debts.reduce((sum, debt) => sum + debt.balance, 0);

    const liquid = getPersonalLiquidAssetsForRad(factFind);
    // Cash first — it carries no CGT and no disposal decision.
    const ordered = [...liquid].sort((a, b) => Number(b.typeId === 'bankAccounts') - Number(a.typeId === 'bankAccounts'));

    const paired = scenarios.filter((scenario) => {
        const name = String(scenario.name || '');
        const match = /Pay (\d+)% RAD/.exec(name);
        if (!match) return name === 'Do Nothing';
        // A plain candidate (no " + lever" suffix) at EVERY level, so the twin always sits beside the level
        // that wins; a lever variant only at the spread (see DEBT_PAYOFF_PAIRED_RAD_LEVELS).
        const isLeverVariant = / \+ /.test(name);
        return !isLeverVariant || DEBT_PAYOFF_PAIRED_RAD_LEVELS.includes(Number(match[1]));
    });

    for (const base of paired) {
        /**
         * What THIS candidate's deposit has already claimed of each asset.
         *
         * The bug this fixes, and it invalidated every paired RAD candidate. The payoff's funding used
         * to be computed once from raw balances and attached to all of them, so a "Pay 60% RAD + Clear
         * Debt" candidate allocated the same bank account twice. The engine clears debt at order 3 and
         * sells for the deposit at order 4, so the debt claimed first and the DEPOSIT WAS STARVED —
         * that candidate quietly paid a smaller RAD than its own name promised, and scored accordingly.
         *
         * Only the no-RAD pairing was unaffected, because it allocates nothing. Which is exactly why
         * "Do Nothing + Clear Debt" kept winning: it was the one paired candidate not being sabotaged.
         *
         * The pool is shared, so the payoff now spends only what the deposit left — the same rule the
         * Manual Control cards enforce with their sliders.
         */
        const claimedByRad = flattenSeRadFundingToManualKeys(base.radFunding ?? {});

        const funding: Record<string, number> = {};
        let stillToRaise = totalToClear;
        for (const asset of ordered) {
            if (stillToRaise <= 0) break;
            const room = Math.max(0, (Number(asset.value) || 0) - (Number(claimedByRad[asset.assetId]) || 0));
            const take = Math.min(stillToRaise, room);
            if (take <= 0) continue;
            funding[asset.assetId] = take;
            stillToRaise -= take;
        }
        // This candidate's deposit has taken everything: there is nothing left to clear a debt with, so
        // there is no choice to model. Skipped rather than generated as a payoff that cannot happen.
        const affordable = Object.values(funding).reduce((sum, amount) => sum + amount, 0);
        if (affordable <= 0) continue;

        /**
         * The payoff variants worth modelling for this candidate.
         *
         * Where the whole debt can be funded from what is left, one variant: clear it. The repayment
         * basis is moot, because a debt cleared outright has no repayment left to decide about.
         *
         * Where it cannot, the payoff is necessarily PARTIAL — and then the basis is a real fork with
         * no universal winner, so both are modelled and the ranking picks.
         */
        const canClearInFull = affordable >= totalToClear - 0.5;
        const partial: Record<string, number> = {};
        if (!canClearInFull) {
            let left = affordable;
            for (const debt of debts) {
                if (left <= 0) break;
                const take = Math.min(left, debt.balance);
                partial[debt.key] = take;
                left -= take;
            }
        }
        const variants: { suffix: string; payoff: Record<string, unknown> }[] = canClearInFull
            ? [{ suffix: '+ Clear Debt', payoff: { payments, funding } }]
            : [
                { suffix: '+ Pay Down Debt (keep repayments)', payoff: { payments: partial, funding, repaymentBasis: 'keep_repayment' } },
                { suffix: '+ Pay Down Debt (lower repayments)', payoff: { payments: partial, funding, repaymentBasis: 'lower_repayment' } },
            ];

        for (const variant of variants) {
            scenarios.push({
                ...base,
                name: `${base.name} ${variant.suffix}`,
                strategyOptions: {
                    ...base.strategyOptions,
                    debtPayoff: variant.payoff,
                },
            });
        }
    }
}

/**
 * "$50,000 from AMP BANK" — the assets this strategy sells to clear debt, or null where it clears none.
 *
 * The "Allocation to RAD" grid beside this is exactly what its heading says: money going to the
 * accommodation deposit. A debt payoff is not that, so it does not appear there — which left an adviser
 * looking at "AMP BANK — Kept: $150,000" on a card whose own badge said it cleared a $50,000 debt, with
 * nothing on screen reconciling the two. The projection was right the whole time; the card just never
 * mentioned the second withdrawal.
 */
function debtPayoffFundingLine(strategy: Strategy, factFind: DraftFactFind): string | null {
    const funding = (strategy.strategyOptions as { debtPayoff?: { funding?: Record<string, number> } } | undefined)?.debtPayoff?.funding;
    if (!funding) return null;
    const parts: string[] = [];
    for (const [key, amount] of Object.entries(funding)) {
        const value = Number(amount) || 0;
        if (value <= 0) continue;
        const parsed = parseRadFundingAssetKey(key);
        const asset = parsed ? (factFind.assets?.[parsed.assetType] || []).find((row) => String(row?.id ?? '') === String(parsed.assetId)) : null;
        parts.push(`${formatCurrency(Math.round(value))} from ${asset?.description || parsed?.assetType || 'your assets'}`);
    }
    if (parts.length === 0) return null;
    return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * "the Personal Loan ($50,000)" — what this strategy pays out, or null where it pays out nothing.
 *
 * Read off the strategy's own options rather than a flag set at generation time, so a candidate that
 * reached the card by any route still describes itself correctly.
 */
/**
 * Whether any debt this plan clears was costing interest. A card paid off in full each month costs
 * none (creditCards.ts), so clearing it must not claim "the interest on it stops".
 */
function clearedDebtsCarryInterest(strategy: Strategy, factFind: DraftFactFind): boolean {
    const payments = (strategy.strategyOptions as { debtPayoff?: { payments?: Record<string, number> } } | undefined)?.debtPayoff?.payments || {};
    return Object.keys(payments).some((key) => {
        const parsed = parseDebtPayoffKey(key);
        if (!parsed) return true;
        const liability = (factFind.liabilities?.[parsed.category] || []).find((l) => String(l?.id) === parsed.liabilityId);
        return !liability || liabilityInterestRate(liability as never, parsed.category) > 0;
    });
}

function debtsClearedBy(strategy: Strategy): string | null {
    const payments = (strategy.strategyOptions as { debtPayoff?: { payments?: Record<string, number> } } | undefined)?.debtPayoff?.payments;
    if (!payments) return null;
    const total = Object.values(payments).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
    if (total <= 0) return null;
    const count = Object.keys(payments).length;
    const basis = (strategy.strategyOptions as { debtPayoff?: { repaymentBasis?: string } } | undefined)?.debtPayoff?.repaymentBasis;
    // Named only where the strategy carries it AND the name says this is a pay-DOWN, since the basis is
    // meaningless once a debt is cleared outright.
    const note = /Pay Down Debt/.test(String(strategy.name || '')) && basis
        ? basis === 'lower_repayment' ? ', lowering the repayment' : ', keeping the repayment'
        : '';
    return `${count === 1 ? 'debt' : `${count} debts`} totalling ${formatCurrency(Math.round(total))}${note}`;
}

function generateScenarios(
    factFind: DraftFactFind,
    agedCareCosts: AgedCareCosts,
    cleanCoupleValidation: CoupleValidation = { partnerAnnualRent: 40000 },
    /** A provider's quoted annual annuity income, where the client has entered one (for one type and amount). */
    annuityIncomeOverride: ScopedAnnuityQuote | null = null,
    /** The second room, where both of a couple enter care: each candidate pays the same share of it up front. */
    secondRoom: SecondRoomForEngine | null = null,
    /** A change of home: "stay put" is the comparison; the refund of the current deposit funds the new room first. */
    facilityMove: FacilityMoveForEngine | null = null,
    /** Leaving a village: the exit payment funds the room first when it arrives; only the rest is sold at entry. */
    villageExit: VillageExitForEngine | null = null,
): RadFundingScenario[] {
    const scenarios: RadFundingScenario[] = [];
    const rentAllowed = cleanCoupleValidation.testRentingHome !== false;
    const secondTargetFor = (percentage: number) => (secondRoom ? { secondRadTarget: Math.round(secondRoom.roomPrice * (percentage / 100)) } : {});
    const radAmount = Number((agedCareCosts && agedCareCosts.radAmount) ? agedCareCosts.radAmount : 500000); // Use actual room cost from agedCareCosts


    // Baseline scenario (Do Nothing) - Full DAP + Daily Fees
    // DAP = Room Cost * MPIR + Daily Fees (MPIR updated quarterly)
    const MPIR = AGED_CARE_SCHEDULE.MPIR;
    const doNothingDap = radAmount * MPIR + Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365 + Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365;
    scenarios.push({
        name: facilityMove ? 'Stay put' : "Do Nothing",
        ...(facilityMove ? { stayPut: true } : {}),
        radFunding: {},
        ...secondTargetFor(0),
        strategyOptions: {
            annualDap: facilityMove ? facilityMove.currentDapAnnual : doNothingDap,
            rentHome: false,
            downsizeHome: false,
            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
        }
    });
    
    // Comprehensive RAD testing: 10% to 100% in 5% increments
    // This ensures we find the actuarially optimal RAD level
    const radPercentages = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];

    /**
     * Funding order + the total the variant can ACTUALLY raise, so a level whose request exceeds the
     * client's fundable assets is recognised as a duplicate of the level where the cap first bit.
     *
     * Without this, a client with $455,000 fundable against a $700,000 room generated "Pay 65%" through
     * "Pay 100%" as EIGHT candidates that all fund $455,000 — identical projections wearing different
     * names. The table showed rows with the same estate to the dollar (which an adviser rightly read as
     * a bug), the ranking evaluated the same strategy eight times, and a card could claim a percentage
     * it does not pay.
     */
    const seenFundedTotals = new Set<string>();

    for (const percentage of radPercentages) {
        const radAmountForThisLevel = radAmount * (percentage / 100);
        const dapAmount = (radAmount - radAmountForThisLevel) * MPIR +
                         Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365 +
                         Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365;

        // Phase 2: All funding order permutations for true optimisation
        // A change of home: only what the refund of the current deposit does not cover is sold.
        const toRaise = facilityMove
            ? Math.max(0, radAmountForThisLevel - facilityMove.refundToday)
            : villageExit
                ? Math.max(0, radAmountForThisLevel - villageExit.entitlement)
                : radAmountForThisLevel;
        const fundingVariants = toRaise > 0 ? generateAllFundingOrderStrategies(factFind, toRaise) : [{ radFunding: {}, orderLabel: facilityMove ? 'Refund only' : 'Village money only' }];

        for (let i = 0; i < fundingVariants.length; i++) {
            const { radFunding, orderLabel } = fundingVariants[i];
            const fundedTotal = Object.values(radFunding ?? {})
                .flatMap((entries) => (Array.isArray(entries) ? entries : entries ? [entries] : []))
                .reduce((sum, entry) => sum + (Number((entry as { amount?: number }).amount) || 0), 0);
            // Keeps the FIRST level that PAYS a given deposit — the lowest percentage, which is the one
            // whose name still tells the truth about what is paid. Keyed on the deposit, not the savings
            // sold: a village payout or a current room's refund tops a level up without a sale
            // (radLevelKey.ts; owner-caught, 9 Oct 2026).
            const topUp = facilityMove ? Math.max(0, Number(facilityMove.refundToday) || 0) : villageExit ? Math.max(0, Number(villageExit.entitlement) || 0) : 0;
            const fundedKey = radLevelDedupeKey({ orderLabel, level: radAmountForThisLevel, fundedTotal, topUp });
            if (seenFundedTotals.has(fundedKey)) continue;
            seenFundedTotals.add(fundedKey);
            const strategySuffix = fundingVariants.length > 1 ? ` (${orderLabel})` : '';
            const groupKeyKeep = `${percentage}-Keep`;
            const groupKeyRent = `${percentage}-Rent`;
            
            // "Keep Home" only where there is a home to keep: a village or granny-flat resident, or a
            // renter, has none, and the words misled (owner-caught, 9 Oct 2026).
            const keepHomeLabel = anyoneOwnsHome(factFind as unknown as FactFind) ? ', Keep Home' : '';
            // Keep Home scenario
            scenarios.push({
                name: `Pay ${percentage}% RAD${keepHomeLabel}${strategySuffix}`,
                radFunding,
                ...secondTargetFor(percentage),
                groupKey: groupKeyKeep,
                fundingOrderLabel: orderLabel,
                strategyOptions: {
                    annualDap: dapAmount,
                    rentHome: false,
                    downsizeHome: false,
                    scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                }
            });
            
            // Rent Home scenario — only where there is a home to let (householdPosition.ts). A renter,
            // a village resident or someone living with family used to get candidates renting out a
            // home that does not exist, at $1,000 a week (owner's review, 7 Oct 2026).
            if (!homeCanBeLet(factFind as unknown as FactFind)) continue;
            // "Keep it, and don't rent it out" (owner, 25 Oct 2026): a decision, so no rent-out plan is tested.
            if (!rentAllowed) continue;
            const rentAmount = factFind.home?.likelyRent || 1000;
            const rentExpenses = factFind.home?.likelyExpenses || 10000;
            const scenarioLivingExpenses = scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0);
            
            scenarios.push({
                name: `Pay ${percentage}% RAD, Rent Home${strategySuffix}`,
                radFunding,
                ...secondTargetFor(percentage),
                groupKey: groupKeyRent,
                fundingOrderLabel: orderLabel,
                strategyOptions: {
                    annualDap: dapAmount,
                    rentHome: true,
                    downsizeHome: false,
                    scenarioLivingExpenses: scenarioLivingExpenses,
                    rentAmount: rentAmount,
                    rentExpenses: rentExpenses
                }
            });
        }
    }
    
    // Comprehensive downsizing scenarios - All RAD levels tested with downsizing
    // Include when downsizeNewHomeValue is set (e.g. from suggestion engine settings)
    /*
     * BOTH IN CARE TOGETHER (owner, 7 Oct 2026): nobody will live in a new home, so "downsize" is
     * not a strategy for them - SELLING the home is. The same block runs with no new home (the
     * whole net proceeds are the surplus), named "Sell home", and without the "rent the home"
     * twins: there is no longer a home to let. Keep and rent-out candidates still run as before.
     * Where the second person enters LATER the home still houses them, so downsizing stays.
     */
    const saleOnly = (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'both' && !!secondRoom && !secondRoom.entersLater;
    const saleLabel = saleOnly ? 'Sell home' : 'Downsize';
    const downsizedHomeValue = saleOnly ? 0 : (cleanCoupleValidation.downsizeNewHomeValue || 250000);
    const downsizeEnabled = saleOnly
        ? parseFloat(String(factFind.home?.value || 0)) > 0
        : (cleanCoupleValidation.downsizeNewHomeValue || 0) > 0 && downsizedHomeValue > 0;
    
    if (downsizeEnabled) {
        const originalHomeValue = parseFloat(String(factFind.home?.value || 0));
        const sellingCosts = parseFloat(String(cleanCoupleValidation.sellingCosts)) || 0;
        // The duty on the new home is a sunk cost of the changeover like the selling costs, so it
        // comes off before the surplus is sized (owner, 23 Sep 2026). Zero where no state was chosen.
        const stampDuty = saleOnly ? 0 : parseFloat(String(cleanCoupleValidation.stampDuty)) || 0;
        const newHomeState = saleOnly ? undefined : typeof cleanCoupleValidation.newHomeState === 'string' ? cleanCoupleValidation.newHomeState : undefined;
        const netProceeds = Math.max(0, originalHomeValue - sellingCosts - stampDuty);
        const surplusFromDownsize = Math.max(0, netProceeds - downsizedHomeValue); // Money left over after buying new home Ã¢â€ â€™ assume goes to RAD first
        
        for (const percentage of radPercentages) {
            const radAmountForThisLevel = radAmount * (percentage / 100);
            const dapAmount = (radAmount - radAmountForThisLevel) * MPIR +
                             Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365 +
                             Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365;
            
            // Surplus from downsize goes to RAD first; shortfall funded from other assets (same sell-down options as non-downsize)
            const radAllocationFromSurplus = Math.min(surplusFromDownsize, radAmountForThisLevel);
            const shortfall = Math.max(0, radAmountForThisLevel - radAllocationFromSurplus);
            
            const fundingVariantsDownsize = shortfall > 0
                ? generateAllFundingOrderStrategies(factFind, shortfall)
                : [{ radFunding: {}, orderLabel: 'Surplus only' }];
            
            for (let i = 0; i < fundingVariantsDownsize.length; i++) {
                const { radFunding, orderLabel } = fundingVariantsDownsize[i];
                const strategySuffix = fundingVariantsDownsize.length > 1 ? ` (${orderLabel})` : '';
                const groupKeyDownKeep = `D${percentage}-Keep`;
                const groupKeyDownRent = `D${percentage}-Rent`;
                
                const downsizeDetails = {
                    newHomePurchasePrice: downsizedHomeValue,
                    radAllocation: radAllocationFromSurplus,
                    ...(stampDuty > 0 ? { stampDuty } : {}),
                    ...(newHomeState ? { newHomeState } : {}),
                };
                const strategyOptionsWithSellingCost = { sellingCosts };
                
                scenarios.push({
                    name: `${saleLabel} + ${percentage}% RAD${strategySuffix}`,
                    radFunding,
                    groupKey: groupKeyDownKeep,
                    fundingOrderLabel: orderLabel,
                    strategyOptions: {
                        annualDap: dapAmount,
                        rentHome: false,
                        downsizeHome: true,
                        downsizeDetails,
                        sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                        scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                    }
                });
                
                if (!saleOnly && rentAllowed) scenarios.push({
                    name: `${saleLabel} + ${percentage}% RAD, Rent Home${strategySuffix}`,
                    radFunding,
                    groupKey: groupKeyDownRent,
                    fundingOrderLabel: orderLabel,
                    strategyOptions: {
                        annualDap: dapAmount,
                        rentHome: true,
                        downsizeHome: true,
                        downsizeDetails,
                        sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                        scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0),
                        rentAmount: factFind.home?.likelyRent || 1000,
                        rentExpenses: factFind.home?.likelyExpenses || 10000
                    }
                });

                const surplusAfterRad = Math.max(0, surplusFromDownsize - radAllocationFromSurplus);
                const clientAge = calculateAge(factFind.client?.dob) || 0;
                const partnerAge = factFind?.adviceFor === 'couple' ? (calculateAge(factFind.partner?.dob) || 0) : 0;

                const buildDownsizerDetails = (commenceIncomeStream: boolean) => {
                    let remaining = surplusAfterRad;
                    let downsizerContribution = 0;
                    let partnerDownsizerContribution = 0;
                    if (clientAge >= DOWNSIZER_MIN_AGE) {
                        downsizerContribution = Math.min(DOWNSIZER_CONTRIBUTION_CAP, remaining);
                        remaining -= downsizerContribution;
                    }
                    if (factFind?.adviceFor === 'couple' && partnerAge >= DOWNSIZER_MIN_AGE) {
                        partnerDownsizerContribution = Math.min(DOWNSIZER_CONTRIBUTION_CAP, remaining);
                    }
                    if (downsizerContribution + partnerDownsizerContribution < 10000) return null;
                    return {
                        newHomePurchasePrice: downsizedHomeValue,
                        radAllocation: radAllocationFromSurplus,
                        downsizerContribution,
                        partnerDownsizerContribution,
                        commenceIncomeStream: commenceIncomeStream === true,
                        ...(stampDuty > 0 ? { stampDuty } : {}),
                        ...(newHomeState ? { newHomeState } : {}),
                    };
                };

                const downsizerSuperDetails = isDownsizerContributionEligible(asFactFind(factFind), 'client')
                    || (factFind?.adviceFor === 'couple' && isDownsizerContributionEligible(asFactFind(factFind), 'partner'))
                    ? buildDownsizerDetails(false)
                    : null;

                if (downsizerSuperDetails) {
                    const suffixDownsizer = `${strategySuffix} + max downsizer`;
                    scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD${suffixDownsizer}`,
                        radFunding,
                        groupKey: `${groupKeyDownKeep}-DS`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: false,
                            downsizeHome: true,
                            downsizeDetails: downsizerSuperDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                        }
                    });
                    if (!saleOnly && rentAllowed) scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD, Rent Home${suffixDownsizer}`,
                        radFunding,
                        groupKey: `${groupKeyDownRent}-DS`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: true,
                            downsizeHome: true,
                            downsizeDetails: downsizerSuperDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0),
                            rentAmount: factFind.home?.likelyRent || 1000,
                            rentExpenses: factFind.home?.likelyExpenses || 10000
                        }
                    });
                }

                const downsizerAbpDetails = (canCommenceAbpFromDownsizer(asFactFind(factFind), 'client')
                    || (factFind?.adviceFor === 'couple' && canCommenceAbpFromDownsizer(asFactFind(factFind), 'partner')))
                    ? buildDownsizerDetails(true)
                    : null;

                if (downsizerAbpDetails) {
                    const suffixAbp = `${strategySuffix} + downsizer ABP`;
                    scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD${suffixAbp}`,
                        radFunding,
                        groupKey: `${groupKeyDownKeep}-ABP`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: false,
                            downsizeHome: true,
                            downsizeDetails: downsizerAbpDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                        }
                    });
                    if (!saleOnly && rentAllowed) scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD, Rent Home${suffixAbp}`,
                        radFunding,
                        groupKey: `${groupKeyDownRent}-ABP`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: true,
                            downsizeHome: true,
                            downsizeDetails: downsizerAbpDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0),
                            rentAmount: factFind.home?.likelyRent || 1000,
                            rentExpenses: factFind.home?.likelyExpenses || 10000
                        }
                    });
                }
            }
        }
    }

    appendEntityIpSaleScenarios(scenarios, factFind, {
        radAmount,
        agedCareCosts,
        cleanCoupleValidation,
        MPIR,
    });

    /*
     * Borrowing against the home, for the families who do not want to sell it. The engine could
     * only ever answer "sell it, rent it, or pay a smaller deposit" — see
     * agedCareEquityReleaseCandidates.ts for the two schemes and why they are not interchangeable.
     */
    appendEquityReleaseScenarios(scenarios, factFind, {
        radAmount,
        otherAnnualFees:
            Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365
            + Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365,
        MPIR,
        scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
    });

    /*
     * The three means-test levers, layered onto the deposit levels above. These are what an adviser
     * reaches for first in a residential care case and the engine had none of them — see
     * agedCareMeansTestCandidates.ts for what each one does and why the amounts are what they are.
     */
    appendMeansTestStrategyScenarios(scenarios, factFind, annuityIncomeOverride);

    // Last, so every scenario above is available to pair against.
    appendDebtPayoffScenarios(scenarios, factFind);

    // The row's target, where money arriving later (a refund, the village's payment) tops it up.
    if (facilityMove || villageExit) {
        for (const s of scenarios) {
            if (s.stayPut) continue;
            const m = /(\d+)%\s+RAD/i.exec(s.name || '');
            if (m) s.radTarget = Math.round(radAmount * (Number(m[1]) / 100));
        }
    }
    return scenarios;
}

function calculateRadFundingForAmount(factFind: DraftFactFind, amount: number) {
    return calculateSeRadFundingForAmount(factFind, amount);
}

interface RadFundingAvailableAsset {
    type: 'bankAccounts' | 'accountBasedPensions' | 'shares';
    id?: number | string;
    description?: string;
    value: number;
    growthRate: number;
    incomeRate: number;
    fullValue: number;
}

/**
 * Generate multiple RAD funding strategies for actuarial optimisation
 * Tests different asset combinations to maximize final net worth
 * @param {Object} factFind - Client's form data with assets and constraints
 * @param {number} amount - Total RAD amount to fund
 * @returns {Array} Array of radFunding objects representing different funding strategies
 */
function generateSmartRadFundingStrategies(factFind: DraftFactFind, amount: number) {
    const strategies: Record<string, { id?: number | string; amount: number; strategyName: string }[]>[] = [];
    const EMERGENCY_FUND_LIMIT = 50000; // $50K emergency fund protection

    // Collect available assets with their characteristics
    const availableAssets: RadFundingAvailableAsset[] = [];

    // Bank accounts
    if (factFind.assets?.bankAccounts) {
        factFind.assets.bankAccounts.forEach(asset => {
            const value = parseFloat(String(asset.value)) || 0;
            const growthRate = parseFloat(String(asset.growthRate)) || 2;
            const incomeRate = parseFloat(String(asset.incomeRate)) || 2;
            const available = Math.max(0, value - EMERGENCY_FUND_LIMIT); // Protect emergency fund

            if (available > 0) {
                availableAssets.push({
                    type: 'bankAccounts',
                    id: asset.id,
                    description: asset.description,
                    value: available,
                    growthRate: growthRate,
                    incomeRate: incomeRate,
                    fullValue: value
                });
            }
        });
    }

    // Account-based pensions
    if (factFind.assets?.accountBasedPensions) {
        factFind.assets.accountBasedPensions.forEach(asset => {
            const value = parseFloat(String(asset.value)) || 0;
            const growthRate = parseFloat(String(asset.growthRate)) || 5;
            const incomeRate = 0; // Income is based on minimum drawdown rules

            if (value > 0) {
                availableAssets.push({
                    type: 'accountBasedPensions',
                    id: asset.id,
                    description: asset.description,
                    value: value,
                    growthRate: growthRate,
                    incomeRate: incomeRate,
                    fullValue: value
                });
            }
        });
    }

    // Shares
    if (factFind.assets?.shares) {
        factFind.assets.shares.forEach(asset => {
            const value = parseFloat(String(asset.value)) || 0;
            const growthRate = parseFloat(String(asset.growthRate)) || 5;
            const incomeRate = parseFloat(String(asset.incomeRate)) || 4;

            if (value > 0) {
                availableAssets.push({
                    type: 'shares',
                    id: asset.id,
                    description: asset.description,
                    value: value,
                    growthRate: growthRate,
                    incomeRate: incomeRate,
                    fullValue: value
                });
            }
        });
    }
    
    // Single strategy: Bank accounts, then ABPs, then shares (optimal order considering CGT and growth)
    // Bank accounts have no CGT and minimal growth impact
    // ABPs provide income and are tax-free
    // Shares have CGT and moderate growth
    const optimalSort = [...availableAssets].sort((a, b) => {
        // Prioritize bank accounts first (no CGT, minimal income/growth impact)
        if (a.type === 'bankAccounts' && b.type !== 'bankAccounts') return -1;
        if (b.type === 'bankAccounts' && a.type !== 'bankAccounts') return 1;
        // Then ABPs (tax-free, income-bearing) - liquidate lowest growth first to preserve higher growth
        if (a.type === 'accountBasedPensions' && b.type !== 'accountBasedPensions') return -1;
        if (b.type === 'accountBasedPensions' && a.type !== 'accountBasedPensions') return 1;
        if (a.type === 'accountBasedPensions' && b.type === 'accountBasedPensions') {
            // Within ABPs, liquidate lowest growth first
            return a.growthRate - b.growthRate;
        }
        // Then shares (last resort due to CGT)
        return 0;
    });
    const optimalStrategy = generateSingleFundingStrategy(optimalSort, amount, 'Optimal');
    if (optimalStrategy) strategies.push(optimalStrategy);
    
    // Fallback: If no strategies generated, return original simple approach
    if (strategies.length === 0) {
        return [calculateRadFundingForAmount(factFind, amount)];
    }
    
    return strategies;
}

/**
 * Generate a single funding strategy from sorted assets
 * @param {Array} sortedAssets - Assets sorted by priority
 * @param {number} amount - Total amount to fund
 * @param {string} strategyName - Name of the strategy
 * @returns {Object|null} radFunding object or null if can't fund
 */
function generateSingleFundingStrategy(sortedAssets: RadFundingAvailableAsset[], amount: number, strategyName: string): Record<string, { id?: number | string; amount: number; strategyName: string }[]> | null {
    const radFunding: Record<string, { id?: number | string; amount: number; strategyName: string }[]> = {};
    let remainingAmount = amount;

    for (const asset of sortedAssets) {
        if (remainingAmount <= 0) break;

        const liquidateAmount = Math.min(remainingAmount, asset.value);

        if (liquidateAmount > 0) {
            if (!radFunding[asset.type]) radFunding[asset.type] = [];
            radFunding[asset.type]!.push({
                id: asset.id,
                amount: liquidateAmount,
                strategyName: strategyName
            });
            remainingAmount -= liquidateAmount;
        }
    }

    // Return null if we couldn't fund the full amount
    if (remainingAmount > 1000) { // Allow $1K tolerance for rounding
        return null;
    }

    return radFunding;
}

/**
 * Phase 2: Generate all funding strategies by trying every order of the client's asset types.
 * Used for full optimisation (accuracy over speed).
 * @returns {Array<{ radFunding: Object, orderLabel: string }>}
 */
function generateAllFundingOrderStrategies(factFind: DraftFactFind, amount: number) {
    return generateSeFundingVariants(factFind, amount);
}

/** Apply scenario RAD + optional entity IP sale (respects structure funding gate via collected assets). */
function applyRadFundingToFactFind(factFind: DraftFactFind, scenarioOrRadFunding: Strategy | Record<string, number | string> | null | undefined): DraftFactFind {
    const scenario: Record<string, unknown> =
        (scenarioOrRadFunding as Strategy)?.strategyOptions != null || (scenarioOrRadFunding as Strategy)?.name != null
            ? (scenarioOrRadFunding as Record<string, unknown>)
            : { radFunding: scenarioOrRadFunding || {}, strategyOptions: {} };
    return applySuggestionEngineScenarioToFactFind(factFind, scenario);
}


// Build tooltip data for a downsize strategy in the "All modelled strategies" table - explains exactly what the system does
function getDownsizeStrategyTooltipData(factFind: DraftFactFind, strategy: Strategy): CalculationTooltipItem[] | null {
    const opts = strategy.strategyOptions as { downsizeHome?: boolean; downsizeDetails?: Record<string, unknown>; sellingCosts?: unknown } | undefined;
    if (!opts?.downsizeHome || !opts?.downsizeDetails) return null;
    const dd = opts.downsizeDetails;
    const salePrice = parseFloat(String(factFind?.home?.value || 0));
    const sellingCost = parseFloat(String(opts.sellingCosts)) || 0;
    const stampDuty = parseFloat(String(dd.stampDuty)) || 0;
    const netProceeds = Math.max(0, salePrice - sellingCost - stampDuty);
    const newHomeCost = parseFloat(String(dd.newHomePurchasePrice)) || 0;
    const toRAD = parseFloat(String(dd.radAllocation)) || 0;
    const clientDownsizer = parseFloat(String(dd.downsizerContribution)) || 0;
    const partnerDownsizer = parseFloat(String(dd.partnerDownsizerContribution)) || 0;
    const surplus = Math.max(0, netProceeds - newHomeCost);
    const remainderToBank = Math.max(0, surplus - toRAD - clientDownsizer - partnerDownsizer);
    const data: CalculationTooltipItem[] = [
        { label: '--- Downsize: what the system does ---', value: '' },
        { label: 'Sale price (current home)', value: formatCurrency(salePrice) },
        { label: 'Less: Selling costs', value: '-' + formatCurrency(sellingCost) },
        // Its own line, so a reader sees the changeover's second sunk cost — or sees that it was not
        // estimated, which is as important (owner, 23 Sep 2026).
        ...(newHomeCost > 0
            ? [stampDuty > 0
                ? { label: 'Less: Stamp duty on new home', value: '-' + formatCurrency(stampDuty) }
                : { label: 'Stamp duty on new home', value: 'not estimated — no state chosen' }]
            : []),
        { label: 'Net proceeds', value: formatCurrency(netProceeds) },
        { label: 'Less: New home cost', value: '-' + formatCurrency(newHomeCost) },
        { label: 'Surplus (to RAD or bank)', value: formatCurrency(surplus) },
        { label: 'Amount to RAD (from surplus)', value: formatCurrency(toRAD) },
    ];
    if (clientDownsizer > 0) {
        data.push({
            label: dd.commenceIncomeStream ? 'Client downsizer to ABP' : 'Client downsizer to super',
            value: formatCurrency(clientDownsizer),
        });
    }
    if (partnerDownsizer > 0) {
        data.push({
            label: dd.commenceIncomeStream ? 'Partner downsizer to ABP' : 'Partner downsizer to super',
            value: formatCurrency(partnerDownsizer),
        });
    }
    data.push({ label: 'Remainder to bank', value: formatCurrency(remainderToBank) });
    const otherSellDowns = getRadFundingTooltipData(strategy.radFunding as unknown as RadFundingBag);
    if (otherSellDowns && otherSellDowns.length > 0) {
        data.push({ label: '--- Other sell downs (shortfall) ---', value: '' });
        otherSellDowns.forEach(ro => data.push({ label: ro.label, value: ro.value }));
    }
    return data;
}

export default AgedCareSuggestionEnginePanel;
